(function () {
    if (window.__memoraPublicEditionLoaded) return;
    window.__memoraPublicEditionLoaded = true;

    let triggerWelcomeThemeMorph = null;
    let quizPaneNetworkVariantIndex = -1;
    const quizPaneNetworkState = {
        svg: null,
        layer: null,
        items: [],
        raf: 0,
        wasWelcomeMode: false
    };

    const cfg = window.PUBLIC_APP_CONFIG || {};
    const PUBLIC_EDITION_ENABLED = !!cfg.enabled;
    const PUBLIC_GUIDE_ENABLED = cfg.tutorialEnabled !== false;
    if (!PUBLIC_EDITION_ENABLED && !PUBLIC_GUIDE_ENABLED) return;

    function createNeuralCanvasAnimationController(canvas, {
        isSurfaceActive = () => true,
        renderFrame,
        resetFrameClock = null
    }) {
        let frameId = 0;
        let running = false;
        let intersecting = !('IntersectionObserver' in window);
        let staticFrameDrawn = false;
        let intersectionObserver = null;
        let resizeObserver = null;

        const canAnimate = () => canvas.isConnected
            && intersecting
            && !document.hidden
            && !document.body.classList.contains('smooth-mode')
            && !!isSurfaceActive();

        const stop = () => {
            running = false;
            if (frameId) window.cancelAnimationFrame(frameId);
            frameId = 0;
        };

        const renderStaticFrame = () => {
            if (!canvas.isConnected
                || staticFrameDrawn
                || canvas.clientWidth <= 0
                || canvas.clientHeight <= 0) return;
            renderFrame(performance.now());
            staticFrameDrawn = true;
        };

        const schedule = () => {
            if (!running || frameId) return;
            frameId = window.requestAnimationFrame((time) => {
                frameId = 0;
                if (!canAnimate()) {
                    stop();
                    renderStaticFrame();
                    return;
                }
                renderFrame(time);
                schedule();
            });
        };

        const sync = () => {
            if (!canvas.isConnected) {
                stop();
                intersectionObserver?.disconnect();
                resizeObserver?.disconnect();
                return;
            }
            if (!canAnimate()) {
                stop();
                renderStaticFrame();
                return;
            }
            staticFrameDrawn = false;
            resetFrameClock?.();
            running = true;
            schedule();
        };

        if ('IntersectionObserver' in window) {
            intersectionObserver = new IntersectionObserver((entries) => {
                intersecting = !!entries[0]?.isIntersecting;
                sync();
            }, { threshold: 0.01 });
            intersectionObserver.observe(canvas);
        }
        if ('ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                if (!running) staticFrameDrawn = false;
                sync();
            });
            resizeObserver.observe(canvas);
        }
        document.addEventListener('visibilitychange', sync, { passive: true });
        document.addEventListener('memora:smooth-mode-changed', sync);

        return { sync, renderStaticFrame };
    }

    const TOKEN_KEY = 'memora_public_access_token';
    const PUBLIC_TUTORIAL_SEEN_KEY = 'memora_public_tutorial_seen_v2';
    const PUBLIC_TUTORIAL_PREFS_KEY = 'memora_public_tutorial_prefs_v1';
    const PUBLIC_HOME_ONBOARDING_HIDE_KEY = 'memora_public_home_onboarding_hide_v1';
    const WELCOME_NEURAL_ROTATION_KEY = 'memora.welcomeThemeRotation.v1';
    const WELCOME_NEURAL_THEMES = Object.freeze([
        'constellation',
        'dna',
        'lattice',
        'cardio',
        'brain',
        'atom',
        'starmap',
        'synapse',
        'cortex',
        'spiral',
        'gridwave',
        'butterfly',
        'orbitals',
        'wavefront',
        'helixfield',
        'crystal',
        'ribbon',
        'vortex',
        'mesh',
        'pulse'
    ]);
    const WELCOME_NEURAL_INTERACTIVE_DELAY_MS = 2000;
    const WELCOME_NEURAL_INTERACTIVE_CLASS = 'welcome-neural-interactive';
    const QUIZ_PANE_NEURAL_VARIANTS = Object.freeze([
        {
            nodes: [[2, 24, 1.1], [12, 14, 0.8], [25, 23, 1.2], [38, 11, 0.9], [52, 20, 1.25], [66, 13, 0.8], [82, 24, 1.1], [97, 16, 0.75], [8, 62, 0.9], [22, 49, 1.15], [36, 59, 0.8], [50, 43, 1.3], [64, 55, 0.9], [78, 40, 1.15], [94, 52, 0.8], [16, 88, 0.75], [42, 80, 1], [70, 85, 0.8], [92, 78, 0.75]],
            links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [0, 9], [2, 9], [4, 11], [6, 13], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [8, 15], [10, 16], [12, 17], [14, 18], [16, 17]]
        },
        {
            nodes: [[5, 10, 0.85], [18, 22, 1.2], [31, 12, 0.8], [47, 26, 1.1], [61, 10, 0.9], [77, 19, 1.25], [94, 8, 0.8], [10, 45, 1.05], [24, 58, 0.85], [39, 45, 1.15], [55, 62, 0.9], [71, 46, 1.2], [88, 60, 0.8], [3, 78, 0.75], [27, 84, 1], [50, 75, 0.85], [76, 88, 1.05], [96, 77, 0.75]],
            links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [1, 7], [7, 8], [8, 9], [3, 9], [9, 10], [10, 11], [5, 11], [11, 12], [7, 13], [8, 14], [10, 15], [12, 16], [16, 17], [15, 16], [2, 9], [4, 11]]
        },
        {
            nodes: [[6, 34, 1], [18, 16, 0.85], [30, 31, 1.25], [42, 18, 0.75], [54, 35, 1.1], [67, 15, 0.9], [80, 32, 1.2], [96, 20, 0.75], [4, 68, 0.8], [19, 54, 1.15], [35, 71, 0.85], [50, 56, 1.3], [65, 73, 0.85], [82, 56, 1.1], [98, 69, 0.8], [24, 91, 0.75], [58, 91, 0.9], [88, 88, 0.75]],
            links: [[0, 1], [0, 9], [1, 2], [2, 3], [2, 9], [3, 4], [4, 5], [4, 11], [5, 6], [6, 7], [6, 13], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [10, 15], [12, 16], [14, 17], [15, 16], [16, 17]]
        },
        {
            nodes: [[3, 18, 0.7], [16, 30, 1.1], [27, 11, 0.85], [40, 28, 1.25], [53, 13, 0.8], [66, 30, 1.1], [79, 12, 0.85], [95, 26, 0.75], [9, 60, 1], [22, 72, 0.8], [35, 55, 1.2], [49, 70, 0.9], [62, 54, 1.25], [76, 69, 0.85], [91, 54, 1], [11, 91, 0.7], [41, 87, 0.85], [72, 90, 0.75]],
            links: [[0, 1], [1, 2], [1, 8], [2, 3], [3, 4], [3, 10], [4, 5], [5, 6], [5, 12], [6, 7], [7, 14], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [9, 15], [11, 16], [13, 17], [16, 17]]
        },
        {
            nodes: [[4, 50, 1.05], [13, 25, 0.8], [25, 42, 1.25], [35, 17, 0.75], [47, 39, 1.1], [58, 20, 0.9], [70, 43, 1.2], [83, 25, 0.8], [96, 47, 1], [15, 75, 0.9], [30, 86, 0.75], [45, 70, 1.25], [60, 88, 0.8], [74, 69, 1.1], [90, 82, 0.75]],
            links: [[0, 1], [0, 2], [1, 2], [2, 3], [2, 4], [3, 4], [4, 5], [4, 6], [5, 6], [6, 7], [6, 8], [7, 8], [0, 9], [2, 9], [9, 10], [9, 11], [10, 11], [11, 12], [11, 13], [12, 13], [13, 14], [8, 14]]
        },
        {
            nodes: [[6, 8, 0.75], [20, 20, 1.15], [34, 9, 0.8], [48, 21, 1.25], [63, 8, 0.8], [78, 21, 1.15], [94, 12, 0.75], [12, 45, 0.9], [28, 38, 1.2], [43, 51, 0.85], [58, 38, 1.25], [73, 52, 0.85], [90, 42, 1], [7, 78, 0.8], [24, 66, 1.1], [43, 82, 0.8], [62, 66, 1.1], [81, 82, 0.8], [98, 70, 0.75]],
            links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [1, 7], [7, 8], [8, 9], [3, 9], [9, 10], [10, 11], [5, 10], [11, 12], [7, 13], [8, 14], [14, 15], [9, 15], [10, 16], [16, 17], [11, 17], [12, 18]]
        },
        {
            nodes: [[3, 30, 0.75], [14, 12, 0.9], [25, 34, 1.2], [38, 18, 0.8], [50, 33, 1.3], [63, 17, 0.8], [75, 34, 1.2], [87, 13, 0.9], [98, 30, 0.75], [12, 64, 1], [27, 55, 0.85], [42, 70, 1.15], [58, 55, 0.9], [73, 70, 1.15], [88, 62, 0.85], [23, 91, 0.75], [51, 88, 0.9], [80, 91, 0.75]],
            links: [[0, 1], [0, 2], [1, 2], [2, 3], [2, 10], [3, 4], [4, 5], [4, 12], [5, 6], [6, 7], [6, 14], [7, 8], [8, 14], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [11, 15], [12, 16], [13, 17], [15, 16], [16, 17]]
        },
        {
            nodes: [[2, 82, 0.75], [13, 61, 1], [26, 77, 0.8], [37, 52, 1.2], [49, 68, 0.9], [61, 45, 1.25], [74, 63, 0.85], [88, 40, 1], [98, 58, 0.75], [11, 22, 0.8], [27, 33, 1.15], [44, 18, 0.85], [59, 30, 1.2], [78, 18, 0.8], [93, 28, 0.75], [22, 93, 0.75], [56, 89, 0.8], [83, 84, 0.75]],
            links: [[0, 1], [1, 2], [1, 10], [2, 3], [3, 4], [3, 10], [4, 5], [5, 6], [5, 12], [6, 7], [7, 8], [7, 13], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [2, 15], [4, 16], [6, 17], [15, 16], [16, 17]]
        },
        {
            nodes: [[4, 16, 0.8], [18, 38, 1.2], [31, 18, 0.85], [44, 39, 1.1], [58, 19, 0.9], [72, 40, 1.25], [86, 20, 0.8], [98, 42, 0.75], [8, 70, 0.9], [24, 58, 1.15], [39, 76, 0.85], [54, 58, 1.3], [70, 76, 0.85], [86, 61, 1], [94, 87, 0.75], [17, 91, 0.75], [50, 91, 0.8], [78, 91, 0.75]],
            links: [[0, 1], [1, 2], [1, 9], [2, 3], [3, 4], [3, 11], [4, 5], [5, 6], [5, 13], [6, 7], [7, 13], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [8, 15], [10, 16], [12, 17], [14, 17], [15, 16]]
        },
        {
            nodes: [[3, 40, 0.75], [15, 28, 1], [28, 46, 0.85], [39, 25, 1.2], [52, 45, 0.9], [64, 27, 1.25], [77, 45, 0.85], [90, 28, 1], [98, 50, 0.75], [10, 76, 0.8], [24, 63, 1.15], [39, 82, 0.8], [55, 64, 1.25], [70, 82, 0.8], [86, 67, 1.1], [21, 9, 0.75], [55, 10, 0.85], [88, 9, 0.75]],
            links: [[0, 1], [0, 10], [1, 2], [2, 3], [2, 10], [3, 4], [3, 15], [4, 5], [4, 12], [5, 6], [5, 16], [6, 7], [6, 14], [7, 8], [7, 17], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [15, 3], [16, 5], [17, 7]]
        }
    ]);
    const DEFAULT_PUBLIC_TUTORIAL_PREFS = {
        goal: 'auto',
        questionCount: 25,
        difficulty: 'moderate'
    };
    const HOME_ONBOARDING_STORY = Object.freeze([
        { text: 'Upload your lecture PDFs and let Memora generate:' },
        { label: 'Smart quizzes:', text: 'Every question keeps the relevant source slide right beside it.' },
        { label: 'Personalized tutoring:', text: 'Get guided help and generate a study guide from your lecture.' },
        { label: 'Flashcards:', text: 'Build recall with your own notes attached.' },
        { text: 'And more', closing: true }
    ]);
    const PUBLIC_HOME_DEMO_SCENES = Object.freeze([
        { id: 'import', caption: 'Add Files', durationMs: 6850 },
        { id: 'generate', caption: 'Choose Your Quiz Preset', durationMs: 3270 },
        { id: 'quiz', caption: 'Open the exact slide the question came from', durationMs: 10808 },
        { id: 'say-more', sceneId: 'quiz', continuation: true, caption: 'Learn why each answer is right or wrong', durationMs: 7390 },
        { id: 'more', caption: 'You Can Also Make:', durationMs: 7160, skipIntro: true }
    ]);
    const WELCOME_SCREEN_TIPS = Object.freeze([
        { text: 'Import PDFs, JSON, or TXT files to start a study session.' },
        { text: 'Choose what you want to make first. Memora asks for a free Gemini API key only when you are ready to create it.' },
        { text: 'No API key? Enable Manual Import in Accessibility, then use ChatGPT or Gemini and paste the JSON back into Memora.' },
        { text: 'Continue Last Session picks up where you stopped.' },
        { text: 'Load Previous Session reopens older saved study runs.' },
        { text: 'Use Guide from the welcome screen or the main study screen for a quick walkthrough.', guideOnly: true },
        { text: 'Search can find matches in questions, choices, explanations, source labels, and indexed PDF text.' },
        { text: 'Show PDF keeps the source lecture beside the quiz while you study.' },
        { text: 'Trio mode opens PDF, quiz, and AI together.', phoneHidden: true },
        { text: 'Hold Shift and left click, then drag the quiz card to move it around.', phoneHidden: true },
        { text: 'Drag the resize handle to resize the quiz card.', phoneHidden: true },
        { text: 'Press Ctrl+Up to enlarge quiz content.', phoneHidden: true },
        { text: 'Press Ctrl+Down to reduce quiz content.', phoneHidden: true },
        { text: 'Press Esc to close overlays, panes, and expanded views.', phoneHidden: true },
        { text: 'Press ~ to reset the view and close open surfaces quickly.', phoneHidden: true },
        { text: 'Press ? to open the shortcuts panel.', phoneHidden: true },
        { text: 'You can edit shortcut keys from the shortcuts panel.', phoneHidden: true },
        { text: 'Press Ctrl+O for fullscreen.', phoneHidden: true },
        { text: 'Press Right Arrow for the next question.', phoneHidden: true },
        { text: 'Press Left Arrow for the previous question.', phoneHidden: true },
        { text: 'Press A, B, C, or D to answer without clicking.', phoneHidden: true },
        { text: 'Press S to save or bookmark the current question.', phoneHidden: true },
        { text: 'Press E to open the linked sources.', phoneHidden: true },
        { text: 'Press U to refresh the current question.', phoneHidden: true },
        { text: 'Press Ctrl+C to copy the current question and answer choices.', phoneHidden: true },
        { text: 'Auto-Save can save wrong answers automatically.' },
        { text: 'Auto-Advance can move to the next question after a correct answer.' },
        { text: 'Auto-Sources can open the linked source automatically.' },
        { text: 'Auto-Explanation can jump to the matching PDF explanation slide automatically.' },
        { text: 'Use Hint when you want a nudge without the full answer.', phoneHidden: true },
        { text: 'Use Explain for a deeper AI breakdown of the concept.', phoneHidden: true },
        { text: 'Add Notes to attach your own memory anchors to a question.' },
        { text: 'Save high-yield misses into Saved Questions for focused review later.' },
        { text: 'Saved Questions supports compact mode, expanded mode, source filters, and duplicate review.' },
        { text: 'Duplicate review helps clean overlapping questions across quiz banks.' },
        { text: 'Open Learn and choose Flashcards for one-card-at-a-time review.' },
        { text: 'Quiz Review Cards uses Again, Hard, Good, and Easy ratings to schedule each flashcard automatically.' },
        { text: 'Hard Mode hides choices until you reveal them.' },
        { text: 'Mix Answers shuffles the answer order.' },
        { text: 'Shuffle prioritizes unanswered questions first.' },
        { text: 'Quiz Spaced Review and Quiz Review Cards use confidence ratings to schedule smarter review.' },
        { text: 'Zen Mode hides nonessential UI for focus.' },
        { text: 'Game Mode adds XP, levels, streaks, and achievements.' },
        { text: 'Statistics tracks streak, accuracy, and completion.' },
        { text: 'Export quizzes to Anki, Quizlet, CSV, or JSON.' },
        { text: 'You can export saved questions only.' },
        { text: 'Database shows which PDFs, quizzes, and explanations are stored locally in this browser.' },
        { text: 'Memora saves sessions, notes, and your Gemini key locally in the browser.' },
        { text: 'Appearance lets you change gradients, colors, fonts, borders, ambience, and Smooth Mode.' },
        { text: 'High Contrast and Reduce Motion are available in Settings.' },
        { text: 'Smart Saver reduces memory use for lighter sessions.' },
        { text: 'The PDF pane has page, zoom, fit, pop-out, and expand tools.' },
        { text: 'Hover over buttons to see what they do before you click them.', weight: 6 }
    ]);

    function normalizeWelcomeNeuralTheme(theme) {
        const raw = String(theme || '').trim().toLowerCase();
        return WELCOME_NEURAL_THEMES.includes(raw) ? raw : '';
    }

    function persistWelcomeNeuralTheme(theme) {
        const safeTheme = normalizeWelcomeNeuralTheme(theme) || WELCOME_NEURAL_THEMES[0];
        try {
            localStorage.setItem('memora.welcomeTheme', safeTheme);
        } catch (_err) { }
        return safeTheme;
    }

    function getStoredWelcomeNeuralTheme() {
        try {
            const safeTheme = normalizeWelcomeNeuralTheme(localStorage.getItem('memora.welcomeTheme'));
            if (safeTheme) return safeTheme;
        } catch (_err) { }
        return persistWelcomeNeuralTheme(WELCOME_NEURAL_THEMES[0]);
    }

    function readWelcomeNeuralRotation() {
        try {
            const parsed = JSON.parse(localStorage.getItem(WELCOME_NEURAL_ROTATION_KEY) || '{}');
            const queue = Array.isArray(parsed?.queue)
                ? parsed.queue.map(normalizeWelcomeNeuralTheme).filter(Boolean)
                : [];
            return { queue: Array.from(new Set(queue)) };
        } catch (_err) {
            return { queue: [] };
        }
    }

    function writeWelcomeNeuralRotation(queue) {
        try {
            localStorage.setItem(WELCOME_NEURAL_ROTATION_KEY, JSON.stringify({ queue }));
        } catch (_err) { }
    }

    function shuffleWelcomeNeuralThemes(excludeFirst = '') {
        const themes = WELCOME_NEURAL_THEMES.slice();
        for (let i = themes.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [themes[i], themes[j]] = [themes[j], themes[i]];
        }
        if (excludeFirst && themes.length > 1 && themes[0] === excludeFirst) {
            const swapIndex = themes.findIndex((theme) => theme !== excludeFirst);
            if (swapIndex > 0) [themes[0], themes[swapIndex]] = [themes[swapIndex], themes[0]];
        }
        return themes;
    }

    function selectRandomWelcomeNeuralTheme({ excludeCurrent = true } = {}) {
        const currentTheme = getStoredWelcomeNeuralTheme();
        const excludedTheme = excludeCurrent ? currentTheme : '';
        let { queue } = readWelcomeNeuralRotation();
        queue = queue.filter((theme) => WELCOME_NEURAL_THEMES.includes(theme));

        if (!queue.length) {
            queue = shuffleWelcomeNeuralThemes(excludedTheme);
        } else if (excludedTheme && queue[0] === excludedTheme && queue.length > 1) {
            const swapIndex = queue.findIndex((theme) => theme !== excludedTheme);
            if (swapIndex > 0) [queue[0], queue[swapIndex]] = [queue[swapIndex], queue[0]];
        } else if (excludedTheme && queue[0] === excludedTheme) {
            queue = shuffleWelcomeNeuralThemes(excludedTheme);
        }

        const nextTheme = queue.shift() || WELCOME_NEURAL_THEMES[0];
        writeWelcomeNeuralRotation(queue);
        return persistWelcomeNeuralTheme(nextTheme);
    }

    function selectQuizPaneNetworkVariantIndex() {
        const total = QUIZ_PANE_NEURAL_VARIANTS.length;
        if (!total) return -1;
        if (total === 1) return 0;
        let nextIndex = Math.floor(Math.random() * total);
        if (nextIndex === quizPaneNetworkVariantIndex) {
            nextIndex = (nextIndex + 1 + Math.floor(Math.random() * (total - 1))) % total;
        }
        return nextIndex;
    }

    function createQuizPaneSvgElement(tagName) {
        return document.createElementNS('http://www.w3.org/2000/svg', tagName);
    }

    function getQuizPaneNetworkPoint(rect, node) {
        return {
            x: rect.left + (Number(node?.[0]) || 0) * rect.width / 100,
            y: rect.top + (Number(node?.[1]) || 0) * rect.height / 100
        };
    }

    function pointIsInsideRect(point, rect) {
        return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
    }

    function shouldDimQuizPaneNetworkItem(item, svgRect, dimRect) {
        if (!dimRect) return false;
        const from = getQuizPaneNetworkPoint(svgRect, item.from);
        const to = getQuizPaneNetworkPoint(svgRect, item.to);
        for (let i = 0; i <= 8; i += 1) {
            const t = i / 8;
            const sample = {
                x: from.x + (to.x - from.x) * t,
                y: from.y + (to.y - from.y) * t
            };
            if (pointIsInsideRect(sample, dimRect)) return true;
        }
        return false;
    }

    function getQuizPaneCardDimRect() {
        const card = document.getElementById('quiz-card');
        if (!card || !isElementVisible(card)) return null;
        const styles = window.getComputedStyle(card);
        if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) <= 0.01) return null;
        const rect = card.getBoundingClientRect();
        if (rect.width < 20 || rect.height < 20) return null;
        const pad = card.classList.contains('quiz-card-expanded') ? 0 : 8;
        return {
            left: rect.left - pad,
            right: rect.right + pad,
            top: rect.top - pad,
            bottom: rect.bottom + pad
        };
    }

    function applyQuizPaneNetworkVisibility() {
        const { svg } = quizPaneNetworkState;
        if (!svg) return false;
        const visible = !document.body.classList.contains('welcome-mode')
            && !document.body.classList.contains('zen-active');
        if (visible) svg.removeAttribute('hidden');
        else svg.setAttribute('hidden', '');
        return visible;
    }

    function updateQuizPaneNetworkFrame() {
        const { svg, items } = quizPaneNetworkState;
        if (!svg) return;
        const visible = applyQuizPaneNetworkVisibility();
        if (visible && quizPaneNetworkState.rendered !== false) {
            const svgRect = svg.getBoundingClientRect();
            const dimRect = getQuizPaneCardDimRect();
            if (svgRect.width > 0 && svgRect.height > 0) {
                items.forEach((item) => {
                    const dimmed = shouldDimQuizPaneNetworkItem(item, svgRect, dimRect);
                    const opacity = dimmed ? '0' : item.baseOpacity;
                    // Unchanged writes still invalidated style on every frame.
                    if (item.appliedOpacity !== opacity) {
                        item.appliedOpacity = opacity;
                        item.el.style.opacity = opacity;
                    }
                });
            }
        }
        quizPaneNetworkState.raf = window.requestAnimationFrame(updateQuizPaneNetworkFrame);
    }

    function renderQuizPaneNetworkVariant(index = selectQuizPaneNetworkVariantIndex()) {
        const variant = QUIZ_PANE_NEURAL_VARIANTS[index] || QUIZ_PANE_NEURAL_VARIANTS[0];
        const { svg, layer } = quizPaneNetworkState;
        if (!svg || !layer || !variant) return;
        quizPaneNetworkVariantIndex = QUIZ_PANE_NEURAL_VARIANTS.indexOf(variant);
        layer.replaceChildren();
        quizPaneNetworkState.items = [];

        variant.links.forEach(([fromIndex, toIndex], linkIndex) => {
            const from = variant.nodes[fromIndex];
            const to = variant.nodes[toIndex];
            if (!from || !to) return;
            const line = createQuizPaneSvgElement('line');
            line.classList.add('quiz-pane-neural-link');
            if (linkIndex % 5 === 0) line.classList.add('is-broken-wide');
            else if (linkIndex % 5 === 1) line.classList.add('is-broken-fine');
            else if (linkIndex % 5 === 2) line.classList.add('is-hairline');
            else if (linkIndex % 5 === 3) line.classList.add('is-strong');
            line.setAttribute('x1', from[0]);
            line.setAttribute('y1', from[1]);
            line.setAttribute('x2', to[0]);
            line.setAttribute('y2', to[1]);
            const baseOpacity = linkIndex % 4 === 0 ? '0.16' : '0.11';
            line.style.opacity = baseOpacity;
            line.style.strokeDashoffset = String((linkIndex % 7) * 1.7);
            layer.appendChild(line);
            quizPaneNetworkState.items.push({ kind: 'link', el: line, from, to, baseOpacity });
        });
    }

    function installQuizPaneNeuralNetwork() {
        const quizPane = document.getElementById('quiz-pane');
        if (!quizPane || quizPaneNetworkState.svg) return;

        const svg = createQuizPaneSvgElement('svg');
        svg.id = 'quiz-pane-neural-network';
        svg.classList.add('quiz-pane-neural-network');
        svg.setAttribute('viewBox', '0 0 100 100');
        svg.setAttribute('preserveAspectRatio', 'none');
        svg.setAttribute('aria-hidden', 'true');

        const layer = createQuizPaneSvgElement('g');
        layer.classList.add('quiz-pane-neural-layer');
        svg.appendChild(layer);
        quizPane.prepend(svg);

        quizPaneNetworkState.svg = svg;
        quizPaneNetworkState.layer = layer;
        quizPaneNetworkState.wasWelcomeMode = document.body.classList.contains('welcome-mode');
        /* Theme overhauls hide the network with display:none, but the frame loop still measured
           the quiz card every frame, forcing a layout whenever anything had changed. The
           observer reports 0 x 0 while the SVG is not rendered, and the loop skips its reads. */
        if (typeof ResizeObserver === 'function') {
            new ResizeObserver((entries) => {
                const box = entries[entries.length - 1]?.contentRect;
                quizPaneNetworkState.rendered = !!box && box.width > 0 && box.height > 0;
            }).observe(svg);
        }

        renderQuizPaneNetworkVariant();
        applyQuizPaneNetworkVisibility();

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName !== 'class') return;
                const isWelcome = document.body.classList.contains('welcome-mode');
                if (quizPaneNetworkState.wasWelcomeMode && !isWelcome) {
                    renderQuizPaneNetworkVariant();
                }
                quizPaneNetworkState.wasWelcomeMode = isWelcome;
                applyQuizPaneNetworkVisibility();
            });
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        if (!quizPaneNetworkState.raf) {
            quizPaneNetworkState.raf = window.requestAnimationFrame(updateQuizPaneNetworkFrame);
        }
    }

    const DEFAULT_RULES = {
        free: {
            features: {
                ai_hint: true,
                ai_explain: true,
                ai_deep_dive: false,
                ai_ask: true,
                ai_quiz_generation: false,
                adaptive_review: false,
                advanced_stats: false,
                export: false
            },
            disabledToolIds: ['srs-mode-btn', 'flashcard-srs-mode-btn', 'fc-srs-mode-btn', 'stats-menu-btn', 'export-quiz-btn']
        },
        paid: {
            features: {
                ai_hint: true,
                ai_explain: true,
                ai_deep_dive: true,
                ai_ask: true,
                ai_quiz_generation: true,
                adaptive_review: true,
                advanced_stats: true,
                export: true
            },
            disabledToolIds: []
        }
    };

    const DEFAULT_WEB_CAPABILITIES = {
        canvasImport: false,
        officeImport: true,
        imageQuestionGeneration: false
    };

    const state = {
        token: '',
        user: null,
        plan: 'free',
        credits: 0,
        features: { ...DEFAULT_RULES.free.features },
        locked: PUBLIC_EDITION_ENABLED
    };
    const publicTourState = {
        active: false,
        mode: '',
        steps: [],
        stepIndex: -1,
        highlightedEl: null,
        highlightedEls: [],
        highlightedRoot: null,
        highlightedStackRoot: null,
        highlightedEffectEls: [],
        savedMenuGroupIds: null,
        openedImportModal: false,
        openedQuizGenModal: false,
        openedMenuDropdown: false,
        homeOpen: false,
        homeEmphasized: false,
        homeVariant: '',
        homeOnboardingStage: '',
        homeStoryTimer: 0,
        homeStoryRunId: 0,
        homeStoryDone: false,
        homeDemoTimer: 0,
        homeDemoRunId: 0,
        homeDemoRevealRunId: 0,
        homeDemoRunning: false,
        homeDemoPaused: false,
        homeDemoSceneIndex: 0,
        homeDemoRoot: null,
        homeDemoSliderTravel: null,
        homeDemoWaitResolve: null,
        homeDemoLifecycleBound: false,
        homeDemoInitialDelayDone: false,
        homeTransitioning: false,
        homeDismissPreference: null,
        autoOpened: false,
        preferences: null,
        guideLabel: 'Full Walkthrough',
        activePopupGuideId: '',
        activePopupGuideTitle: '',
        homeTipText: '',
        homeTipsOpen: false,
        phoneComputerTipReady: false,
        stepTransitioning: false,
        stepTransitionDirection: '',
        stepPlaybackActive: false,
        stepPlaybackRunId: 0,
        stepPlaybackStepId: '',
        stepPlaybackDispatchingClick: false,
        pdfDemoRestore: null,
        pdfDemoPointerId: 0,
        pdfDemoCursor: null,
        pdfDemoSelection: null,
        pdfDemoAskCard: null,
        pdfDemoClosing: false,
        sayMoreDemoActive: false,
        sayMoreDemoOpenedAsk: false
    };
    let publicTourWelcomeActionRestore = null;
    window.isPublicTourSayMoreDemoSurfaceActive = () => {
        const step = publicTourState.steps[publicTourState.stepIndex];
        return !!(publicTourState.active && step?.id === 'say-more-demo');
    };
    const PUBLIC_TOUR_BLOCKED_HINT = 'Stop Guide to use this.';
    const POPUP_GUIDE_MODAL_IDS = [
        'history-modal',
        'stats-modal',
        'achievements-modal',
        'level-roadmap-modal',
        'export-modal',
        'database-modal',
        'saved-modal',
        'adaptive-snapshot-modal',
        'theme-modal',
        'api-key-modal',
        'notes-modal',
        'canvas-settings-modal',
        'canvas-help-modal',
        'canvas-browser-modal',
        'quiz-gen-modal'
    ];
    const POPUP_GUIDE_DEFINITIONS = {
        'quiz-gen-modal': {
            title: 'Generate Quiz From PDF',
            intro: 'This popup turns one or more PDFs into a quiz. Pick files, choose settings, then generate.',
            note: 'If you are new, keep the defaults near the middle and start with Quiz Only.',
            steps: [
                {
                    target: '#quiz-gen-modal-header-actions',
                    title: 'Header Actions',
                    body: 'Quizzes reopens generated sets. Usage shows an estimate. Minus minimizes the popup. X closes it.'
                },
                {
                    target: '#quiz-gen-pdf-list',
                    title: 'Selected PDFs',
                    body: 'This list shows every PDF in the batch. Add More adds files. Select All helps when you want the same settings for all of them.'
                },
                {
                    target: '#quiz-gen-modal [data-flow-step="count"]',
                    title: 'Question Count',
                    body: 'Pick the quiz size first. This is the main control for output length.'
                },
                {
                    target: '#quiz-gen-modal [data-flow-step="model"]',
                    title: 'Choose Model',
                    body: 'Choose the model. Check the quota card. Use Customize Quiz Request only when you want to steer the content focus.'
                },
                {
                    target: '#quiz-gen-modal [data-flow-step="difficulty"]',
                    title: 'Difficulty',
                    body: 'Easy is simpler recall. Moderate is balanced. Difficult needs more reasoning. Mixed blends them.'
                },
                {
                    target: '#quiz-gen-modal [data-flow-step="enhancements"]',
                    title: 'Output Enhancements',
                    body: 'Turn on PDF Explanations only if you want a second output that walks through the source slides.'
                },
                {
                    target: '#quiz-gen-modal [data-flow-step="advanced"]',
                    title: 'Performance',
                    body: 'Concurrency controls how many PDFs run at once. Higher values can be faster, but they use more memory.'
                },
                {
                    target: '#quiz-gen-generate-wrap',
                    title: 'Generate Menu',
                    body: 'Generate Quiz Only is the safest first run. If no Gemini key is saved, the short Connect Gemini popup appears only after you press Generate.'
                }
            ]
        },
        'saved-modal': {
            title: 'Saved Questions',
            intro: 'Saved Questions is Memora\'s high-yield review vault. It lets you turn important misses, flagged items, and duplicate audits into a focused bank instead of hunting through the main session again.',
            note: 'This popup now has a dedicated guide because it carries more than one workflow: review, duplicate cleanup, and export.',
            steps: [
                {
                    target: '#saved-modal .saved-header',
                    title: 'Saved Questions Hub',
                    body: 'Quiz Saved launches a quiz built from the saved pool, Download PDF turns the current view into a handout, fullscreen expands the browser, and X closes the popup.'
                },
                {
                    target: '#saved-compact-btn',
                    title: 'Compact Mode',
                    body: 'Compact mode keeps each saved item short so you can scan many questions quickly. It is best when you want to triage a large saved bank without opening every card in full.'
                },
                {
                    target: '#saved-expanded-btn',
                    title: 'Expanded Mode',
                    body: 'Expanded mode gives each entry more room so the question, answer, and supporting details are easier to inspect in one pass.'
                },
                {
                    target: '#saved-duplicates-btn',
                    title: 'Duplicate Questions',
                    body: 'This switches the popup into duplicate-review mode. Use it to inspect exact and near-duplicate questions across the loaded quiz files instead of mixing those entries into the normal saved list.'
                },
                {
                    target: '#saved-duplicate-export-btn',
                    title: 'Download Duplicates',
                    body: 'This exports the duplicate report as its own file so you can audit the duplicate cleanup outside Memora or keep a separate record of what was removed.'
                },
                {
                    target: '#saved-source-btn',
                    title: 'Sources Filter',
                    body: 'Sources lets you narrow the list to one or more lecture files or quiz files. The badge shows the active scope, and the chips below the controls show which filters are currently applied.'
                },
                {
                    target: '#saved-list',
                    title: 'Saved List',
                    body: 'This list is the actual vault. It is where you review the saved entries, inspect duplicate notes, and decide what deserves to stay in your long-term study stack.'
                }
            ]
        },
        'database-modal': {
            title: 'Database',
            intro: 'Database is the local storage map for Memora. It shows which sessions, lectures, quizzes, explanations, and duplicate reports are still saved in this browser.',
            note: 'Think of this as file management, not question answering. It is where you inspect, download, or clear stored study material.',
            steps: [
                {
                    target: '#database-modal .db-header-row',
                    title: 'Session Count And Storage',
                    body: 'The header shows how many sessions exist and how much of the local file allowance is currently occupied. It gives you a quick sense of how much material is saved in this browser.'
                },
                {
                    target: '#db-content-list',
                    title: 'Session Cards',
                    body: 'Each card groups one session. Expand a session to see its lectures, quiz files, and saved PDF explanations instead of browsing raw browser storage.'
                },
                {
                    target: () => document.querySelector('#db-content-list .db-session-dup-btn:not(.disabled)') || document.querySelector('#db-content-list'),
                    title: 'Duplicate Questions Download',
                    body: 'When a session has duplicate findings, this button downloads the duplicate report for that session only. It is useful when you want to clean or compare one session in isolation.'
                },
                {
                    target: () => document.querySelector('#db-content-list .db-download-all-btn') || document.querySelector('#db-content-list'),
                    title: 'Download All Files',
                    body: 'The All button downloads everything attached to a session at once. Use it when you want a complete local backup without opening each item separately.'
                },
                {
                    target: () => document.querySelector('#db-content-list .db-item-del') || document.querySelector('#db-content-list'),
                    title: 'Delete Session',
                    body: 'The delete button moves a session to Recently Deleted, where it can be restored for 15 days before it is removed for good.'
                },
                {
                    target: '#database-modal .db-danger-btn',
                    title: 'Delete All Sessions',
                    body: 'Moves every saved session to Recently Deleted. They can be restored for 15 days; after that they are removed for good.'
                }
            ]
        },
        'export-modal': {
            title: 'Export Quiz',
            intro: 'Export is where Memora hands the quiz off to another format. Use it when you want a portable deck, spreadsheet, or full backup outside the app.',
            note: 'JSON is the safest full-fidelity backup. The text formats are convenience exports for external tools.',
            steps: [
                {
                    target: '#export-modal .export-option-btn:nth-of-type(1)',
                    title: 'Anki Export',
                    body: 'Exports the quiz as tab-separated text so it can be imported into Anki. This is the fastest bridge when you still want the quiz to end up as flashcards.'
                },
                {
                    target: '#export-modal .export-option-btn:nth-of-type(2)',
                    title: 'Quizlet Export',
                    body: 'Creates a Quizlet-friendly text export for quick copy-paste import.'
                },
                {
                    target: '#export-modal .export-option-btn:nth-of-type(3)',
                    title: 'CSV Export',
                    body: 'CSV is useful when you want the quiz in Excel, Google Sheets, or any workflow that benefits from spreadsheet sorting and filtering.'
                },
                {
                    target: '#export-modal .export-option-btn:nth-of-type(4)',
                    title: 'JSON Backup',
                    body: 'JSON keeps the richest version of the quiz data. Use this when you care most about preserving the structure for later re-import or archival.'
                },
                {
                    target: '#export-saved-only',
                    title: 'Export Saved Questions Only',
                    body: 'Turn this on when you want the export to include only the questions you explicitly saved instead of the entire quiz.'
                }
            ]
        },
        'theme-modal': {
            title: 'Appearance',
            intro: 'Appearance changes the look and feel of the study workspace. Layout, motion, and ambience settings also change how the workspace behaves while you study.',
            note: 'If you over-tune it, Reset Defaults gives you a quick way back to a clean baseline.',
            steps: [
                {
                    target: '#theme-modal .gradient-grid',
                    title: 'Gradient Presets',
                    body: 'These swatches change the primary accent treatment for the app. It is the quickest way to shift the visual identity without touching every color manually.'
                },
                {
                    target: '#theme-modal .custom-grad-row',
                    title: 'Custom Gradient',
                    body: 'Use the start and end color pickers when you want a custom accent pair instead of the preset gradients.'
                },
                {
                    target: '#font-select',
                    title: 'Layout And Style',
                    body: 'Font changes the reading feel of the workspace, and Border Mode controls the split-view frame treatment.'
                },
                {
                    target: '#ambient-btn',
                    title: 'Ambient Mode',
                    body: 'Ambient mode adds a more animated atmosphere to the workspace. Once it is enabled, the controls underneath define glow size, motion style, visual mode, audio sync, and beat-color behavior.'
                },
                {
                    target: '#reset-theme-btn',
                    title: 'Reset Defaults',
                    body: 'Use this when the theme has drifted too far. It restores the default visual settings without making you undo each option one by one.'
                }
            ]
        },
        'api-key-modal': {
            title: 'Connect Gemini',
            intro: 'This is the final setup step after you choose what to make. A free Gemini key lets Memora create the selected study material and stays in this browser.',
            steps: [
                {
                    target: '#api-key-modal a',
                    title: 'Get API Key Link',
                    body: 'This takes the user to Google AI Studio, where the free key is created.'
                },
                {
                    target: '#gemini-api-key-input',
                    title: 'API Key Field',
                    body: 'Paste the Gemini key here. Memora stores it locally on this device rather than making you re-enter it for each AI action.'
                },
                {
                    target: '#gemini-api-key-save-btn',
                    title: 'Save And Continue',
                    body: 'This saves the key locally, then continues the Quiz, Flashcards, Study Guide, or Guided Tutor action you already chose.'
                },
                {
                    target: '#api-key-modal .theme-close-btn',
                    title: 'Cancel',
                    body: 'Cancel closes the popup without changing the saved key state.'
                }
            ]
        },
        'notes-modal': {
            title: 'Question Notes',
            intro: 'Notes lets you attach your own memory anchors to the current question. It is useful for mnemonics, why-you-missed-it notes, or one-line clinical reminders.',
            steps: [
                {
                    target: '#notes-input',
                    title: 'Notes Field',
                    body: 'Write the note exactly how you want to see it later. These notes are saved locally, so they become part of your personal study layer for the question.'
                },
                {
                    target: '#notes-modal .welcome-btn.primary',
                    title: 'Save Note',
                    body: 'Save attaches the note to the current question so it is available the next time you revisit it.'
                },
                {
                    target: '#notes-modal .welcome-btn:not(.primary)',
                    title: 'Cancel',
                    body: 'Cancel closes the popup without saving the current edits.'
                }
            ]
        },
        'history-modal': {
            title: 'Session History',
            intro: 'History is the fastest way to resume work without rebuilding the session. It keeps prior sessions close enough that review starts in seconds instead of from scratch.',
            steps: [
                {
                    target: '#history-list',
                    title: 'Saved Sessions',
                    body: 'This list holds the reopenable sessions for the current browser. Use it when you want to return to an older quiz state quickly.'
                },
                {
                    target: '#history-modal .welcome-btn:nth-of-type(1)',
                    title: 'Start New Session',
                    body: 'Starts fresh instead of reopening existing work.'
                },
                {
                    target: '#history-modal .welcome-btn:nth-of-type(2)',
                    title: 'Recently Deleted',
                    body: 'Opens the recycle-bin style view for sessions that were deleted but are still recoverable.'
                }
            ]
        },
        'stats-modal': {
            title: 'Study Statistics',
            intro: 'Statistics turns the current study history into a performance snapshot. Use it to spot streaks, weak areas, and whether the workload is actually moving in the right direction.',
            steps: [
                {
                    target: '#xp-stats-card',
                    title: 'Level And Progress',
                    body: 'Your level, progress to the next one, streaks, average answer time, and any active power-ups.'
                },
                {
                    target: '#heatmap-container',
                    title: 'Activity Heatmap',
                    body: 'Use the heatmap to see consistency over the last 30 or 90 days instead of relying on memory or streak feel.'
                },
                {
                    target: '#stats-attention-list',
                    title: 'Needs Attention',
                    body: 'The questions you miss most often, which is usually the highest-yield place to focus review. Select one to jump to it.'
                },
                {
                    target: '#stats-modal .stats-tab-bar',
                    title: 'More Views',
                    body: 'Lecture Analytics breaks progress down by source, Hardest Qs holds the full ranked list with a practice deck, and Badges tracks achievements.'
                }
            ]
        }
    };
    const nativeFetch = window.fetch.bind(window);
    const BACKEND_AI_SENTINEL_KEY = '__memora_backend__';
    let publicTourWelcomeToggleResizeObserver = null;
    let publicTourWelcomeToggleResizeTarget = null;
    let publicTourWelcomeToggleResizeBound = false;
    let publicHomeOnboardingLayoutResizeBound = false;
    let publicHomeOnboardingViewportResizeBound = false;
    let publicHomeOnboardingLayoutRaf = 0;
    let publicHomeOnboardingLayoutTimers = [];
    let publicHomeOnboardingWelcomeResizeObserver = null;
    let publicHomeOnboardingWelcomeResizeTarget = null;
    let publicFloatingVisibilityObserver = null;
    let publicFloatingBodyObserver = null;
    let publicFloatingWelcomeObserver = null;

    function safePopup(message, isError) {
        if (typeof window.showPopup === 'function') {
            window.showPopup(message, null, !!isError);
        } else if (isError) {
            console.error(message);
        } else {
            console.log(message);
        }
    }

    function getPlanRules() {
        const userRules = cfg.planRules && typeof cfg.planRules === 'object' ? cfg.planRules : {};
        return { ...DEFAULT_RULES, ...userRules };
    }

    function getRuleForPlan(plan) {
        const rules = getPlanRules();
        return rules[plan] || rules.free;
    }

    function getWebCapabilities() {
        const configured = cfg.webCapabilities && typeof cfg.webCapabilities === 'object'
            ? cfg.webCapabilities
            : {};
        return { ...DEFAULT_WEB_CAPABILITIES, ...configured };
    }

    function capabilityEnabled(capabilityKey) {
        return !!getWebCapabilities()[capabilityKey];
    }

    function getPublicWebMessage(featureName) {
        const normalized = String(featureName || 'This feature').trim().toLowerCase();
        if (normalized.includes('canvas')) {
            return 'Canvas import is not part of the public web app.';
        }
        if (normalized.includes('powerpoint') || normalized.includes('ppt') || normalized.includes('office')) {
            return 'This import type is not part of the public web app.';
        }
        if (normalized.includes('image')) {
            return 'Image-based question generation is not part of the public web app.';
        }
        return `${featureName || 'This feature'} is not part of the public web app.`;
    }

    function getAIBackendConfig() {
        const configured = cfg.aiBackend && typeof cfg.aiBackend === 'object'
            ? cfg.aiBackend
            : {};
        return {
            enabled: false,
            provider: 'gemini',
            generatePath: '/ai/generate',
            attachUserApiKey: true,
            ...configured
        };
    }

    function aiBackendEnabled() {
        const apiBase = String(cfg.apiBaseUrl || '').trim();
        return !!apiBase && !!getAIBackendConfig().enabled;
    }

    function buildAIBackendUrl() {
        const apiBase = String(cfg.apiBaseUrl || '').trim().replace(/\/+$/, '');
        const generatePath = String(getAIBackendConfig().generatePath || '/ai/generate').trim();
        if (!apiBase) return '';
        return `${apiBase}${generatePath.startsWith('/') ? generatePath : `/${generatePath}`}`;
    }

    function isGeminiGenerateRequest(requestUrl) {
        const normalized = String(requestUrl || '');
        return normalized.includes('generativelanguage.googleapis.com') && normalized.includes(':generateContent');
    }

    function extractGeminiModelFromUrl(requestUrl) {
        const match = String(requestUrl || '').match(/\/models\/([^/:?]+):generateContent/i);
        return match && match[1] ? match[1] : '';
    }

    async function maybeReadJsonBody(body) {
        if (!body) return null;
        if (typeof body === 'string') {
            try {
                return JSON.parse(body);
            } catch (_err) {
                return null;
            }
        }
        if (typeof body === 'object') {
            return body;
        }
        return null;
    }

    function sanitizeManualChoiceText(choice) {
        return String(choice || '')
            .replace(/^[A-F]\.\s*/i, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    function normalizeManualChoiceList(question) {
        let choices = question?.c || question?.o || question?.choices || question?.options || question?.answers || [];
        if (!Array.isArray(choices) && choices && typeof choices === 'object') {
            choices = Object.values(choices);
        }
        if (!Array.isArray(choices)) return [];

        return choices
            .map((choice, index) => {
                if (typeof choice === 'string') return sanitizeManualChoiceText(choice);
                if (choice && typeof choice === 'object') {
                    const nested = choice.text || choice.label || choice.value || choice.answer || choice.choice || '';
                    return sanitizeManualChoiceText(nested || `Choice ${index + 1}`);
                }
                return sanitizeManualChoiceText(choice);
            })
            .filter(Boolean);
    }

    function resolveManualAnswerIndex(question, choices) {
        const numericCandidates = [
            question?.a,
            question?.answerIndex,
            question?.correctIndex,
            question?.correct_index
        ];

        for (const candidate of numericCandidates) {
            const parsed = Number(candidate);
            if (Number.isInteger(parsed) && parsed >= 0 && parsed < choices.length) {
                return parsed;
            }
        }

        const answerCandidates = [
            question?.answer,
            question?.correct,
            question?.correctAnswer,
            question?.correct_answer,
            question?.correctOption
        ];

        for (const candidate of answerCandidates) {
            const value = String(candidate || '').trim();
            if (!value) continue;

            if (/^[A-F]$/i.test(value)) {
                const idx = value.toUpperCase().charCodeAt(0) - 65;
                if (idx >= 0 && idx < choices.length) return idx;
            }

            if (/^\d+$/.test(value)) {
                const zeroBased = Number(value);
                if (zeroBased >= 0 && zeroBased < choices.length) return zeroBased;
                const oneBased = zeroBased - 1;
                if (oneBased >= 0 && oneBased < choices.length) return oneBased;
            }

            const normalizedValue = sanitizeManualChoiceText(value).toLowerCase();
            const matchIndex = choices.findIndex((choice) => sanitizeManualChoiceText(choice).toLowerCase() === normalizedValue);
            if (matchIndex >= 0) return matchIndex;
        }

        return 0;
    }

    function normalizeManualImportQuestion(question, index) {
        const choices = normalizeManualChoiceList(question);
        const answerIndex = resolveManualAnswerIndex(question, choices);
        const prompt = String(
            question?.q ||
            question?.question ||
            question?.prompt ||
            question?.stem ||
            `Question ${index + 1}`
        ).trim();

        return {
            q: prompt,
            c: choices,
            o: choices,
            a: answerIndex,
            answer: String.fromCharCode(65 + Math.max(0, answerIndex)),
            e: String(question?.e || question?.explanation || question?.rationale || question?.reasoning || '').trim(),
            w: question?.w || question?.wrong || question?.wrongExplanations || question?.wrong_explanations || {},
            src: String(question?.src || question?.source || question?.sourceFile || question?.pdf || question?.pdfName || 'Manual Import').trim() || 'Manual Import',
            add: question?.add || question?.page || question?.pageNumber || question?.pageRef || null,
            genModel: question?.genModel || question?.generationModel || question?.generatedModel || question?.aiModel || question?.model || question?.modelName || null
        };
    }

    function repairManualImportPayload(rawText) {
        let parsed = null;
        if (typeof window.parseQuizQuestionsFromModelText === 'function') {
            parsed = window.parseQuizQuestionsFromModelText(rawText);
        } else {
            parsed = JSON.parse(rawText);
        }

        const questions = Array.isArray(parsed)
            ? parsed
            : Array.isArray(parsed?.questions)
                ? parsed.questions
                : Array.isArray(parsed?.quiz)
                    ? parsed.quiz
                    : [parsed];

        const normalized = questions
            .map((question, index) => normalizeManualImportQuestion(question, index))
            .filter((question) => question.q && Array.isArray(question.c) && question.c.length > 0);

        if (normalized.length === 0) {
            throw new Error('No valid questions found in the pasted JSON.');
        }

        return JSON.stringify(normalized, null, 2);
    }

    function overrideGlobalFunction(name, replacement) {
        const original = window[name];
        if (typeof original !== 'function') return;
        if (original.__publicOverrideWrapped) return;

        const wrapped = function (...args) {
            return replacement.call(this, original, ...args);
        };

        wrapped.__publicOverrideWrapped = true;
        wrapped.__original = original;
        window[name] = wrapped;
    }

    function setLocked(isLocked) {
        state.locked = !!isLocked;
        document.body.classList.toggle('public-locked', state.locked);
        const overlay = document.getElementById('public-auth-overlay');
        if (overlay) overlay.classList.toggle('active', state.locked);
    }

    function dismissAuthOverlay() {
        setLocked(false);
    }

    function updateAccountPill() {
        let pill = document.getElementById('public-account-pill');
        if (!pill) {
            pill = document.createElement('div');
            pill.id = 'public-account-pill';
            pill.innerHTML = `
                <span class="plan"></span>
                <span class="credits"></span>
                <button id="public-logout-btn" type="button">Logout</button>
            `;
            document.body.appendChild(pill);
            const logoutBtn = document.getElementById('public-logout-btn');
            if (logoutBtn) logoutBtn.addEventListener('click', handleLogout);
        }

        if (!state.user) {
            pill.classList.remove('active');
            return;
        }

        const planEl = pill.querySelector('.plan');
        const creditsEl = pill.querySelector('.credits');
        if (planEl) planEl.textContent = `Plan: ${(state.plan || 'free').toUpperCase()}`;
        if (creditsEl) creditsEl.textContent = `Credits: ${Math.max(0, Number(state.credits) || 0)}`;
        pill.classList.add('active');

        if (publicTourState.active) {
            window.requestAnimationFrame(() => {
                syncPublicTourStep(false);
            });
        }
    }

    function wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    function isElementVisible(el) {
        if (!el) return false;
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    function normalizePublicTutorialPrefs(rawPrefs = null) {
        const raw = rawPrefs && typeof rawPrefs === 'object' ? rawPrefs : {};
        const goal = ['auto', 'manual', 'study'].includes(raw.goal) ? raw.goal : DEFAULT_PUBLIC_TUTORIAL_PREFS.goal;
        const parsedQuestionCount = Number(raw.questionCount);
        const questionCount = [25, 50, 100].includes(parsedQuestionCount)
            ? parsedQuestionCount
            : DEFAULT_PUBLIC_TUTORIAL_PREFS.questionCount;
        const difficulty = ['easy', 'moderate', 'mixed'].includes(raw.difficulty)
            ? raw.difficulty
            : DEFAULT_PUBLIC_TUTORIAL_PREFS.difficulty;
        return { goal, questionCount, difficulty };
    }

    function loadPublicTutorialPrefs() {
        try {
            const raw = localStorage.getItem(PUBLIC_TUTORIAL_PREFS_KEY);
            if (!raw) return { ...DEFAULT_PUBLIC_TUTORIAL_PREFS };
            return normalizePublicTutorialPrefs(JSON.parse(raw));
        } catch (_err) {
            return { ...DEFAULT_PUBLIC_TUTORIAL_PREFS };
        }
    }

    function getPublicTutorialPrefs() {
        if (!publicTourState.preferences) {
            publicTourState.preferences = loadPublicTutorialPrefs();
        }
        return { ...publicTourState.preferences };
    }

    function setPublicTutorialPrefs(nextPrefs, { persist = false, markSeen = false } = {}) {
        publicTourState.preferences = normalizePublicTutorialPrefs(nextPrefs);
        if (persist) {
            try {
                localStorage.setItem(PUBLIC_TUTORIAL_PREFS_KEY, JSON.stringify(publicTourState.preferences));
                if (markSeen) localStorage.setItem(PUBLIC_TUTORIAL_SEEN_KEY, '1');
            } catch (_err) { }
        }
        return getPublicTutorialPrefs();
    }

    function markPublicTutorialSeen() {
        return setPublicTutorialPrefs(getPublicTutorialPrefs(), { persist: true, markSeen: true });
    }

    function hasSeenPublicTutorial() {
        try {
            return localStorage.getItem(PUBLIC_TUTORIAL_SEEN_KEY) === '1';
        } catch (_err) {
            return false;
        }
    }

    function loadPublicHomeOnboardingHidden() {
        try {
            return localStorage.getItem(PUBLIC_HOME_ONBOARDING_HIDE_KEY) === '1';
        } catch (_err) {
            return false;
        }
    }

    function getPublicHomeOnboardingHidden() {
        if (publicTourState.homeDismissPreference == null) {
            publicTourState.homeDismissPreference = loadPublicHomeOnboardingHidden();
        }
        return !!publicTourState.homeDismissPreference;
    }

    function setPublicHomeOnboardingHidden(nextValue) {
        publicTourState.homeDismissPreference = !!nextValue;
        try {
            if (nextValue) localStorage.setItem(PUBLIC_HOME_ONBOARDING_HIDE_KEY, '1');
            else localStorage.removeItem(PUBLIC_HOME_ONBOARDING_HIDE_KEY);
        } catch (_err) { }
        return !!publicTourState.homeDismissPreference;
    }

    // index.html sets html.public-onboarding-prepaint before the first paint when
    // a first-run visitor is going to land on the onboarding choice card, so the
    // Welcome surfaces never paint only to be swapped out. Release it the moment
    // the real landing takes over, or the moment we know it will not.
    function isPublicOnboardingPrepaintPending() {
        return document.documentElement.classList.contains('public-onboarding-prepaint');
    }

    function releasePublicOnboardingPrepaint() {
        document.documentElement.classList.remove('public-onboarding-prepaint');
    }

    function isPublicTourWelcomeHomeVisible() {
        const welcome = document.getElementById('welcome-screen');
        if (!welcome || !document.body.classList.contains('welcome-mode')) return false;
        if (publicTourState.homeOpen && publicTourState.homeVariant === 'onboarding') return true;
        // The pre-paint gate hides the Welcome screen on purpose; the landing is
        // still the current surface, so do not make the tour wait for a paint
        // that will never happen.
        if (isPublicOnboardingPrepaintPending()) return true;
        return isElementVisible(welcome);
    }

    function isPublicGuideAvailable() {
        const root = document.documentElement;
        return root.classList.contains('phone-device')
            || root.classList.contains('ipad-like-device');
    }

    function shouldRenderPublicHomeOnboarding() {
        return isPublicTourWelcomeHomeVisible()
            && !getPublicHomeOnboardingHidden();
    }

    function pickRandomWelcomeTip() {
        const isPhone = document.documentElement.classList.contains('phone-device');
        const guideAvailable = isPublicGuideAvailable();
        const availableTips = WELCOME_SCREEN_TIPS.filter((entry) => {
            if (isPhone && entry.phoneHidden) return false;
            return guideAvailable || !entry.guideOnly;
        });
        const totalWeight = availableTips.reduce((sum, entry) => {
            const weight = Number(entry?.weight);
            return sum + (Number.isFinite(weight) && weight > 0 ? weight : 1);
        }, 0);

        if (!totalWeight) return '';

        let remaining = Math.random() * totalWeight;
        for (const entry of availableTips) {
            const weight = Number(entry?.weight);
            const normalizedWeight = Number.isFinite(weight) && weight > 0 ? weight : 1;
            remaining -= normalizedWeight;
            if (remaining <= 0) return String(entry?.text || '').trim();
        }

        return String(availableTips[availableTips.length - 1]?.text || '').trim();
    }

    function getRandomWelcomeTip() {
        if (!publicTourState.homeTipText) {
            publicTourState.homeTipText = pickRandomWelcomeTip();
        }
        return publicTourState.homeTipText;
    }

    function ensurePublicWelcomeTipSurface() {
        const welcome = document.getElementById('welcome-screen');
        if (!welcome) return null;

        let tipHost = document.getElementById('public-home-tip-surface');
        const welcomeActions = welcome.querySelector('.welcome-actions');
        if (!tipHost) {
            tipHost = document.createElement('div');
            tipHost.id = 'public-home-tip-surface';
            tipHost.innerHTML = `
                <div class="public-home-tip-copy">
                    <p class="public-home-tip-text"></p>
                </div>
            `;
        }

        if (welcomeActions && tipHost.parentElement !== welcomeActions) {
            welcomeActions.appendChild(tipHost);
        } else if (!welcomeActions) {
            const welcomeParent = welcome.parentElement;
            if (welcomeParent && tipHost.parentElement !== welcomeParent) {
                welcome.insertAdjacentElement('afterend', tipHost);
            } else if (!welcomeParent && tipHost.parentElement !== welcome) {
                welcome.appendChild(tipHost);
            }
        }

        return tipHost;
    }

    function renderPublicWelcomeTip() {
        const tipHost = ensurePublicWelcomeTipSurface();
        if (!tipHost) return;
        const tipText = tipHost.querySelector('.public-home-tip-text');
        if (!tipText) return;
        if (document.documentElement.classList.contains('phone-device')) {
            tipHost.hidden = !publicTourState.phoneComputerTipReady;
            tipText.textContent = 'For the best experience, use Memora on a computer for the full study workspace.';
            return;
        }
        tipHost.hidden = false;
        tipText.textContent = `Tip: ${getRandomWelcomeTip()}`;
    }

    function stripDockControlEmojiText(value) {
        return String(value || '')
            .replace(/[\u2600-\u27BF]\uFE0F?/gu, '')
            .replace(/[\u{1F300}-\u{1FAFF}]\uFE0F?/gu, '')
            .replace(/\uFE0F/gu, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    function installDockControlTextInterceptor(labelEl) {
        if (!labelEl || labelEl.dataset.dockTextInterceptor === 'true') return;
        const textEl = labelEl.querySelector('.dock-control-text');
        if (!textEl) return;

        const innerTextDescriptor = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'innerText');
        const textContentDescriptor = Object.getOwnPropertyDescriptor(Node.prototype, 'textContent');

        Object.defineProperty(labelEl, 'innerText', {
            configurable: true,
            enumerable: true,
            get() {
                return innerTextDescriptor?.get
                    ? innerTextDescriptor.get.call(textEl)
                    : textEl.textContent;
            },
            set(value) {
                const text = stripDockControlEmojiText(value);
                if (innerTextDescriptor?.set) innerTextDescriptor.set.call(textEl, text);
                else textEl.textContent = text;
            }
        });

        Object.defineProperty(labelEl, 'textContent', {
            configurable: true,
            enumerable: true,
            get() {
                return textContentDescriptor?.get
                    ? textContentDescriptor.get.call(textEl)
                    : textEl.textContent;
            },
            set(value) {
                const text = stripDockControlEmojiText(value);
                if (textContentDescriptor?.set) textContentDescriptor.set.call(textEl, text);
                else textEl.textContent = text;
            }
        });

        labelEl.dataset.dockTextInterceptor = 'true';
    }

    function installDockControlTextInterceptors() {
        [
            '#header-import-btn .gradient-text',
            '#toggle-viewer-btn .gradient-text',
            '#toggle-trio-btn .gradient-text',
            '#source-trigger-btn .gradient-text',
            '#menu-trigger-btn .gradient-text'
        ].forEach((selector) => installDockControlTextInterceptor(document.querySelector(selector)));
    }

    function ensureWelcomeTipsMenuButton() {
        const panel = document.getElementById('menu-group-panel-accessibility');
        if (!panel) return null;

        let button = document.getElementById('welcome-tips-menu-btn');
        if (button && button.parentElement !== panel) {
            button.remove();
            button = null;
        }
        if (button) return button;

        button = document.createElement('button');
        button.type = 'button';
        button.className = 'glass-btn';
        button.id = 'welcome-tips-menu-btn';
        button.dataset.menuLeaf = 'true';
        button.title = 'Open the full welcome tips list';
        button.innerHTML = '<span class="gradient-text">All Tips</span>';
        panel.appendChild(button);
        return button;
    }

    function ensurePublicWelcomeTipModal() {
        let modal = document.getElementById('public-home-tip-modal');
        if (modal) return modal;

        modal = document.createElement('div');
        modal.id = 'public-home-tip-modal';
        modal.innerHTML = `
            <div class="public-home-tip-modal-card" role="dialog" aria-modal="true" aria-labelledby="public-home-tip-modal-title">
                <div class="public-home-tip-modal-header">
                    <div>
                        <h3 id="public-home-tip-modal-title" class="public-home-tip-modal-title">Welcome Tips</h3>
                    </div>
                    <button type="button" class="glass-btn theme-close-btn" data-public-home-tip-action="close-list" aria-label="Close tips list">&times;</button>
                </div>
                <div class="public-home-tip-modal-body">
                    <div class="public-home-tip-modal-note">One of these is chosen at random each time the app loads.</div>
                    <div class="public-home-tip-list" data-public-home-tip-list></div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        return modal;
    }

    function renderPublicWelcomeTipModal() {
        const modal = ensurePublicWelcomeTipModal();
        const list = modal?.querySelector('[data-public-home-tip-list]');
        if (!list) return;

        const isPhone = document.documentElement.classList.contains('phone-device');
        const guideAvailable = isPublicGuideAvailable();
        const availableTips = WELCOME_SCREEN_TIPS.filter((entry) => {
            if (isPhone && entry.phoneHidden) return false;
            return guideAvailable || !entry.guideOnly;
        });
        list.innerHTML = availableTips.map((entry) => {
            const text = escapeHtml(String(entry?.text || '').trim());
            return `<div class="public-home-tip-list-item"><span class="public-home-tip-list-mark" aria-hidden="true">&bull;</span><span>${text}</span></div>`;
        }).join('');
    }

    function openPublicWelcomeTipModal() {
        const modal = ensurePublicWelcomeTipModal();
        renderPublicWelcomeTipModal();
        publicTourState.homeTipsOpen = true;
        modal.classList.add('active');
        document.body.classList.add('public-home-tip-modal-open');
    }

    window.openPublicWelcomeTipModal = openPublicWelcomeTipModal;

    function closePublicWelcomeTipModal() {
        const modal = document.getElementById('public-home-tip-modal');
        publicTourState.homeTipsOpen = false;
        if (modal) modal.classList.remove('active');
        document.body.classList.remove('public-home-tip-modal-open');
    }

    function handlePublicWelcomeTipInteraction(event) {
        const actionEl = event.target.closest('[data-public-home-tip-action]');
        if (!actionEl) return;
        event.preventDefault();
        event.stopPropagation();

        const action = String(actionEl.dataset.publicHomeTipAction || '').trim().toLowerCase();
        if (action === 'open-list') {
            openPublicWelcomeTipModal();
            return;
        }
        if (action === 'close-list') {
            closePublicWelcomeTipModal();
        }
    }

    function syncPublicTourChromeState() {
        const donate = document.querySelector('.welcome-donate');
        const welcomeScreen = document.getElementById('welcome-screen');
        const { overlay } = getPublicTourUi();
        const overlayVisible = publicTourState.active
            || (publicTourState.homeEmphasized && publicTourState.homeVariant !== 'onboarding');
        const homeOnboardingActive = publicTourState.homeOpen && publicTourState.homeVariant === 'onboarding';
        const homeOnboardingChoice = homeOnboardingActive && publicTourState.homeOnboardingStage === 'choice';

        if (overlay) {
            overlay.hidden = !overlayVisible;
            if (publicTourState.active) {
                overlay.title = PUBLIC_TOUR_BLOCKED_HINT;
                overlay.setAttribute('aria-label', PUBLIC_TOUR_BLOCKED_HINT);
            } else {
                overlay.removeAttribute('title');
                overlay.removeAttribute('aria-label');
            }
        }
        document.body.classList.toggle('public-tour-active', overlayVisible);
        document.body.classList.toggle('public-tour-home-open', publicTourState.homeOpen);
        document.body.classList.toggle(
            'public-tour-home-onboarding',
            homeOnboardingActive
        );
        document.body.classList.toggle('public-tour-home-choice', homeOnboardingChoice);
        // Same task as the class that replaces it, so there is no frame in
        // between where the Welcome screen becomes visible.
        if (homeOnboardingChoice) releasePublicOnboardingPrepaint();
        if (welcomeScreen) {
            welcomeScreen.classList.toggle('public-tour-home-locked', homeOnboardingActive);
            if (homeOnboardingActive) {
                welcomeScreen.setAttribute('inert', '');
            } else {
                welcomeScreen.removeAttribute('inert');
            }
        }
        const warmupPanel = document.getElementById('welcome-warmup-panel');
        if (warmupPanel) {
            if (homeOnboardingActive) {
                warmupPanel.setAttribute('inert', '');
            } else {
                warmupPanel.removeAttribute('inert');
            }
        }
        if (donate?.open && publicTourState.homeOpen && publicTourState.homeVariant === 'onboarding') {
            donate.open = false;
        }
    }

    function getPublicHomeDismissCheckbox(card = getPublicTourUi().card) {
        return card ? card.querySelector('[data-tour-role="hide-home-toggle"]') : null;
    }

    function commitPublicHomeDismissPreference() {
        const checkbox = getPublicHomeDismissCheckbox();
        if (checkbox) setPublicHomeOnboardingHidden(checkbox.checked);
        return getPublicHomeOnboardingHidden();
    }

    async function dismissPublicTourHome({ markSeen = false, animateLanding = false, revealPhoneTip = false } = {}) {
        if (publicTourState.homeTransitioning) return;
        commitPublicHomeDismissPreference();
        if (markSeen) {
            setPublicTutorialPrefs(getPublicTutorialPrefs(), { persist: true, markSeen: true });
            applyPublicTutorialPrefsToApp();
        }

        const { card } = getPublicTourUi();
        const shouldAnimateLanding = !!(
            animateLanding
            && publicTourState.homeVariant === 'onboarding'
            && document.documentElement.classList.contains('phone-device')
            && card
            && !card.hidden
            && !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        );
        if (shouldAnimateLanding) {
            publicTourState.homeTransitioning = true;
            document.body.classList.add('public-tour-home-transitioning');
            document.body.classList.remove('public-tour-home-revealed');
            card.classList.add('public-tour-home-leaving');
            await wait(260);
            // Install the reveal state before onboarding unlocks the welcome
            // content, so its default entrance animation cannot paint first.
            document.body.classList.add('public-tour-home-revealing');
        }

        closePublicTourHome();

        if (revealPhoneTip && document.documentElement.classList.contains('phone-device')) {
            publicTourState.phoneComputerTipReady = true;
            renderPublicWelcomeTip();
        }

        if (shouldAnimateLanding && isPublicTourWelcomeHomeVisible()) {
            window.setTimeout(() => {
                document.body.classList.remove('public-tour-home-revealing');
                document.body.classList.add('public-tour-home-revealed');
            }, 980);
        }
    }

    function scrollPublicHomeStory(scrollEl) {
        if (!scrollEl) return;
        try {
            scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'auto' });
        } catch (_err) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
        }
    }

    function stopPublicHomeStoryPlayback() {
        if (publicTourState.homeStoryTimer) {
            window.clearTimeout(publicTourState.homeStoryTimer);
            publicTourState.homeStoryTimer = 0;
        }
        publicTourState.homeStoryRunId += 1;
        publicTourState.homeStoryDone = false;
        getPublicTourUi().card?.classList.remove('story-playing');
        document.body.classList.remove('public-tour-home-story-playing');
    }

    function setPublicHomeStoryActionsVisible(card = getPublicTourUi().card, visible = false) {
        const footer = card?.querySelector('.public-tour-home-footer');
        const actions = card?.querySelector('[data-tour-role="story-actions"]');
        const closeButton = card?.querySelector('.public-tour-home-close');
        const storyPlaying = !!card?.classList.contains('public-tour-card-home-onboarding') && !visible;
        card?.classList.toggle('story-playing', storyPlaying);
        document.body.classList.toggle('public-tour-home-story-playing', storyPlaying);
        footer?.classList.toggle('is-visible', visible);
        if (closeButton && card?.classList.contains('public-tour-card-home-onboarding')) {
            closeButton.disabled = !visible;
        }
        if (!actions) return;
        actions.classList.toggle('is-visible', visible);
        actions.querySelectorAll('button').forEach((button) => {
            button.disabled = !visible;
        });
    }

    function appendPublicHomeStoryLine(output, scrollEl, line, { visible = false, deferReveal = false, scrollIntoView = true } = {}) {
        if (!output) return null;
        const storyLine = (line && typeof line === 'object') ? line : { text: line };
        const lineEl = document.createElement('div');
        lineEl.className = 'public-tour-story-line';
        if (storyLine.label) lineEl.classList.add('is-feature');
        if (storyLine.closing) lineEl.classList.add('is-closing');
        if (visible) lineEl.classList.add('is-visible');
        if (deferReveal) lineEl.setAttribute('aria-hidden', 'true');
        let characterIndex = 0;
        const appendAnimatedText = (text, target) => {
            String(text || '').split(/(\s+)/u).forEach((token) => {
                if (!token) return;
                if (/^\s+$/u.test(token)) {
                    target.append(document.createTextNode(token));
                    return;
                }
                const wordEl = document.createElement('span');
                wordEl.className = 'public-tour-story-word';
                Array.from(token).forEach((character) => {
                    const characterEl = document.createElement('span');
                    characterEl.className = 'public-tour-story-char';
                    characterEl.style.setProperty('--c', String(characterIndex));
                    characterEl.textContent = character;
                    wordEl.append(characterEl);
                    characterIndex += 1;
                });
                target.append(wordEl);
            });
        };

        if (storyLine.label) {
            const bullet = document.createElement('span');
            bullet.className = 'public-tour-story-bullet';
            bullet.setAttribute('aria-hidden', 'true');
            bullet.textContent = '•';
            const emphasis = document.createElement('strong');
            emphasis.className = 'public-tour-story-emphasis';
            appendAnimatedText(storyLine.label, emphasis);
            lineEl.append(bullet, emphasis, document.createTextNode(' '));
        }
        appendAnimatedText(storyLine.text, lineEl);
        output.appendChild(lineEl);
        if (!visible && !deferReveal) {
            window.requestAnimationFrame(() => {
                lineEl.classList.add('is-visible');
            });
        }
        if (scrollIntoView) scrollPublicHomeStory(scrollEl);
        return lineEl;
    }

    function revealPublicHomeStory(card = getPublicTourUi().card) {
        const output = card?.querySelector('[data-tour-role="story-output"]');
        const scrollEl = card?.querySelector('[data-tour-role="story-scroll"]');
        if (!output || !scrollEl) return;

        stopPublicHomeStoryPlayback();
        output.innerHTML = '';
        setPublicHomeStoryActionsVisible(card, false);

        HOME_ONBOARDING_STORY.forEach((line) => {
            appendPublicHomeStoryLine(output, scrollEl, line, { visible: true });
        });

        publicTourState.homeStoryDone = true;
        const demoRoot = card.querySelector('.public-tour-home-hero-demo');
        demoRoot?.classList.add('is-revealed');
        setPublicHomeStoryActionsVisible(card, true);
        scrollPublicHomeStory(scrollEl);
    }

    function startPublicHomeStoryPlayback(card = getPublicTourUi().card) {
        if (publicTourState.homeVariant !== 'onboarding' || !card) return;

        const output = card.querySelector('[data-tour-role="story-output"]');
        const scrollEl = card.querySelector('[data-tour-role="story-scroll"]');
        if (!output || !scrollEl) return;

        stopPublicHomeStoryPlayback();
        output.innerHTML = '';
        publicTourState.homeStoryDone = false;
        setPublicHomeStoryActionsVisible(card, false);
        const storyLineEls = HOME_ONBOARDING_STORY.map((line) => appendPublicHomeStoryLine(output, scrollEl, line, {
            deferReveal: true,
            scrollIntoView: false
        }));

        const runId = publicTourState.homeStoryRunId;
        let storyStarted = false;

        const finalizeStory = () => {
            if (publicTourState.homeStoryRunId !== runId) return;
            publicTourState.homeStoryDone = true;
            scrollPublicHomeStory(scrollEl);
            publicTourState.homeStoryTimer = window.setTimeout(() => {
                if (publicTourState.homeStoryRunId !== runId) return;
                publicTourState.homeStoryTimer = 0;
                setPublicHomeStoryActionsVisible(card, true);
            }, 460);
        };

        const schedule = (delayMs) => {
            publicTourState.homeStoryTimer = window.setTimeout(tick, delayMs);
        };

        const tick = () => {
            if (publicTourState.homeStoryRunId !== runId || publicTourState.homeVariant !== 'onboarding' || !publicTourState.homeOpen) {
                return;
            }
            if (storyStarted) {
                finalizeStory();
                return;
            }

            storyStarted = true;
            storyLineEls.forEach((lineEl) => {
                lineEl?.removeAttribute('aria-hidden');
                lineEl?.classList.add('is-visible');
            });
            const demoRoot = card.querySelector('.public-tour-home-hero-demo');
            demoRoot?.classList.add('is-revealed');
            schedule(860);
        };

        schedule(isPublicHomeDemoViewportEligible() ? 720 : 260);
    }

    // PROTECTED ONBOARDING DEMO SURFACE: edit only when the user explicitly
    // asks to change the demo. Keep this renderer and its playback lifecycle
    // isolated from the live Create/import and quiz-generator surfaces.
    function getPublicHomeDemoMarkup() {
        const sampleQuestion = (typeof PUBLIC_TOUR_SAMPLE_QUESTION === 'object' && PUBLIC_TOUR_SAMPLE_QUESTION)
            ? PUBLIC_TOUR_SAMPLE_QUESTION
            : {
                q: 'Which of these is the demo answer?',
                c: ['Sample choice A', 'Sample choice B', 'Sample choice C', 'Sample choice D'],
                a: 1
            };
        const questionText = String(sampleQuestion.q || 'Which of these is the demo answer?')
            .replace(/^Sample Question\s*[—-]\s*/u, 'From your lecture: ');
        const getDemoQuizCardMarkup = ({ status = 'Question 1 of 25', activeControl = '', wrongChoiceIndex = -1, showWrongFeedback = false } = {}) => {
            const choiceMarkup = (Array.isArray(sampleQuestion.c) ? sampleQuestion.c : [])
                .slice(0, 4)
                .map((choice, index) => {
                    const classes = [
                        index === Number(sampleQuestion.a) ? 'is-demo-answer' : '',
                        index === Number(wrongChoiceIndex) ? 'is-demo-wrong-answer' : ''
                    ].filter(Boolean).join(' ');
                    return `
                        <div class="public-tour-home-demo-choice${classes ? ` ${classes}` : ''}" data-home-demo-choice="${index}">
                            <span>${String.fromCharCode(65 + index)}.</span>
                            <b>${escapeHtml(String(choice || `Choice ${index + 1}`))}</b>
                        </div>
                    `;
                })
                .join('');
            const toolbarControls = [
                ['refresh', '&#8635;'],
                ['auto', 'Auto'],
                ['ask', 'Ask'],
                ['source', 'Source'],
                ['notes', 'Notes']
            ].map(([id, label]) => `
                <span class="public-tour-home-demo-quiz-control${activeControl === id ? ' is-demo-control' : ' is-muted'}" data-home-demo-quiz-control="${id}">${label}</span>
            `).join('');
            return `
                <div class="public-tour-home-demo-quiz-card">
                    <div class="public-tour-home-demo-quiz-visualizer" aria-hidden="true">
                        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                    </div>
                    <div class="public-tour-home-demo-quiz-progress"><span></span></div>
                    <div class="public-tour-home-demo-quiz-meta">
                        <div class="public-tour-home-demo-quiz-toolbar">
                            <span class="public-tour-home-demo-quiz-jump is-muted">Q 1 &#9662;</span>
                            ${toolbarControls}
                        </div>
                        <i>${status}</i>
                    </div>
                    <h4>${escapeHtml(questionText)}</h4>
                    <div class="public-tour-home-demo-choices">${choiceMarkup}</div>
                    <div class="public-tour-home-demo-correct-note">&#10003; Correct &mdash; source: slide 18</div>
                    ${showWrongFeedback ? `
                        <div class="public-tour-home-demo-feedback public-tour-home-demo-wrong-feedback">
                            <strong>Why this answer is wrong</strong>
                            <p>Choice A does not match the relationship in the stem. Choice B is the best-supported answer.</p>
                            <span>Say more</span>
                        </div>
                    ` : ''}
                    <div class="public-tour-home-demo-quiz-nav" aria-hidden="true">
                        <span>Previous</span><span>&#9734; Save</span><span>Next</span>
                    </div>
                </div>
            `;
        };
        const getDemoPdfPaneMarkup = ({ showAsk = false } = {}) => `
            <div class="public-tour-home-demo-pdf-pane">
                <div class="public-tour-home-demo-pdf-toolbar">
                    <b>Sample Lecture.pdf</b>
                    <span>1 / 1</span>
                    <span class="public-tour-home-demo-pdf-close" aria-label="Close source">&times;</span>
                </div>
                <div class="public-tour-home-demo-pdf-page">
                    <strong><span class="public-tour-home-demo-pdf-selection-text">Sample Lecture</span></strong>
                </div>
                ${showAsk ? `
                    <div class="public-tour-home-demo-pdf-ask-card">
                        <div class="public-tour-home-demo-pdf-ask-head">
                            <b>Ask Memoraful</b><span>Selected region</span>
                        </div>
                        <div class="public-tour-home-demo-pdf-ask-input"><span data-home-demo-pdf-question></span><i>ask anything</i></div>
                        <button type="button" class="public-tour-home-demo-pdf-ask-send" tabindex="-1">Send</button>
                        <div class="public-tour-home-demo-pdf-ask-thinking"><i></i><span>Thinking...</span></div>
                        <div class="public-tour-home-demo-pdf-ask-response">
                            <strong>How this source connects</strong>
                            <p>The highlighted passage is kept beside your question as grounded context. Memora explains what it means, connects it to the tested concept, and points out the detail that makes the supported answer the best choice.</p>
                        </div>
                    </div>
                ` : ''}
            </div>
        `;
        const getDemoAskPanelMarkup = () => `
            <div class="public-tour-home-demo-ask-panel">
                <div class="public-tour-home-demo-ask-header">
                    <div class="public-tour-home-demo-ask-mode">
                        <b>Ask Memoraful</b><em>Instant</em><span>&#9662;</span>
                    </div>
                    <div class="public-tour-home-demo-ask-window-controls" aria-hidden="true">
                        <span>&minus;</span><span>100%</span><span>+</span><span>&#9881;</span><span>Duo</span><span>&times;</span>
                    </div>
                </div>
                <div class="public-tour-home-demo-ask-response">
                    <div class="public-tour-home-demo-ask-chat">
                        <div class="public-tour-home-demo-ask-message is-user">
                            <small>You</small><p>Why is choice A wrong?</p>
                        </div>
                        <div class="public-tour-home-demo-ask-message is-assistant">
                            <div class="public-tour-home-demo-ask-answer">
                                <strong>Why A is incorrect</strong>
                                <p>Choice A skips the key relationship tested by the question and reverses the sequence described in the lecture. Choice B preserves that sequence, so it is the answer best supported by the source.</p>
                            </div>
                            <div class="public-tour-home-demo-ask-source"><span>Source</span><b>Sample Lecture</b></div>
                        </div>
                    </div>
                </div>
                <div class="public-tour-home-demo-ask-composer">
                    <div class="public-tour-home-demo-ask-input">ask anything</div>
                    <div class="public-tour-home-demo-ask-actions">
                        <div><span class="is-active">Instant</span><span>Thinking</span></div>
                        <i aria-hidden="true">&#9835;</i><b>Send</b>
                    </div>
                </div>
            </div>
        `;

        return `
            <div class="public-tour-home-demo-frame">
                <div class="public-tour-home-demo-viewport">
                    <section class="public-tour-home-demo-scene" data-home-demo-scene="import">
                        <div class="public-tour-home-demo-panel public-tour-home-demo-modal public-tour-home-demo-import-panel">
                            <div class="public-tour-home-demo-modal-header">
                                <h3 data-home-demo-import-title aria-label="Add Files"></h3>
                                <span aria-hidden="true">×</span>
                            </div>
                            <p class="public-tour-home-demo-route-copy">Drop in a lecture PDF to get started.</p>
                            <div class="public-tour-home-demo-drop-zone">
                                <span class="public-tour-home-demo-upload-icon">+</span>
                                <strong>Drag &amp; Drop or Click</strong>
                                <small>PDF</small>
                                <span class="public-tour-home-demo-drop-success">&#10003; PDF ready</span>
                            </div>
                        </div>
                        <div class="public-tour-home-demo-file-picker" aria-hidden="true">
                            <div class="public-tour-home-demo-file-picker-title">
                                <b>File Explorer</b><span>&#215;</span>
                            </div>
                            <div class="public-tour-home-demo-file-picker-path">
                                <span class="public-tour-home-demo-file-picker-back">&#8249;</span>
                                <svg class="public-tour-home-demo-folder-icon" viewBox="0 0 24 20" aria-hidden="true">
                                    <path d="M2.25 4.25A2.25 2.25 0 0 1 4.5 2h5l2.1 2.25h7.9A2.5 2.5 0 0 1 22 6.75v8.75a2.5 2.5 0 0 1-2.5 2.5h-15A2.5 2.5 0 0 1 2 15.5V4.75c0-.17.02-.34.05-.5h.2Z" fill="currentColor"/>
                                    <path d="M2.35 7h19.3" fill="none" stroke="rgba(255,255,255,.5)" stroke-width="1.1"/>
                                </svg>
                                <b>Files</b><i>Lectures</i>
                            </div>
                            <div class="public-tour-home-demo-file-picker-body">
                                <div class="public-tour-home-demo-file-picker-sidebar">
                                    <span class="is-active">Recent</span>
                                    <span>Desktop</span>
                                    <span>Downloads</span>
                                    <span>Cloud Drive</span>
                                </div>
                                <div class="public-tour-home-demo-file-picker-list">
                                    <div class="public-tour-home-demo-file-picker-columns"><span>Name</span><span>Type</span><span>Size</span></div>
                                    <div class="public-tour-home-demo-file-row is-target-file">
                                        <span class="public-tour-home-demo-file-icon" aria-hidden="true">
                                            <svg viewBox="0 0 24 28"><path d="M4 1.5h10l6 6V24a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 2 24V4a2.5 2.5 0 0 1 2-2.5Z" fill="currentColor"/><path d="M14 1.5v6h6" fill="rgba(255,255,255,.34)"/><path d="M6.5 17.5h11M6.5 21h8" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>
                                        </span>
                                        <b>Cardiology Week 3.pdf</b><i>PDF</i><em>4.8 MB</em>
                                    </div>
                                    <div class="public-tour-home-demo-file-row">
                                        <span class="public-tour-home-demo-file-icon" aria-hidden="true">
                                            <svg viewBox="0 0 24 28"><path d="M4 1.5h10l6 6V24a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 2 24V4a2.5 2.5 0 0 1 2-2.5Z" fill="currentColor"/><path d="M14 1.5v6h6" fill="rgba(255,255,255,.34)"/><path d="M6.5 17.5h11M6.5 21h8" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>
                                        </span>
                                        <b>Neuroanatomy Review.pdf</b><i>PDF</i><em>6.1 MB</em>
                                    </div>
                                    <div class="public-tour-home-demo-file-row">
                                        <span class="public-tour-home-demo-file-icon is-text" aria-hidden="true">
                                            <svg viewBox="0 0 24 28"><path d="M4 1.5h10l6 6V24a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 2 24V4a2.5 2.5 0 0 1 2-2.5Z" fill="currentColor"/><path d="M14 1.5v6h6" fill="rgba(255,255,255,.34)"/><path d="M6.5 14h11M6.5 17.5h11M6.5 21h8" fill="none" stroke="white" stroke-width="1.35" stroke-linecap="round"/></svg>
                                        </span>
                                        <b>Exam study plan.txt</b><i>Text</i><em>12 KB</em>
                                    </div>
                                </div>
                            </div>
                            <div class="public-tour-home-demo-file-picker-footer">
                                <span>PDF, JSON, or TXT</span><i>Cancel</i><b class="public-tour-home-demo-file-open">Add</b>
                            </div>
                        </div>
                    </section>

                    <section class="public-tour-home-demo-scene" data-home-demo-scene="generate">
                        <div class="public-tour-home-demo-generator-modal active" data-home-demo-generator>
                            <div class="modal-content quiz-gen-modal-content">
                                <div data-home-demo-generator-header>
                                    <h2 class="gradient-text" style="margin:0;">Generate Quiz</h2>
                                    <div data-home-demo-generator-header-actions>
                                        <button data-home-demo-generator-action="add-more" class="glass-btn" type="button"><span class="gradient-text">+ Add More</span></button>
                                        <button data-home-demo-generator-action="view-quizzes" class="glass-btn" type="button"><span class="gradient-text">Quizzes</span></button>
                                        <button data-home-demo-generator-action="close" class="glass-btn" type="button" aria-label="Close"><span class="gradient-text">&times;</span></button>
                                    </div>
                                </div>
                                <div class="quiz-gen-landscape">
                                    <aside class="quiz-gen-source-pane" aria-labelledby="public-tour-home-demo-source-title">
                                        <div class="quiz-gen-pane-heading">
                                            <div><div class="quiz-gen-pane-title" id="public-tour-home-demo-source-title">Your Content</div></div>
                                            <span class="quiz-gen-source-count">1 selected</span>
                                        </div>
                                        <div class="quiz-gen-pdf-list-wrap">
                                            <div class="quiz-gen-pdf-list" data-home-demo-pdf-list>
                                                <div class="quiz-gen-pdf-item selected" data-pdf-name="Cardiology Week 3.pdf">
                                                    <input type="checkbox" name="quiz-gen-pdf" checked tabindex="-1">
                                                    <span class="pdf-name">Cardiology Week 3.pdf</span>
                                                    <div class="quiz-gen-existing-badges"><span class="quiz-gen-existing-badge quiz-gen-quiz-badge"><span class="quiz-gen-badge-check" aria-hidden="true">&#10003;</span><span>QUIZ</span></span></div>
                                                    <button type="button" class="quiz-gen-pdf-remove-btn" aria-label="Remove PDF" tabindex="-1">
                                                        <svg class="remove-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </aside>
                                    <section class="quiz-gen-config-pane" aria-labelledby="public-tour-home-demo-config-title">
                                        <div class="quiz-gen-pane-heading">
                                            <div><div class="quiz-gen-pane-title" id="public-tour-home-demo-config-title">Quiz Configuration</div></div>
                                        </div>
                                        <div class="quiz-gen-options">
                                            <section class="quiz-gen-preset-step" data-home-demo-preset-step aria-label="Preset modes">
                                                <div class="quiz-gen-preset-picker" data-home-demo-preset-picker role="radiogroup" aria-label="Quiz presets">
                                                    <button type="button" class="quiz-gen-preset-option" data-preset="quick" role="radio" aria-checked="false" tabindex="-1">
                                                        <span class="quiz-gen-preset-icon is-quick" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none" focusable="false"><path d="M18.2 3.5 7.4 18.1h7.1L12.7 28.5l12-15.6h-7.2l.7-9.4Z" fill="currentColor" stroke="currentColor" stroke-linejoin="round"/></svg></span>
                                                        <span class="quiz-gen-preset-content"><span class="quiz-gen-preset-label">Quick</span><span class="quiz-gen-preset-detail">10 questions <span aria-hidden="true">&middot;</span> Easy</span></span>
                                                        <span class="quiz-gen-preset-check" aria-hidden="true">&#10003;</span>
                                                    </button>
                                                    <button type="button" class="quiz-gen-preset-option" data-preset="standard" role="radio" aria-checked="false" tabindex="-1">
                                                        <span class="quiz-gen-preset-icon is-standard" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none" focusable="false"><rect x="7" y="5.5" width="18" height="21" rx="3" stroke="currentColor" stroke-width="2"/><path d="m11 12 2.2 2.2L17 10.5M11 19h10M11 23h7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                                                        <span class="quiz-gen-preset-content"><span class="quiz-gen-preset-label">Standard</span><span class="quiz-gen-preset-detail">25 questions <span aria-hidden="true">&middot;</span> Moderate</span></span>
                                                        <span class="quiz-gen-preset-check" aria-hidden="true">&#10003;</span>
                                                    </button>
                                                    <button type="button" class="quiz-gen-preset-option" data-preset="exam" role="radio" aria-checked="false" tabindex="-1">
                                                        <span class="quiz-gen-preset-icon is-exam" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none" focusable="false"><path d="M8 11.5h16v14H8zM11 11.5V8.8A2.8 2.8 0 0 1 13.8 6h4.4A2.8 2.8 0 0 1 21 8.8v2.7" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M8 16h16M14 19.5h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>
                                                        <span class="quiz-gen-preset-content"><span class="quiz-gen-preset-label">Exam (Hard)</span><span class="quiz-gen-preset-detail">50 questions <span aria-hidden="true">&middot;</span> Difficult</span></span>
                                                        <span class="quiz-gen-preset-check" aria-hidden="true">&#10003;</span>
                                                    </button>
                                                    <button type="button" class="quiz-gen-preset-option" data-preset="custom" role="radio" aria-checked="false" tabindex="-1">
                                                        <span class="quiz-gen-preset-icon is-custom" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none" focusable="false"><path d="M7 9h18M7 16h18M7 23h18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="13" cy="9" r="2.5" fill="currentColor"/><circle cx="21" cy="16" r="2.5" fill="currentColor"/><circle cx="16" cy="23" r="2.5" fill="currentColor"/></svg></span>
                                                        <span class="quiz-gen-preset-content"><span class="quiz-gen-preset-label">Custom</span><span class="quiz-gen-preset-detail">Choose your count, difficulty, and prompt</span></span>
                                                        <span class="quiz-gen-preset-check" aria-hidden="true">&#10003;</span>
                                                    </button>
                                                </div>
                                            </section>
                                        </div>
                                        <div data-home-demo-generator-actions>
                                            <div data-home-demo-generate-wrap><button class="welcome-btn" data-home-demo-generate type="button"><span class="gradient-text">Generate</span></button></div>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="public-tour-home-demo-scene" data-home-demo-scene="quiz">
                        <div class="public-tour-home-demo-workspace public-tour-home-demo-study-workspace">
                            <div class="public-tour-home-demo-study-stage public-tour-home-demo-pdf-workspace public-tour-home-demo-ask-workspace public-tour-home-demo-continuity-workspace">
                                ${getDemoQuizCardMarkup({ status: 'Question 1 of 25', activeControl: 'source', wrongChoiceIndex: 0, showWrongFeedback: true })}
                                ${getDemoPdfPaneMarkup({ showAsk: true })}
                                ${getDemoAskPanelMarkup()}
                            </div>
                        </div>
                    </section>

                    <section class="public-tour-home-demo-scene" data-home-demo-scene="more">
                        <div class="public-tour-home-demo-more-screen">
                            <h3>You Can Also Make:</h3>
                            <ul>
                                <li>Flashcards</li>
                                <li>Study guides</li>
                                <li>Guided tutoring sessions</li>
                                <li>Closed Occlusion exercises</li>
                                <li>Study games</li>
                                <li>Saved-question review sets</li>
                                <li>Review guides from missed questions</li>
                            </ul>
                            <p class="public-tour-home-demo-more-closing">and more</p>
                        </div>
                    </section>

                    <div class="public-tour-home-demo-step-intro" aria-hidden="true">
                        <p data-home-demo-step-intro-text></p>
                    </div>

                    <div class="public-tour-home-demo-cursor" aria-hidden="true">
                        <span class="public-tour-home-demo-mouse"><span></span></span>
                        <span class="public-tour-home-demo-cursor-label">Click</span>
                    </div>
                </div>
                <div class="public-tour-home-demo-controls">
                    <button type="button" data-tour-action="toggle-home-demo" aria-label="Pause preview">
                        <span class="public-tour-home-demo-pause-icon" aria-hidden="true"></span>
                        <span data-home-demo-toggle-label>Pause</span>
                    </button>
                    <div class="public-tour-home-demo-slider-shell">
                        <input type="range" min="1" max="${PUBLIC_HOME_DEMO_SCENES.length + 1}" step="any" value="1"
                            data-home-demo-slider aria-label="Choose preview step"
                            aria-valuetext="1 of ${PUBLIC_HOME_DEMO_SCENES.length}: ${escapeHtml(PUBLIC_HOME_DEMO_SCENES[0].caption)}">
                        <span class="public-tour-home-demo-slider-stops" aria-hidden="true">
                            ${Array.from({ length: PUBLIC_HOME_DEMO_SCENES.length + 1 }, () => '<i></i>').join('')}
                        </span>
                    </div>
                    <output data-home-demo-position>1 / ${PUBLIC_HOME_DEMO_SCENES.length}</output>
                </div>
            </div>
        `;
    }

    function isPublicHomeDemoViewportEligible() {
        if (document.documentElement.classList.contains('phone-device')) return false;
        return !!window.matchMedia?.('(min-width: 981px)')?.matches;
    }

    function isPublicHomeDemoReduced() {
        return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
            || document.body.classList.contains('smooth-mode');
    }

    function syncPublicHomeDemoMotionPreference() {
        publicTourState.homeDemoRoot?.classList.toggle('is-motion-reduced', isPublicHomeDemoReduced());
    }

    function setPublicHomeDemoSliderPosition(root, value) {
        const slider = root?.querySelector('[data-home-demo-slider]');
        if (!slider) return;
        const max = Math.max(2, PUBLIC_HOME_DEMO_SCENES.length + 1);
        const safeValue = Math.max(1, Math.min(max, Number(value) || 1));
        slider.value = String(safeValue);
        slider.style.setProperty('--public-home-demo-slider-progress', `${(safeValue - 1) / Math.max(1, max - 1) * 100}%`);
    }

    function beginPublicHomeDemoSliderTravel(root, sceneIndex, durationMs) {
        const from = Math.max(1, Math.min(PUBLIC_HOME_DEMO_SCENES.length, Number(sceneIndex) + 1));
        publicTourState.homeDemoSliderTravel = {
            root,
            from,
            to: Math.min(PUBLIC_HOME_DEMO_SCENES.length + 1, from + 1),
            duration: Math.max(1, Number(durationMs) || 1),
            elapsed: 0
        };
        setPublicHomeDemoSliderPosition(root, from);
    }

    function advancePublicHomeDemoSlider(root, elapsedMs) {
        const travel = publicTourState.homeDemoSliderTravel;
        if (!travel || travel.root !== root) return;
        travel.elapsed = Math.min(travel.duration, travel.elapsed + Math.max(0, Number(elapsedMs) || 0));
        const progress = travel.elapsed / travel.duration;
        setPublicHomeDemoSliderPosition(root, travel.from + ((travel.to - travel.from) * progress));
    }

    function completePublicHomeDemoSliderTravel(root) {
        const travel = publicTourState.homeDemoSliderTravel;
        if (!travel || travel.root !== root) return;
        setPublicHomeDemoSliderPosition(root, travel.to);
        publicTourState.homeDemoSliderTravel = null;
    }

    function isPublicHomeDemoCurrent(root, runId) {
        return !!(
            publicTourState.homeDemoRunning
            && publicTourState.homeDemoRunId === runId
            && publicTourState.homeDemoRoot === root
            && root?.isConnected
            && publicTourState.homeOpen
            && publicTourState.homeVariant === 'onboarding'
            && isPublicHomeDemoViewportEligible()
        );
    }

    function waitForPublicHomeDemo(durationMs, root, runId) {
        const duration = Math.max(0, Number(durationMs) || 0);
        return new Promise((resolve) => {
            let remaining = duration;
            let previousTime = performance.now();
            let settled = false;

            const finish = (completed) => {
                if (settled) return;
                settled = true;
                if (publicTourState.homeDemoTimer) {
                    window.clearTimeout(publicTourState.homeDemoTimer);
                    publicTourState.homeDemoTimer = 0;
                }
                if (publicTourState.homeDemoWaitResolve === finish) {
                    publicTourState.homeDemoWaitResolve = null;
                }
                resolve(completed);
            };

            const tick = () => {
                if (!isPublicHomeDemoCurrent(root, runId)) {
                    finish(false);
                    return;
                }
                const now = performance.now();
                if (!document.hidden && !publicTourState.homeDemoPaused) {
                    const elapsed = Math.max(0, now - previousTime);
                    remaining -= elapsed;
                    advancePublicHomeDemoSlider(root, elapsed);
                }
                previousTime = now;
                if (remaining <= 0) {
                    finish(true);
                    return;
                }
                publicTourState.homeDemoTimer = window.setTimeout(tick, Math.min(100, Math.max(16, remaining)));
            };

            publicTourState.homeDemoWaitResolve = finish;
            publicTourState.homeDemoTimer = window.setTimeout(tick, duration > 0 ? Math.min(100, duration) : 0);
        });
    }

    function setPublicHomeDemoCursorPosition(root, point) {
        const viewport = root?.querySelector('.public-tour-home-demo-viewport');
        const cursor = root?.querySelector('.public-tour-home-demo-cursor');
        if (!viewport || !cursor) return null;
        const width = Math.max(1, viewport.clientWidth);
        const height = Math.max(1, viewport.clientHeight);
        cursor.style.setProperty('--public-home-demo-cursor-x', `${Math.round(width * (Number(point?.x) || 0) / 100)}px`);
        cursor.style.setProperty('--public-home-demo-cursor-y', `${Math.round(height * (Number(point?.y) || 0) / 100)}px`);
        return cursor;
    }

    function setPublicHomeDemoCursorLabel(root, label = 'Click') {
        const labelEl = root?.querySelector('.public-tour-home-demo-cursor-label');
        if (labelEl) labelEl.textContent = label;
    }

    async function typePublicHomeDemoText(target, text, root, runId, characterDelay = 82) {
        if (!target || !isPublicHomeDemoCurrent(root, runId)) return false;
        target.textContent = '';
        for (const character of Array.from(String(text || ''))) {
            if (!isPublicHomeDemoCurrent(root, runId)) return false;
            const letter = document.createElement('span');
            letter.className = 'public-tour-home-demo-type-letter';
            letter.textContent = character === ' ' ? '\u00a0' : character;
            target.appendChild(letter);
            if (!await waitForPublicHomeDemo(characterDelay, root, runId)) return false;
        }
        return true;
    }

    async function animatePublicHomeDemoCursor(root, from, to, duration, runId, label = 'Click', motion = 'eased') {
        const cursor = setPublicHomeDemoCursorPosition(root, from);
        if (!cursor || !isPublicHomeDemoCurrent(root, runId)) return false;
        setPublicHomeDemoCursorLabel(root, label);
        cursor.classList.toggle('has-wide-label', String(label).length > 8);
        cursor.classList.add('is-visible');

        if (isPublicHomeDemoReduced()) {
            cursor.classList.remove('is-visible', 'is-pressing', 'is-clicking');
            setPublicHomeDemoCursorPosition(root, to);
            return true;
        }

        const safeDuration = Math.max(1, Number(duration) || 1);
        let elapsed = 0;
        while (elapsed < safeDuration) {
            if (!isPublicHomeDemoCurrent(root, runId)) return false;
            if (isPublicHomeDemoReduced()) {
                cursor.classList.remove('is-visible', 'is-pressing', 'is-clicking');
                setPublicHomeDemoCursorPosition(root, to);
                return true;
            }
            const frameMs = Math.min(32, safeDuration - elapsed);
            if (!await waitForPublicHomeDemo(frameMs, root, runId)) return false;
            elapsed += frameMs;
            const progress = Math.min(1, elapsed / safeDuration);
            const eased = motion === 'linear' ? progress : (1 - Math.pow(1 - progress, 3));
            setPublicHomeDemoCursorPosition(root, {
                x: from.x + ((to.x - from.x) * eased),
                y: from.y + ((to.y - from.y) * eased)
            });
        }
        return true;
    }

    async function pressPublicHomeDemoCursor(root, runId, holdMs = 170) {
        const cursor = root?.querySelector('.public-tour-home-demo-cursor');
        if (!cursor || !isPublicHomeDemoCurrent(root, runId)) return false;
        if (isPublicHomeDemoReduced()) return true;
        cursor.classList.add('is-pressing');
        const completed = await waitForPublicHomeDemo(holdMs, root, runId);
        cursor.classList.remove('is-pressing');
        return completed;
    }

    async function activatePublicHomeDemoScene(root, sceneMeta, sceneIndex, runId) {
        const sceneId = String(sceneMeta.sceneId || sceneMeta.id);
        const scene = root?.querySelector(`[data-home-demo-scene="${sceneId}"]`);
        if (!scene || !isPublicHomeDemoCurrent(root, runId)) return null;
        const previous = root.querySelector('.public-tour-home-demo-scene.is-active');
        const isContinuation = !!sceneMeta.continuation && previous === scene;
        if (!isContinuation) {
            if (previous && previous !== scene) {
                previous.classList.remove('is-active');
                previous.classList.add('is-leaving');
            }
            root.classList.add('is-changing-scene');
            root.querySelector('.public-tour-home-demo-cursor')?.classList.remove('is-visible', 'is-pressing', 'is-clicking', 'is-open-scene-punch');
            if (!await waitForPublicHomeDemo(previous ? 150 : 40, root, runId)) return null;
            const sweepIntoScene = root.classList.contains('is-home-demo-sweep-next') && sceneMeta.id === 'quiz';
            root.querySelectorAll('.public-tour-home-demo-scene').forEach((sceneEl) => {
                sceneEl.className = 'public-tour-home-demo-scene';
            });
            if (sweepIntoScene) root.classList.remove('is-home-demo-sweep-next');
            if (sceneMeta.id === 'import') {
                const importTitle = scene.querySelector('[data-home-demo-import-title]');
                scene.style.removeProperty('transform');
                scene.style.removeProperty('transform-origin');
                scene.style.removeProperty('transition');
                if (importTitle) importTitle.textContent = '';
                scene.classList.add('is-import-title-zoom');
            }
            if (sceneMeta.id === 'more') {
                scene.querySelectorAll('.public-tour-home-demo-more-screen li').forEach((item) => item.classList.remove('is-visible'));
                scene.querySelector('.public-tour-home-demo-more-closing')?.classList.remove('is-visible');
            }
            scene.classList.add('is-active');
            if (sweepIntoScene) scene.classList.add('is-demo-sweep-in');
        } else {
            root.querySelector('.public-tour-home-demo-cursor')?.classList.remove('is-pressing');
        }
        root.dataset.homeDemoScene = sceneMeta.id;
        publicTourState.homeDemoSceneIndex = sceneIndex;
        const slider = root.querySelector('[data-home-demo-slider]');
        const position = root.querySelector('[data-home-demo-position]');
        if (slider) {
            setPublicHomeDemoSliderPosition(root, sceneIndex + 1);
            slider.setAttribute('aria-valuetext', `${sceneIndex + 1} of ${PUBLIC_HOME_DEMO_SCENES.length}: ${sceneMeta.caption}`);
        }
        if (position) position.textContent = `${sceneIndex + 1} / ${PUBLIC_HOME_DEMO_SCENES.length}`;
        root.classList.remove('is-changing-scene');
        return scene;
    }

    async function playPublicHomeDemoScene(root, scene, sceneMeta, runId) {
        if (!scene || !isPublicHomeDemoCurrent(root, runId)) return false;
        if (isPublicHomeDemoReduced()) {
            if (sceneMeta.id === 'import') {
                const importTitle = scene.querySelector('[data-home-demo-import-title]');
                scene.classList.remove('is-import-title-zoom');
                if (importTitle) importTitle.textContent = 'Add Files';
                if (!await waitForPublicHomeDemo(160, root, runId)) return false;
            }
            if (sceneMeta.id === 'say-more') {
                const questionEl = scene.querySelector('[data-home-demo-pdf-question]');
                const inputHint = scene.querySelector('.public-tour-home-demo-pdf-ask-input > i');
                if (questionEl) questionEl.textContent = 'Explain this concept';
                if (inputHint) inputHint.textContent = '';
                scene.className = 'public-tour-home-demo-scene is-active is-pdf-closed is-answer-wrong is-feedback-open is-ask-open is-ask-answer-zoom';
            }
            if (sceneMeta.id === 'more') {
                scene.classList.add('is-more-title-visible');
                scene.querySelectorAll('.public-tour-home-demo-more-screen li').forEach((item) => item.classList.add('is-visible'));
                scene.querySelector('.public-tour-home-demo-more-closing')?.classList.add('is-visible');
            }
            scene.classList.add('is-complete');
            return waitForPublicHomeDemo(sceneMeta.id === 'import' ? 600 : 1350, root, runId);
        }

        const targetPoint = (selector, fallback, anchorX = 0.5, anchorY = 0.5) => {
            const viewport = root.querySelector('.public-tour-home-demo-viewport');
            const target = scene.querySelector(selector);
            if (!viewport || !target) return fallback;
            const viewportRect = viewport.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            if (!viewportRect.width || !viewportRect.height || !targetRect.width || !targetRect.height) return fallback;
            const x = ((targetRect.left - viewportRect.left) + (targetRect.width * anchorX)) / viewportRect.width * 100;
            const y = ((targetRect.top - viewportRect.top) + (targetRect.height * anchorY)) / viewportRect.height * 100;
            return {
                x: Math.max(4, Math.min(96, x)),
                y: Math.max(4, Math.min(96, y))
            };
        };

        switch (sceneMeta.id) {
            case 'import': {
                const importTitle = scene.querySelector('[data-home-demo-import-title]');
                if (!await waitForPublicHomeDemo(1120, root, runId)) return false;
                const importTitleText = 'Add Files';
                if (importTitle) {
                    importTitle.textContent = importTitleText;
                    void importTitle.offsetWidth;
                }
                scene.classList.add('is-import-title-revealed');
                if (!await waitForPublicHomeDemo(1100, root, runId)) return false;
                if (!await waitForPublicHomeDemo(260, root, runId)) return false;
                scene.classList.remove('is-import-title-zoom');
                if (!await waitForPublicHomeDemo(620, root, runId)) return false;
                const dropPoint = targetPoint('.public-tour-home-demo-drop-zone', { x: 50, y: 66 });
                if (!await animatePublicHomeDemoCursor(root, { x: 64, y: 24 }, dropPoint, 560, runId, 'Choose PDF')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId, 180)) return false;
                scene.classList.add('is-picker-open');
                if (!await waitForPublicHomeDemo(520, root, runId)) return false;
                const filePoint = targetPoint('.public-tour-home-demo-file-row.is-target-file', { x: 55, y: 48 });
                if (!await animatePublicHomeDemoCursor(root, dropPoint, filePoint, 680, runId, 'Select file')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId, 150)) return false;
                scene.classList.add('is-file-selected');
                if (!await waitForPublicHomeDemo(320, root, runId)) return false;
                const openButtonPoint = targetPoint('.public-tour-home-demo-file-open', { x: 82, y: 82 });
                const openPoint = { x: 64, y: 68 };
                const openTravel = {
                    x: openPoint.x - openButtonPoint.x,
                    y: openPoint.y - openButtonPoint.y
                };
                const cursor = setPublicHomeDemoCursorPosition(root, filePoint);
                if (!cursor) return false;
                setPublicHomeDemoCursorLabel(root, 'Add');
                cursor.classList.remove('is-clicking', 'is-pressing', 'has-wide-label');
                cursor.classList.add('is-visible');
                scene.style.transition = 'none';
                scene.style.transformOrigin = `${openButtonPoint.x.toFixed(2)}% ${openButtonPoint.y.toFixed(2)}%`;
                scene.classList.add('is-file-open-zoom');
                const openTravelDuration = 1100;
                let openTravelElapsed = 0;
                while (openTravelElapsed < openTravelDuration) {
                    const frameMs = Math.min(32, openTravelDuration - openTravelElapsed);
                    if (!await waitForPublicHomeDemo(frameMs, root, runId)) return false;
                    openTravelElapsed += frameMs;
                    const progress = Math.min(1, openTravelElapsed / openTravelDuration);
                    const eased = progress * progress * (3 - (2 * progress));
                    scene.style.transform = `translate3d(${(openTravel.x * eased).toFixed(3)}%, ${(openTravel.y * eased).toFixed(3)}%, 0) scale(${(1 + (0.78 * eased)).toFixed(4)})`;
                    setPublicHomeDemoCursorPosition(root, {
                        x: filePoint.x + ((openPoint.x - filePoint.x) * eased),
                        y: filePoint.y + ((openPoint.y - filePoint.y) * eased)
                    });
                }
                cursor.classList.add('is-clicking', 'is-pressing', 'is-open-scene-punch');
                scene.style.transition = 'transform 220ms cubic-bezier(0.4, 0, 1, 1), opacity 180ms ease, filter 180ms ease';
                scene.style.transform = `translate3d(${openTravel.x.toFixed(3)}%, ${openTravel.y.toFixed(3)}%, 0) scale(8.6)`;
                scene.classList.add('is-accepted', 'is-import-transition-out');
                const clickCompleted = await waitForPublicHomeDemo(240, root, runId);
                cursor.classList.remove('is-visible', 'is-clicking', 'is-pressing', 'is-open-scene-punch');
                return clickCompleted;
            }
            case 'generate': {
                scene.querySelectorAll('.quiz-gen-preset-option').forEach((option) => {
                    option.classList.remove('is-selected', 'is-demo-selection-pulse');
                    option.setAttribute('aria-checked', 'false');
                });
                scene.classList.remove('is-standard-selected', 'is-generating', 'is-demo-sweep-out');
                scene.querySelector('[data-home-demo-generator]')?.classList.remove('is-generating');
                scene.classList.add('is-preset-zoom');
                if (!await waitForPublicHomeDemo(360, root, runId)) return false;
                const standardOption = scene.querySelector('.quiz-gen-preset-option[data-preset="standard"]');
                const standardPoint = targetPoint('.quiz-gen-preset-option[data-preset="standard"]', { x: 50, y: 50 });
                if (!await animatePublicHomeDemoCursor(root, { x: 28, y: 84 }, standardPoint, 520, runId, 'Standard')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId, 130)) return false;
                standardOption?.classList.add('is-selected', 'is-demo-selection-pulse');
                standardOption?.setAttribute('aria-checked', 'true');
                scene.classList.add('is-standard-selected');
                if (!await waitForPublicHomeDemo(320, root, runId)) return false;
                const generatePoint = targetPoint('[data-home-demo-generate]', { x: 67, y: 88 });
                if (!await animatePublicHomeDemoCursor(root, standardPoint, generatePoint, 520, runId, 'Generate')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                root.querySelector('.public-tour-home-demo-cursor')?.classList.remove(
                    'is-visible',
                    'is-clicking',
                    'is-pressing',
                    'has-wide-label'
                );
                scene.classList.add('is-generating');
                scene.querySelector('[data-home-demo-generator]')?.classList.add('is-generating');
                if (!await waitForPublicHomeDemo(420, root, runId)) return false;
                root.classList.add('is-home-demo-sweep-next');
                scene.classList.add('is-demo-sweep-out');
                return waitForPublicHomeDemo(960, root, runId);
            }
            case 'quiz': {
                const sourceControlPoint = targetPoint('[data-home-demo-quiz-control="source"]', { x: 58, y: 31 });
                scene.style.setProperty('--public-home-demo-source-origin-x', `${sourceControlPoint.x.toFixed(2)}%`);
                scene.style.setProperty('--public-home-demo-source-origin-y', `${sourceControlPoint.y.toFixed(2)}%`);
                scene.classList.add('is-source-control-zoom');
                if (!await waitForPublicHomeDemo(720, root, runId)) return false;
                const sourceClickPoint = targetPoint('[data-home-demo-quiz-control="source"]', sourceControlPoint);
                if (!await animatePublicHomeDemoCursor(root, { x: 82, y: 82 }, sourceClickPoint, 720, runId, 'Source')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                scene.classList.remove('is-source-control-zoom');
                scene.classList.add('is-split');
                if (!await waitForPublicHomeDemo(780, root, runId)) return false;

                const selectionStart = targetPoint('.public-tour-home-demo-pdf-selection-text', { x: 64, y: 51 }, 0.02, 0.55);
                const selectionEnd = targetPoint('.public-tour-home-demo-pdf-selection-text', { x: 85, y: 51 }, 0.98, 0.55);
                const closePoint = targetPoint('.public-tour-home-demo-pdf-close', { x: 91, y: 18 });
                if (!await animatePublicHomeDemoCursor(root, sourceClickPoint, selectionStart, 720, runId, 'Left-click + drag')) return false;
                const cursor = root.querySelector('.public-tour-home-demo-cursor');
                cursor?.classList.add('is-pressing');
                void scene.getBoundingClientRect();
                scene.classList.add('is-selecting');
                if (!await animatePublicHomeDemoCursor(root, selectionStart, selectionEnd, 1040, runId, 'Highlight', 'linear')) return false;
                cursor?.classList.remove('is-pressing');
                scene.classList.add('is-selection-complete');
                if (!await waitForPublicHomeDemo(260, root, runId)) return false;
                scene.classList.add('is-source-selection-zoom');
                if (!await waitForPublicHomeDemo(900, root, runId)) return false;
                scene.classList.add('is-pdf-ask-open');
                if (!await waitForPublicHomeDemo(420, root, runId)) return false;

                const questionEl = scene.querySelector('[data-home-demo-pdf-question]');
                const inputHint = scene.querySelector('.public-tour-home-demo-pdf-ask-input > i');
                const sampleQuestionText = 'Explain this concept';
                if (inputHint) inputHint.textContent = '';
                if (!await typePublicHomeDemoText(questionEl, sampleQuestionText, root, runId, 58)) return false;

                const sendPoint = targetPoint('.public-tour-home-demo-pdf-ask-send', { x: 88, y: 78 });
                if (!await animatePublicHomeDemoCursor(root, selectionEnd, sendPoint, 520, runId, 'Send')) return false;
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                scene.classList.add('is-pdf-ask-sending');
                if (!await waitForPublicHomeDemo(900, root, runId)) return false;
                scene.classList.add('is-pdf-ask-answered');
                if (!await waitForPublicHomeDemo(800, root, runId)) return false;
                if (!await waitForPublicHomeDemo(500, root, runId)) return false;
                scene.classList.add('is-source-selection-returning');
                scene.classList.remove('is-source-selection-zoom');
                if (!await animatePublicHomeDemoCursor(root, sendPoint, closePoint, 900, runId, 'Close source')) return false;
                scene.classList.remove('is-source-selection-returning');
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                scene.classList.add('is-pdf-closing', 'is-scene-outro');
                return waitForPublicHomeDemo(360, root, runId);
            }
            case 'say-more': {
                const questionEl = scene.querySelector('[data-home-demo-pdf-question]');
                const inputHint = scene.querySelector('.public-tour-home-demo-pdf-ask-input > i');
                if (questionEl) questionEl.textContent = 'Explain this concept';
                if (inputHint) inputHint.textContent = '';
                scene.className = 'public-tour-home-demo-scene is-active is-pdf-closed is-review-ready';
                if (!await waitForPublicHomeDemo(420, root, runId)) return false;
                const answerChoicePoint = targetPoint('.public-tour-home-demo-choice.is-demo-wrong-answer', { x: 42, y: 49 });
                if (!await animatePublicHomeDemoCursor(root, { x: 78, y: 82 }, answerChoicePoint, 850, runId, 'Choose A')) return false;
                scene.classList.add('is-answer-choice-hover');
                if (!await waitForPublicHomeDemo(180, root, runId)) return false;
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                scene.classList.remove('is-answer-choice-hover');
                scene.classList.add('is-answer-wrong', 'is-feedback-open');
                if (!await waitForPublicHomeDemo(800, root, runId)) return false;
                const sayMoreButtonPoint = targetPoint('.public-tour-home-demo-feedback > span', { x: 62, y: 66 });
                const sayMoreFocusPoint = { x: 58, y: 56 };
                scene.style.setProperty('--public-home-demo-say-more-origin-x', `${sayMoreButtonPoint.x.toFixed(2)}%`);
                scene.style.setProperty('--public-home-demo-say-more-origin-y', `${sayMoreButtonPoint.y.toFixed(2)}%`);
                scene.style.setProperty('--public-home-demo-say-more-travel-x', `${(sayMoreFocusPoint.x - sayMoreButtonPoint.x).toFixed(2)}%`);
                scene.style.setProperty('--public-home-demo-say-more-travel-y', `${(sayMoreFocusPoint.y - sayMoreButtonPoint.y).toFixed(2)}%`);
                scene.classList.add('is-say-more-zoom');
                if (!await animatePublicHomeDemoCursor(root, answerChoicePoint, sayMoreFocusPoint, 1100, runId, 'Say more')) return false;
                const sayMorePoint = targetPoint('.public-tour-home-demo-feedback > span', sayMoreFocusPoint);
                setPublicHomeDemoCursorPosition(root, sayMorePoint);
                scene.classList.add('is-say-more-hover');
                if (!await waitForPublicHomeDemo(220, root, runId)) return false;
                if (!await pressPublicHomeDemoCursor(root, runId)) return false;
                if (!await waitForPublicHomeDemo(480, root, runId)) return false;
                scene.classList.remove('is-say-more-hover', 'is-say-more-zoom');
                scene.classList.add('is-ask-open', 'is-ask-answer-zoom');
                return waitForPublicHomeDemo(3000, root, runId);
            }
            case 'more': {
                scene.classList.add('is-more-title-visible');
                if (!await waitForPublicHomeDemo(560, root, runId)) return false;
                const featureItems = Array.from(scene.querySelectorAll('.public-tour-home-demo-more-screen li'));
                for (const item of featureItems) {
                    item.classList.add('is-visible');
                    if (!await waitForPublicHomeDemo(650, root, runId)) return false;
                }
                scene.querySelector('.public-tour-home-demo-more-closing')?.classList.add('is-visible');
                if (!await waitForPublicHomeDemo(650, root, runId)) return false;
                return waitForPublicHomeDemo(1400, root, runId);
            }
            default:
                return waitForPublicHomeDemo(900, root, runId);
        }
    }

    async function runPublicHomeDemoLoop(root, runId, startIndex = 0, initialDelayMs = 0) {
        const sceneCount = PUBLIC_HOME_DEMO_SCENES.length;
        let cycleStart = Math.max(0, Math.min(sceneCount - 1, Number(startIndex) || 0));
        const playStepIntro = async (sceneMeta, sceneIndex) => {
            const intro = root?.querySelector('.public-tour-home-demo-step-intro');
            const introText = intro?.querySelector('[data-home-demo-step-intro-text]');
            if (!intro || !introText || sceneIndex === 0 || sceneMeta.skipIntro) {
                if (intro) intro.className = 'public-tour-home-demo-step-intro';
                if (introText) introText.textContent = '';
                return true;
            }

            const reducedMotion = isPublicHomeDemoReduced();
            intro.className = 'public-tour-home-demo-step-intro';
            introText.textContent = '';
            void intro.offsetWidth;
            intro.classList.add('is-visible');
            root.querySelector('.public-tour-home-demo-cursor')?.classList.remove('is-visible', 'is-pressing', 'is-clicking');
            if (!await waitForPublicHomeDemo(reducedMotion ? 80 : 120, root, runId)) return false;
            const usesSourceReveal = sceneMeta.id === 'quiz';
            const usesReviewReveal = sceneMeta.id === 'say-more';
            const usesLeftToRightReveal = sceneMeta.id === 'generate' || usesSourceReveal || usesReviewReveal;
            if (usesLeftToRightReveal) {
                introText.textContent = sceneMeta.caption;
                void introText.offsetWidth;
                intro.classList.add('is-left-to-right-reveal');
                if (usesSourceReveal) intro.classList.add('is-source-title-reveal');
                if (!await waitForPublicHomeDemo(reducedMotion ? 160 : (usesSourceReveal ? 605 : 720), root, runId)) return false;
            } else if (!await typePublicHomeDemoText(
                introText,
                sceneMeta.caption,
                root,
                runId,
                reducedMotion ? 38 : 44
            )) return false;
            if (!await waitForPublicHomeDemo(
                reducedMotion ? 360 : (usesSourceReveal ? 1720 : (usesLeftToRightReveal ? 520 : 720)),
                root,
                runId
            )) return false;
            intro.classList.add('is-leaving');
            if (!await waitForPublicHomeDemo(reducedMotion ? 160 : 300, root, runId)) return false;
            intro.className = 'public-tour-home-demo-step-intro';
            introText.textContent = '';
            return true;
        };
        try {
            if (initialDelayMs > 0 && !await waitForPublicHomeDemo(initialDelayMs, root, runId)) return;
            while (isPublicHomeDemoCurrent(root, runId)) {
                for (let offset = 0; offset < sceneCount; offset += 1) {
                    const sceneIndex = (cycleStart + offset) % sceneCount;
                    const sceneMeta = PUBLIC_HOME_DEMO_SCENES[sceneIndex];
                    const scene = await activatePublicHomeDemoScene(root, sceneMeta, sceneIndex, runId);
                    if (!scene) return;
                    const reducedMotion = isPublicHomeDemoReduced();
                    const introDuration = sceneIndex === 0 || sceneMeta.skipIntro
                        ? 0
                        : (sceneMeta.id === 'quiz'
                            ? (reducedMotion ? 760 : 2745)
                            : (sceneMeta.id === 'generate' || sceneMeta.id === 'say-more'
                            ? (reducedMotion ? 760 : 1660)
                            : ((sceneMeta.caption.length * (reducedMotion ? 38 : 44)) + (reducedMotion ? 600 : 1140))));
                    const sceneDuration = (reducedMotion ? 1350 : Number(sceneMeta.durationMs || 1)) + introDuration;
                    beginPublicHomeDemoSliderTravel(root, sceneIndex, sceneDuration);
                    if (!await playStepIntro(sceneMeta, sceneIndex)) return;
                    if (!await playPublicHomeDemoScene(root, scene, sceneMeta, runId)) return;
                    scene.classList.add('is-complete');
                    completePublicHomeDemoSliderTravel(root);
                }
                cycleStart = 0;
                if (!await waitForPublicHomeDemo(900, root, runId)) return;
                root.classList.add('is-loop-reset');
                if (!await waitForPublicHomeDemo(isPublicHomeDemoReduced() ? 220 : 380, root, runId)) return;
                root.classList.remove('is-loop-reset');
            }
        } finally {
            if (publicTourState.homeDemoRunId === runId) {
                publicTourState.homeDemoRunning = false;
            }
        }
    }

    function stopPublicHomeDemoLoop({ preserveScene = false, preserveRoot = false, preservePause = false } = {}) {
        publicTourState.homeDemoRunId += 1;
        publicTourState.homeDemoRunning = false;
        publicTourState.homeDemoSliderTravel = null;
        if (publicTourState.homeDemoTimer) {
            window.clearTimeout(publicTourState.homeDemoTimer);
            publicTourState.homeDemoTimer = 0;
        }
        const resolveWait = publicTourState.homeDemoWaitResolve;
        publicTourState.homeDemoWaitResolve = null;
        resolveWait?.(false);
        const root = publicTourState.homeDemoRoot;
        root?.classList.remove(
            'is-changing-scene',
            'is-loop-reset',
            'is-motion-reduced',
            'is-paused'
        );
        root?.querySelector('.public-tour-home-demo-cursor')?.classList.remove('is-visible', 'is-pressing', 'is-clicking', 'is-open-scene-punch');
        const stepIntro = root?.querySelector('.public-tour-home-demo-step-intro');
        if (stepIntro) {
            stepIntro.className = 'public-tour-home-demo-step-intro';
            const stepIntroText = stepIntro.querySelector('[data-home-demo-step-intro-text]');
            if (stepIntroText) stepIntroText.textContent = '';
        }
        if (!preserveScene) {
            root?.querySelectorAll('.public-tour-home-demo-scene').forEach((scene) => {
                scene.className = 'public-tour-home-demo-scene';
            });
            publicTourState.homeDemoSceneIndex = 0;
        }
        if (!preservePause) publicTourState.homeDemoPaused = false;
        if (!preserveRoot) publicTourState.homeDemoRoot = null;
    }

    function startPublicHomeDemoLoop(card = getPublicTourUi().card, startIndex = 0) {
        const requestedIndex = Math.max(0, Math.min(PUBLIC_HOME_DEMO_SCENES.length - 1, Number(startIndex) || 0));
        stopPublicHomeDemoLoop();
        if (publicTourState.homeVariant !== 'onboarding' || !publicTourState.homeOpen || !isPublicHomeDemoViewportEligible()) return;
        const root = card?.querySelector('.public-tour-home-hero-demo');
        if (!root?.classList.contains('is-revealed')) return;
        root.classList.add('is-started');
        publicTourState.homeDemoRoot = root;
        publicTourState.homeDemoRunning = true;
        publicTourState.homeDemoPaused = false;
        publicTourState.homeDemoSceneIndex = requestedIndex;
        root.classList.remove('is-paused');
        const toggleButton = root.querySelector('[data-tour-action="toggle-home-demo"]');
        const toggleLabel = root.querySelector('[data-home-demo-toggle-label]');
        if (toggleLabel) toggleLabel.textContent = 'Pause';
        if (toggleButton) toggleButton.setAttribute('aria-label', 'Pause preview');
        const runId = publicTourState.homeDemoRunId;
        syncPublicHomeDemoMotionPreference();
        const initialDelayMs = !publicTourState.homeDemoInitialDelayDone && requestedIndex === 0 ? 1000 : 0;
        if (initialDelayMs > 0) publicTourState.homeDemoInitialDelayDone = true;
        void runPublicHomeDemoLoop(root, runId, requestedIndex, initialDelayMs);
    }

    function ensurePublicHomeDemoLifecycle() {
        if (publicTourState.homeDemoLifecycleBound) return;
        publicTourState.homeDemoLifecycleBound = true;

        const syncEligibility = () => {
            const { card } = getPublicTourUi();
            syncPublicHomeDemoMotionPreference();
            if (!publicTourState.homeOpen || publicTourState.homeVariant !== 'onboarding') return;
            if (!isPublicHomeDemoViewportEligible()) {
                stopPublicHomeDemoLoop();
                return;
            }
            const root = card?.querySelector('.public-tour-home-hero-demo');
            if (!document.hidden
                && !publicTourState.homeDemoRunning
                && root?.classList.contains('is-revealed')
                && root.classList.contains('is-started')) {
                startPublicHomeDemoLoop(card);
            }
        };

        document.addEventListener('visibilitychange', syncEligibility, { passive: true });
        document.addEventListener('memora:smooth-mode-changed', syncPublicHomeDemoMotionPreference);
        window.addEventListener('resize', syncEligibility, { passive: true });
        const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        motionQuery?.addEventListener?.('change', syncPublicHomeDemoMotionPreference);
    }

    const projectionDistance = 650;
    const minProjectionDepth = 40;

    const themePalettes = {
        constellation: [
            ['--g1', '--g2'],
            ['--g2', '--g1'],
            ['--g1', '--g3'],
            ['--g3', '--g2']
        ]
    };
    const QUIZ_EMPTY_NEURAL_PALETTE = Object.freeze([
        ['--quiz-skeleton-gray-dark', '--quiz-skeleton-gray-mid'],
        ['--quiz-skeleton-gray-mid', '--quiz-skeleton-gray-dark'],
        ['--quiz-skeleton-gray-dark', '--quiz-skeleton-gray-mid'],
        ['--quiz-skeleton-gray-mid', '--quiz-skeleton-gray-dark']
    ]);

    let getWelcomeThemeCoordinatesGlobal = null;

    function installWelcomeNeuralAtlas() {
        getWelcomeThemeCoordinatesGlobal = getThemeCoordinates;
        const canvas = document.getElementById('welcome-neural-canvas');
        if (!canvas || canvas.dataset.neuralAtlasBound === '1') return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.dataset.neuralAtlasBound = '1';
        const phoneLite = document.documentElement.classList.contains('phone-device');
        let welcomeNeuralReadyTimer = 0;
        let lastWelcomeModeState = null;
        let lastRenderedFrameTime = 0;
        let animationController = null;

        function clearWelcomeNeuralReadyTimer() {
            if (!welcomeNeuralReadyTimer) return;
            window.clearTimeout(welcomeNeuralReadyTimer);
            welcomeNeuralReadyTimer = 0;
        }

        function syncWelcomeNeuralInteractivityDelay() {
            const isWelcomeMode = document.body.classList.contains('welcome-mode')
                && !document.body.hasAttribute('data-theme-overhaul');
            if (!isWelcomeMode) {
                if (lastWelcomeModeState === false) return;
                lastWelcomeModeState = false;
                clearWelcomeNeuralReadyTimer();
                if (document.body.classList.contains(WELCOME_NEURAL_INTERACTIVE_CLASS)) {
                    document.body.classList.remove(WELCOME_NEURAL_INTERACTIVE_CLASS);
                }
                animationController?.sync();
                return;
            }
            if (lastWelcomeModeState === true) return;

            lastWelcomeModeState = true;
            clearWelcomeNeuralReadyTimer();
            if (document.body.classList.contains(WELCOME_NEURAL_INTERACTIVE_CLASS)) {
                document.body.classList.remove(WELCOME_NEURAL_INTERACTIVE_CLASS);
            }
            welcomeNeuralReadyTimer = window.setTimeout(() => {
                welcomeNeuralReadyTimer = 0;
                if (document.body.classList.contains('welcome-mode')
                    && !document.body.hasAttribute('data-theme-overhaul')) {
                    document.body.classList.add(WELCOME_NEURAL_INTERACTIVE_CLASS);
                }
            }, WELCOME_NEURAL_INTERACTIVE_DELAY_MS);
            animationController?.sync();
        }

        const welcomeNeuralModeObserver = new MutationObserver(syncWelcomeNeuralInteractivityDelay);
        welcomeNeuralModeObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class', 'data-theme-overhaul']
        });
        window.addEventListener('memora:theme-overhaul-changed', syncWelcomeNeuralInteractivityDelay);
        syncWelcomeNeuralInteractivityDelay();

        function getThemeCoordinates(theme, index) {
            if (theme === 'dna') {
                if (index < 150) {
                    const t = (index / 150) * Math.PI * 4.5;
                    const r = 240;
                    const z = ((index / 150) - 0.5) * 720;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: z };
                } else if (index < 300) {
                    const idx = index - 150;
                    const t = (idx / 150) * Math.PI * 4.5 + Math.PI;
                    const r = 240;
                    const z = ((idx / 150) - 0.5) * 720;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: z };
                } else {
                    const idx = index - 300;
                    const t = (idx / 50) * Math.PI * 4.5;
                    const r = 240 * ((idx % 3 === 0 ? 0.6 : (idx % 3 === 1 ? -0.6 : 0.0)));
                    const z = ((idx / 50) - 0.5) * 720;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: z };
                }
            } else if (theme === 'lattice') {
                if (index < 216) {
                    const gx = (index % 6) - 2.5;
                    const gy = (Math.floor(index / 6) % 6) - 2.5;
                    const gz = (Math.floor(index / 36) % 6) - 2.5;
                    return { x: gx * 105, y: gy * 105, z: gz * 105 };
                } else {
                    const idx = index - 216;
                    const theta = (idx / 134) * Math.PI * 2;
                    const phi = Math.acos(((idx % 13) / 13) * 2 - 1);
                    const r = 420;
                    return {
                        x: Math.sin(phi) * Math.cos(theta) * r,
                        y: Math.sin(phi) * Math.sin(theta) * r,
                        z: Math.cos(phi) * r
                    };
                }
            } else if (theme === 'cardio') {
                const t = (index / 350) * Math.PI * 2;
                const a = 520;
                const denom = 1 + Math.sin(t) * Math.sin(t);
                return {
                    x: Math.cos(t) * a / denom,
                    y: Math.sin(t) * Math.cos(t) * a / denom,
                    z: Math.sin(2 * t) * 240
                };
            } else if (theme === 'brain') {
                if (index < 175) {
                    const t = (index / 175) * Math.PI;
                    const p = (index % 13) * (Math.PI * 2 / 13);
                    const r = 230 + 45 * Math.sin(6 * t) * Math.cos(6 * p);
                    return {
                        x: Math.sin(t) * Math.cos(p) * r - 120,
                        y: Math.sin(t) * Math.sin(p) * r,
                        z: Math.cos(t) * r
                    };
                } else {
                    const idx = index - 175;
                    const t = (idx / 175) * Math.PI;
                    const p = (idx % 13) * (Math.PI * 2 / 13);
                    const r = 230 + 45 * Math.sin(6 * t) * Math.cos(6 * p);
                    return {
                        x: Math.sin(t) * Math.cos(p) * r + 120,
                        y: Math.sin(t) * Math.sin(p) * r,
                        z: Math.cos(t) * r
                    };
                }
            } else if (theme === 'atom') {
                if (index < 110) {
                    const t = (index / 110) * Math.PI * 2;
                    const r = 450;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: 0 };
                } else if (index < 220) {
                    const idx = index - 110;
                    const t = (idx / 110) * Math.PI * 2;
                    const r = 450;
                    return { x: Math.cos(t) * r, y: 0, z: Math.sin(t) * r };
                } else if (index < 330) {
                    const idx = index - 220;
                    const t = (idx / 110) * Math.PI * 2;
                    const r = 450;
                    return { x: 0, y: Math.cos(t) * r, z: Math.sin(t) * r };
                } else {
                    const idx = index - 330;
                    const t = (idx / 20) * Math.PI * 2;
                    const phi = Math.acos(((idx % 5) / 5) * 2 - 1);
                    const r = 70 + (idx % 3) * 20;
                    return {
                        x: Math.sin(phi) * Math.cos(t) * r,
                        y: Math.sin(phi) * Math.sin(t) * r,
                        z: Math.cos(phi) * r
                    };
                }
            } else if (theme === 'starmap') {
                if (index < 80) {
                    const t = (index / 80) * Math.PI * 2;
                    const r = 200;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: 0 };
                } else if (index < 160) {
                    const idx = index - 80;
                    const t = (idx / 80) * Math.PI * 2;
                    const r = 380;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: 0 };
                } else if (index < 240) {
                    const idx = index - 160;
                    const t = (idx / 80) * Math.PI * 2;
                    const r = 560;
                    return { x: Math.cos(t) * r, y: Math.sin(t) * r, z: 0 };
                } else if (index < 250) {
                    const idx = index - 240;
                    return { x: 0, y: 0, z: (idx - 5) * 90 };
                } else {
                    const idx = index - 250;
                    const step = idx % 20;
                    const branch = Math.floor(idx / 20);
                    const scaleVal = step * 22;
                    if (branch === 0) return { x: scaleVal, y: scaleVal, z: step * 8 };
                    if (branch === 1) return { x: -scaleVal, y: scaleVal, z: -step * 8 };
                    if (branch === 2) return { x: scaleVal, y: -scaleVal, z: -step * 8 };
                    if (branch === 3) return { x: -scaleVal, y: -scaleVal, z: step * 8 };
                    return {
                        x: ((idx % 7) * 65) - 195,
                        y: ((idx % 11) * 50) - 250,
                        z: 260
                    };
                }
            } else if (theme === 'synapse') {
                const branch = Math.floor(index / 70);
                const idx = index % 70;
                const progress = idx / 69;
                const weave = Math.sin(progress * Math.PI * 4 + branch * 0.7);
                return {
                    x: -430 + progress * 860,
                    y: (branch - 2) * 76 + weave * 58,
                    z: Math.cos(progress * Math.PI * 3 + branch) * 165 + (branch - 2) * 34
                };
            } else if (theme === 'cortex') {
                const columns = 25;
                const row = Math.floor(index / columns);
                const col = index % columns;
                return {
                    x: (col - 12) * 34,
                    y: (row - 6.5) * 34 + Math.sin(col * 0.76 + row * 0.42) * 68,
                    z: Math.sin(col * 0.48) * 150 + Math.cos(row * 0.72) * 112
                };
            } else if (theme === 'spiral') {
                const t = index * 0.31;
                const r = 26 + (index / 349) * 500;
                return {
                    x: Math.cos(t) * r,
                    y: Math.sin(t) * r,
                    z: Math.sin(index * 0.17) * 220
                };
            } else if (theme === 'gridwave') {
                const gx = (index % 10) - 4.5;
                const gy = (Math.floor(index / 10) % 7) - 3;
                const gz = Math.floor(index / 70) - 2;
                return {
                    x: gx * 72,
                    y: gy * 80 + Math.sin(gx * 0.84 + gz) * 42,
                    z: gz * 130 + Math.cos(gy * 0.9 + gx * 0.24) * 46
                };
            } else if (theme === 'butterfly') {
                const side = index < 175 ? -1 : 1;
                const idx = index % 175;
                const t = (idx / 175) * Math.PI * 2;
                const r = 185 + Math.pow(Math.sin(t * 3), 2) * 118;
                return {
                    x: side * (116 + Math.cos(t) * r),
                    y: Math.sin(t) * r * 0.86,
                    z: Math.sin(t * 2 + side * 0.6) * 210
                };
            } else if (theme === 'orbitals') {
                if (index < 320) {
                    const band = Math.floor(index / 80);
                    const idx = index % 80;
                    const t = (idx / 80) * Math.PI * 2;
                    const angle = band * Math.PI / 4;
                    const ox = Math.cos(t) * 455;
                    const oy = Math.sin(t) * 165;
                    const z = Math.sin(t) * 365;
                    return {
                        x: ox * Math.cos(angle) - oy * Math.sin(angle),
                        y: ox * Math.sin(angle) + oy * Math.cos(angle),
                        z
                    };
                }
                const idx = index - 320;
                const t = idx * 0.72;
                const phi = Math.acos(((idx % 10) / 10) * 2 - 1);
                const r = 74 + (idx % 4) * 16;
                return {
                    x: Math.sin(phi) * Math.cos(t) * r,
                    y: Math.sin(phi) * Math.sin(t) * r,
                    z: Math.cos(phi) * r
                };
            } else if (theme === 'wavefront') {
                const ring = Math.floor(index / 50);
                const idx = index % 50;
                const t = (idx / 50) * Math.PI * 2;
                const r = 112 + ring * 66;
                return {
                    x: Math.cos(t) * r,
                    y: Math.sin(t) * r * 0.72 + (ring - 3) * 12,
                    z: Math.sin(t * 3 + ring * 0.6) * 154
                };
            } else if (theme === 'helixfield') {
                const strand = index % 3;
                const step = Math.floor(index / 3);
                const progress = step / 116;
                const t = progress * Math.PI * 5 + strand * (Math.PI * 2 / 3);
                const r = 205 + strand * 38;
                return {
                    x: Math.cos(t) * r + (strand - 1) * 44,
                    y: Math.sin(t) * r,
                    z: (progress - 0.5) * 720
                };
            } else if (theme === 'crystal') {
                const layer = Math.floor(index / 35);
                const idx = index % 35;
                const t = (idx / 35) * Math.PI * 2 + (layer % 2) * 0.09;
                const r = 318 - Math.abs(layer - 4.5) * 36;
                return {
                    x: Math.cos(t) * r + ((idx % 5) - 2) * 9,
                    y: (layer - 4.5) * 68,
                    z: Math.sin(t) * r
                };
            } else if (theme === 'ribbon') {
                const t = (index / 350) * Math.PI * 4;
                const u = ((index % 17) - 8) / 8;
                const r = 304;
                const width = 122 * u;
                return {
                    x: (r + width * Math.cos(t / 2)) * Math.cos(t),
                    y: (r + width * Math.cos(t / 2)) * Math.sin(t) * 0.66,
                    z: width * Math.sin(t / 2) + Math.sin(t * 3) * 70
                };
            } else if (theme === 'vortex') {
                const progress = index / 349;
                const t = index * 0.31;
                const r = 486 * (1 - progress) + 82;
                return {
                    x: Math.cos(t) * r,
                    y: Math.sin(t) * r,
                    z: (progress - 0.5) * 650 + Math.sin(t * 2) * 72
                };
            } else if (theme === 'mesh') {
                const columns = 25;
                const band = Math.floor(index / columns);
                const col = index % columns;
                const theta = (col / columns) * Math.PI * 2 + (band % 2) * (Math.PI / columns);
                const phi = ((band + 0.5) / 14) * Math.PI;
                const r = 350 + Math.sin(col * 0.78 + band) * 34;
                return {
                    x: Math.sin(phi) * Math.cos(theta) * r,
                    y: Math.cos(phi) * r * 0.86,
                    z: Math.sin(phi) * Math.sin(theta) * r
                };
            } else if (theme === 'pulse') {
                const ring = Math.floor(index / 70);
                const idx = index % 70;
                const t = (idx / 70) * Math.PI * 2;
                const r = 110 + ring * 84 + Math.sin(t * 6) * 18;
                return {
                    x: Math.cos(t) * r,
                    y: Math.sin(t) * r,
                    z: (ring - 2) * 90 + Math.sin(t * 4 + ring) * 148
                };
            } else {
                const theta = (index / 350) * Math.PI * 2;
                const phi = Math.acos(((index % 23) / 23) * 2 - 1);
                const r = 380 + (index % 17) * 22;
                return {
                    x: Math.sin(phi) * Math.cos(theta) * r,
                    y: Math.sin(phi) * Math.sin(theta) * r,
                    z: Math.cos(phi) * r
                };
            }
        }

        function getActivePalette() {
            return themePalettes.constellation;
        }

        const atlasState = {
            width: 0,
            height: 0,
            rotationX: -0.15,
            rotationY: 0.32,
            targetX: -0.15,
            targetY: 0.32,
            dragging: false,
            lastX: 0,
            lastY: 0,
            points: [],
            paletteIndex: 0,
            colorFadeStart: performance.now(),
            spawnFadeStart: 0,
            mouseX: -9999,
            mouseY: -9999,
            transitionStart: 0,
            dimZones: [],
            dimZoneRefreshTime: 0
        };

        function resolveRgb(value, fallback = '#60a5fa') {
            const raw = value.startsWith('--')
                ? getComputedStyle(document.documentElement).getPropertyValue(value).trim()
                : value;
            const probe = document.createElement('span');
            probe.style.color = raw || fallback;
            document.body.appendChild(probe);
            const color = getComputedStyle(probe).color;
            probe.remove();
            const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
            if (!match) return { r: 114, g: 242, b: 255 };
            return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
        }

        function mixRgb(a, b, amount) {
            return {
                r: Math.round(a.r + (b.r - a.r) * amount),
                g: Math.round(a.g + (b.g - a.g) * amount),
                b: Math.round(a.b + (b.b - a.b) * amount)
            };
        }

        function smoothStep(value) {
            return value * value * (3 - (2 * value));
        }

        function paletteColor(index) {
            const activePalette = getActivePalette();
            const [fromToken, toToken] = activePalette[index % activePalette.length];
            return mixRgb(resolveRgb(fromToken), resolveRgb(toToken), 0.42);
        }

        function activeColor(time) {
            const cycleMs = 5000;
            const fadeMs = 1700;
            const elapsed = time - atlasState.colorFadeStart;
            const activePalette = getActivePalette();
            if (elapsed >= cycleMs) {
                const steps = Math.floor(elapsed / cycleMs);
                atlasState.paletteIndex = (atlasState.paletteIndex + steps) % activePalette.length;
                atlasState.colorFadeStart += steps * cycleMs;
            }
            const amount = smoothStep(Math.min(1, (time - atlasState.colorFadeStart) / fadeMs));
            const color = mixRgb(paletteColor(atlasState.paletteIndex), paletteColor(atlasState.paletteIndex + 1), amount);
            return `${color.r}, ${color.g}, ${color.b}`;
        }

        function isVisibleDimTarget(element) {
            if (!element || element.hidden) return false;
            const styles = window.getComputedStyle(element);
            if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) <= 0.01) return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 8 && rect.height > 8;
        }

        function refreshDimZones(time) {
            if (time - atlasState.dimZoneRefreshTime < 120) return;
            atlasState.dimZoneRefreshTime = time;
            const canvasRect = canvas.getBoundingClientRect();
            if (!canvasRect.width || !canvasRect.height) {
                atlasState.dimZones = [];
                return;
            }

            const targets = [
                { element: document.getElementById('welcome-warmup-card'), pad: 18, floor: 0.06 },
                { element: document.getElementById('welcome-status-panel'), pad: 18, floor: 0.08 },
                { element: document.getElementById('quiz-card'), pad: 22, floor: 0.02 },
                { element: document.getElementById('public-tour-card'), pad: 24, floor: 0.02 },
                ...Array.from(document.querySelectorAll('#welcome-screen .welcome-title, #welcome-screen .welcome-btn, #public-home-tip-surface, #public-tour-launcher, .welcome-donate-toggle'))
                    .map((element) => ({ element, pad: element.classList?.contains('welcome-btn') ? 18 : 24, floor: 0.05 }))
            ];
            atlasState.dimZones = targets
                .filter(({ element }) => isVisibleDimTarget(element))
                .map(({ element, pad, floor }) => {
                    const rect = element.getBoundingClientRect();
                    return {
                        left: rect.left - canvasRect.left - pad,
                        right: rect.right - canvasRect.left + pad,
                        top: rect.top - canvasRect.top - pad,
                        bottom: rect.bottom - canvasRect.top + pad,
                        floor
                    };
                });
        }

        function getZoneDimFactor(x, y) {
            if (!atlasState.dimZones.length) return 1;
            const fadeDistance = 96;
            let factor = 1;
            atlasState.dimZones.forEach((zone) => {
                const outsideX = Math.max(zone.left - x, 0, x - zone.right);
                const outsideY = Math.max(zone.top - y, 0, y - zone.bottom);
                const distance = Math.sqrt(outsideX * outsideX + outsideY * outsideY);
                if (distance >= fadeDistance) return;
                const dimFloor = Number.isFinite(zone.floor) ? zone.floor : 0.12;
                const dimBlend = smoothStep(1 - (distance / fadeDistance));
                factor = Math.min(factor, 1 - dimBlend * (1 - dimFloor));
            });
            return factor;
        }

        function makePoints() {
            const currentTheme = getStoredWelcomeNeuralTheme();
            const pointCount = phoneLite ? 150 : 350;
            atlasState.points = Array.from({ length: pointCount }, (_, index) => {
                const themeIndex = phoneLite
                    ? Math.round(index * 349 / Math.max(1, pointCount - 1))
                    : index;
                const coords = getThemeCoordinates(currentTheme, themeIndex);
                return {
                    themeIndex,
                    x: coords.x,
                    y: coords.y,
                    z: coords.z,
                    startX: coords.x,
                    startY: coords.y,
                    startZ: coords.z,
                    targetX: coords.x,
                    targetY: coords.y,
                    targetZ: coords.z,
                    size: themeIndex % 13 === 0 ? 3.1 : 1.4 + (themeIndex % 5) * 0.4,
                    important: themeIndex % 13 === 0,
                    pulse: (themeIndex % 11) * (Math.PI / 5)
                };
            });
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            atlasState.width = canvas.clientWidth;
            atlasState.height = canvas.clientHeight;
            atlasState.pixelRatio = dpr;
            canvas.width = Math.floor(atlasState.width * dpr);
            canvas.height = Math.floor(atlasState.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        function clearCanvasFrame() {
            // Clear in backing-store coordinates. iOS Safari can retain part of a
            // scaled canvas buffer after its visual viewport settles, which leaves
            // stale connection lines behind as the atlas continues to animate.
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
        }

        function project(point, time) {
            const sinX = Math.sin(atlasState.rotationX);
            const cosX = Math.cos(atlasState.rotationX);
            const sinY = Math.sin(atlasState.rotationY);
            const cosY = Math.cos(atlasState.rotationY);
            const wobble = Math.sin(time * 0.001 + point.pulse) * 10;
            const y1 = point.y * cosX - (point.z + wobble) * sinX;
            const z1 = point.y * sinX + (point.z + wobble) * cosX;
            const x2 = point.x * cosY + z1 * sinY;
            const z2 = -point.x * sinY + z1 * cosY;
            const depth = projectionDistance + z2;
            if (!Number.isFinite(depth) || depth <= minProjectionDepth) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            const scale = projectionDistance / depth;
            let vx = atlasState.width * 0.58 + x2 * scale;
            let vy = atlasState.height * 0.52 + y1 * scale;
            if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(scale)) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            // Interactive magnetic sway towards pointer
            if (atlasState.mouseX > -9000 && atlasState.mouseY > -9000) {
                const dx = vx - atlasState.mouseX;
                const dy = vy - atlasState.mouseY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > 0 && dist < 180) {
                    const pull = (1 - dist / 180) * 12 * scale;
                    vx -= (dx / dist) * pull;
                    vy -= (dy / dist) * pull;
                }
            }

            point.screenX = vx;
            point.screenY = vy;

            return {
                x: vx,
                y: vy,
                z: z2,
                scale,
                dim: getZoneDimFactor(vx, vy)
            };
        }

        function drawConnections(projected, rgb, time, spawnAlpha) {
            ctx.lineWidth = 1;
            for (let i = 0; i < projected.length; i += 1) {
                for (let j = i + 1; j < projected.length; j += 1) {
                    const a = projected[i].view;
                    const b = projected[j].view;
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    if (Math.abs(dx) > 86 || Math.abs(dy) > 86) continue;
                    const dz = a.z - b.z;
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz * 0.12);
                    if (distance > 86) continue;
                    const alpha = Math.max(0, 1 - distance / 86) * 0.24;
                    const pathDim = Math.min(
                        a.dim ?? 1,
                        b.dim ?? 1,
                        getZoneDimFactor(a.x + (b.x - a.x) * 0.5, a.y + (b.y - a.y) * 0.5)
                    );
                    const important = projected[i].point.important || projected[j].point.important;
                    ctx.strokeStyle = important ? `rgba(${rgb}, ${alpha * 1.7 * spawnAlpha * pathDim})` : `rgba(${rgb}, ${alpha * spawnAlpha * pathDim})`;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();

                    // Procedural animated synapse signal pulses traveling along connection paths
                    if (!phoneLite && important && (i + j) % 3 === 0) {
                        const fraction = (time * 0.0006 + i * 0.17) % 1;
                        const px = a.x + (b.x - a.x) * fraction;
                        const py = a.y + (b.y - a.y) * fraction;
                        const pz = a.z + (b.z - a.z) * fraction;
                        const pScale = projectionDistance / (projectionDistance + pz);
                        const signalDim = getZoneDimFactor(px, py);

                        // Glowing signal dot
                        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 2.2 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 2.2 * pScale, 0, Math.PI * 2);
                        ctx.fill();

                        // Soft signal aura
                        ctx.fillStyle = `rgba(${rgb}, ${alpha * 0.95 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 4.4 * pScale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        function drawNodes(projected, rgb, time, spawnAlpha) {
            projected.sort((a, b) => a.view.z - b.view.z).forEach(({ point, view }) => {
                const glow = Math.max(0.15, view.scale);
                const pulse = point.important ? 0.24 + Math.sin(time * 0.004 + point.pulse) * 0.12 : 0;
                const dim = view.dim ?? 1;

                // Volumetric radial gradient glow halo for important hubs
                if (point.important && !phoneLite) {
                    const haloRadius = point.size * view.scale * 4.8;
                    const gradient = ctx.createRadialGradient(view.x, view.y, 0, view.x, view.y, haloRadius);
                    gradient.addColorStop(0, `rgba(${rgb}, ${(0.36 + pulse) * spawnAlpha * dim})`);
                    gradient.addColorStop(0.35, `rgba(${rgb}, ${(0.16 + pulse * 0.4) * spawnAlpha * dim})`);
                    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, haloRadius, 0, Math.PI * 2);
                    ctx.fill();
                }

                // Core node body (depth-of-field opacity scaling)
                ctx.fillStyle = point.important
                    ? `rgba(${rgb}, ${(0.52 + pulse) * spawnAlpha * dim})`
                    : `rgba(255, 255, 255, ${(0.28 + glow * 0.32) * spawnAlpha * dim})`;
                ctx.beginPath();
                ctx.arc(view.x, view.y, point.size * view.scale, 0, Math.PI * 2);
                ctx.fill();

                if (point.important) {
                    // Outer structural glowing orbit ring
                    ctx.strokeStyle = `rgba(${rgb}, ${(0.18 + pulse) * spawnAlpha * dim})`;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, point.size * view.scale * 3.4, 0, Math.PI * 2);
                    ctx.stroke();

                    // Organic secondary orbiting satellite particle
                    if (!phoneLite) {
                        const orbitRadius = point.size * view.scale * 5.2;
                        const speed = time * 0.0022 + point.pulse;
                        const sx = view.x + Math.cos(speed) * orbitRadius;
                        const sy = view.y + Math.sin(speed) * orbitRadius;
                        const satelliteDim = getZoneDimFactor(sx, sy);

                        ctx.fillStyle = `rgba(${rgb}, ${0.85 * spawnAlpha * satelliteDim})`;
                        ctx.beginPath();
                        ctx.arc(sx, sy, 1.25 * view.scale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            });
        }

        function frame(time) {
            if (phoneLite && lastRenderedFrameTime && time - lastRenderedFrameTime < 1000 / 30) {
                return;
            }
            const frameDelta = lastRenderedFrameTime
                ? Math.min(50, time - lastRenderedFrameTime)
                : 1000 / 60;
            lastRenderedFrameTime = time;
            if (!atlasState.spawnFadeStart) {
                atlasState.spawnFadeStart = time;
            }
            const spawnFadeDuration = 1800; // 1.8 seconds fade-in
            const spawnAlpha = Math.min(1, Math.max(0, (time - atlasState.spawnFadeStart) / spawnFadeDuration));
            const rgb = activeColor(time);

            // Handle morph transition
            if (atlasState.transitionStart) {
                const elapsed = time - atlasState.transitionStart;
                const progress = Math.min(1, elapsed / 1000);
                const t = smoothStep(progress);

                atlasState.points.forEach((point) => {
                    point.x = point.startX + (point.targetX - point.startX) * t;
                    point.y = point.startY + (point.targetY - point.startY) * t;
                    point.z = point.startZ + (point.targetZ - point.startZ) * t;
                });

                if (progress >= 1) {
                    atlasState.transitionStart = 0;
                }
            }

            const rotationEase = 1 - Math.pow(1 - 0.045, frameDelta / (1000 / 60));
            atlasState.rotationX += (atlasState.targetX - atlasState.rotationX) * rotationEase;
            atlasState.rotationY += (atlasState.targetY - atlasState.rotationY) * rotationEase;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            if (canvas.clientWidth !== atlasState.width
                || canvas.clientHeight !== atlasState.height
                || dpr !== atlasState.pixelRatio) resize();
            if (!atlasState.width || !atlasState.height) {
                return;
            }
            refreshDimZones(time);
            clearCanvasFrame();
            const projected = atlasState.points
                .map((point) => ({ point, view: project(point, time) }))
                .filter(({ view }) => view);
            drawConnections(projected, rgb, time, spawnAlpha);
            drawNodes(projected, rgb, time, spawnAlpha);
            if (!atlasState.dragging && !atlasState.grabbedPoint) {
                atlasState.targetY += 0.0018 * (frameDelta / (1000 / 60));
            }
        }

        canvas.addEventListener('pointerdown', (event) => {
            const rect = canvas.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const clickY = event.clientY - rect.top;

            let closestPoint = null;
            let minDistance = 25;

            for (let i = 0; i < atlasState.points.length; i++) {
                const point = atlasState.points[i];
                if (point.screenX !== undefined && point.screenY !== undefined) {
                    const dx = clickX - point.screenX;
                    const dy = clickY - point.screenY;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < minDistance) {
                        minDistance = dist;
                        closestPoint = point;
                    }
                }
            }

            if (closestPoint) {
                atlasState.grabbedPoint = closestPoint;
            } else {
                atlasState.dragging = true;
            }

            atlasState.lastX = event.clientX;
            atlasState.lastY = event.clientY;
            canvas.setPointerCapture(event.pointerId);
        });

        canvas.addEventListener('pointermove', (event) => {
            const rect = canvas.getBoundingClientRect();
            atlasState.mouseX = event.clientX - rect.left;
            atlasState.mouseY = event.clientY - rect.top;

            if (atlasState.grabbedPoint) {
                const dx = (event.clientX - atlasState.lastX) * 1.3;
                const dy = (event.clientY - atlasState.lastY) * 1.3;

                const grabbed = atlasState.grabbedPoint;
                grabbed.x += dx;
                grabbed.y += dy;
                grabbed.targetX = grabbed.x;
                grabbed.targetY = grabbed.y;

                atlasState.points.forEach((point) => {
                    if (point === grabbed) return;
                    const pDistance = Math.sqrt(
                        (point.x - grabbed.x) ** 2 +
                        (point.y - grabbed.y) ** 2 +
                        (point.z - grabbed.z) ** 2
                    );
                    if (pDistance < 120) {
                        const pull = (1 - pDistance / 120) * 0.35;
                        point.x += dx * pull;
                        point.y += dy * pull;
                        point.targetX = point.x;
                        point.targetY = point.y;
                    }
                });

                canvas.style.cursor = 'grabbing';
                atlasState.lastX = event.clientX;
                atlasState.lastY = event.clientY;
                return;
            }

            if (atlasState.dragging) {
                atlasState.targetY += (event.clientX - atlasState.lastX) * 0.006;
                atlasState.targetX += (event.clientY - atlasState.lastY) * 0.004;
                atlasState.lastX = event.clientX;
                atlasState.lastY = event.clientY;
                canvas.style.cursor = 'grabbing';
                return;
            }

            let hoverNode = false;
            const clickX = event.clientX - rect.left;
            const clickY = event.clientY - rect.top;
            for (let i = 0; i < atlasState.points.length; i++) {
                const point = atlasState.points[i];
                if (point.screenX !== undefined && point.screenY !== undefined) {
                    const dx = clickX - point.screenX;
                    const dy = clickY - point.screenY;
                    if (dx * dx + dy * dy < 625) {
                        hoverNode = true;
                        break;
                    }
                }
            }
            canvas.style.cursor = hoverNode ? 'grab' : 'default';

            atlasState.targetY = ((event.clientX / Math.max(1, atlasState.width)) - 0.5) * 0.9;
            atlasState.targetX = ((event.clientY / Math.max(1, atlasState.height)) - 0.5) * -0.45;
        });

        canvas.addEventListener('pointerup', () => {
            atlasState.dragging = false;
            atlasState.grabbedPoint = null;
            canvas.style.cursor = 'default';
        });

        canvas.addEventListener('pointercancel', () => {
            atlasState.dragging = false;
            atlasState.grabbedPoint = null;
            canvas.style.cursor = 'default';
        });

        canvas.addEventListener('pointerleave', () => {
            atlasState.mouseX = -9999;
            atlasState.mouseY = -9999;
        });
        window.addEventListener('resize', () => {
            resize();
            animationController?.renderStaticFrame();
        }, { passive: true });
        triggerWelcomeThemeMorph = (nextTheme) => {
            const safeNextTheme = normalizeWelcomeNeuralTheme(nextTheme) || selectRandomWelcomeNeuralTheme({ excludeCurrent: false });
            atlasState.spawnFadeStart = performance.now();
            atlasState.transitionStart = performance.now();
            if (!atlasState.points.length) makePoints();
            atlasState.points.forEach((point, index) => {
                point.startX = point.x;
                point.startY = point.y;
                point.startZ = point.z;
                
                const targets = getThemeCoordinates(safeNextTheme, point.themeIndex ?? index);
                point.targetX = targets.x;
                point.targetY = targets.y;
                point.targetZ = targets.z;
            });
        };

        window.addEventListener('memora-welcome-theme-changed', (event) => {
            const nextTheme = event.detail.theme;
            triggerWelcomeThemeMorph(nextTheme);
            animationController?.sync();
        });

        makePoints();
        resize();
        animationController = createNeuralCanvasAnimationController(canvas, {
            isSurfaceActive: () => document.body.classList.contains('welcome-mode')
                && !document.body.hasAttribute('data-theme-overhaul')
                && canvas.clientWidth > 0
                && canvas.clientHeight > 0,
            renderFrame: frame,
            resetFrameClock: () => { lastRenderedFrameTime = 0; }
        });
        animationController.sync();
    }

    function installQuizEmptyNeuralAtlas() {
        const canvas = document.getElementById('quiz-empty-neural-canvas');
        if (!canvas || canvas.dataset.neuralAtlasBound === '1') return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.dataset.neuralAtlasBound = '1';

        const sizeScale = 0.28;

        const atlasState = {
            width: 0,
            height: 0,
            rotationX: -0.15,
            rotationY: 0.32,
            targetX: -0.15,
            targetY: 0.32,
            dragging: false,
            lastX: 0,
            lastY: 0,
            points: [],
            paletteIndex: 0,
            colorFadeStart: performance.now(),
            spawnFadeStart: 0,
            mouseX: -9999,
            mouseY: -9999,
            transitionStart: 0,
            dimZones: [],
            dimZoneRefreshTime: 0
        };
        let animationController = null;

        function resolveRgb(value, fallback = '#626262') {
            const raw = value.startsWith('--')
                ? getComputedStyle(document.documentElement).getPropertyValue(value).trim()
                : value;
            const probe = document.createElement('span');
            probe.style.color = raw || fallback;
            document.body.appendChild(probe);
            const color = getComputedStyle(probe).color;
            probe.remove();
            const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
            if (!match) return { r: 114, g: 242, b: 255 };
            return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
        }

        function mixRgb(a, b, amount) {
            return {
                r: Math.round(a.r + (b.r - a.r) * amount),
                g: Math.round(a.g + (b.g - a.g) * amount),
                b: Math.round(a.b + (b.b - a.b) * amount)
            };
        }

        function smoothStep(value) {
            return value * value * (3 - (2 * value));
        }

        function getActivePalette() {
            return QUIZ_EMPTY_NEURAL_PALETTE;
        }

        function paletteColor(index) {
            const activePalette = getActivePalette();
            const [fromToken, toToken] = activePalette[index % activePalette.length];
            return mixRgb(resolveRgb(fromToken), resolveRgb(toToken), 0.42);
        }

        function activeColor(time) {
            const cycleMs = 5000;
            const fadeMs = 1700;
            const elapsed = time - atlasState.colorFadeStart;
            const activePalette = getActivePalette();
            if (elapsed >= cycleMs) {
                const steps = Math.floor(elapsed / cycleMs);
                atlasState.paletteIndex = (atlasState.paletteIndex + steps) % activePalette.length;
                atlasState.colorFadeStart += steps * cycleMs;
            }
            const amount = smoothStep(Math.min(1, (time - atlasState.colorFadeStart) / fadeMs));
            const color = mixRgb(paletteColor(atlasState.paletteIndex), paletteColor(atlasState.paletteIndex + 1), amount);
            return `${color.r}, ${color.g}, ${color.b}`;
        }

        function isVisibleDimTarget(element) {
            if (!element || element.hidden) return false;
            const styles = window.getComputedStyle(element);
            if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) <= 0.01) return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 8 && rect.height > 8;
        }

        function refreshDimZones(time) {
            if (time - atlasState.dimZoneRefreshTime < 120) return;
            atlasState.dimZoneRefreshTime = time;
            const canvasRect = canvas.getBoundingClientRect();
            if (!canvasRect.width || !canvasRect.height) {
                atlasState.dimZones = [];
                return;
            }

            const targets = [
                { element: document.querySelector('.quiz-empty-state-title'), pad: 20, floor: 0.05 }
            ];
            atlasState.dimZones = targets
                .filter(({ element }) => isVisibleDimTarget(element))
                .map(({ element, pad, floor }) => {
                    const rect = element.getBoundingClientRect();
                    return {
                        left: rect.left - canvasRect.left - pad,
                        right: rect.right - canvasRect.left + pad,
                        top: rect.top - canvasRect.top - pad,
                        bottom: rect.bottom - canvasRect.top + pad,
                        floor
                    };
                });
        }

        function getZoneDimFactor(x, y) {
            if (!atlasState.dimZones.length) return 1;
            const fadeDistance = 96;
            let factor = 1;
            atlasState.dimZones.forEach((zone) => {
                const outsideX = Math.max(zone.left - x, 0, x - zone.right);
                const outsideY = Math.max(zone.top - y, 0, y - zone.bottom);
                const distance = Math.sqrt(outsideX * outsideX + outsideY * outsideY);
                if (distance >= fadeDistance) return;
                const dimFloor = Number.isFinite(zone.floor) ? zone.floor : 0.12;
                const dimBlend = smoothStep(1 - (distance / fadeDistance));
                factor = Math.min(factor, 1 - dimBlend * (1 - dimFloor));
            });
            return factor;
        }

        function makePoints() {
            const currentTheme = 'constellation';
            atlasState.points = Array.from({ length: 350 }, (_, index) => {
                const coords = (getWelcomeThemeCoordinatesGlobal ? getWelcomeThemeCoordinatesGlobal(currentTheme, index) : { x: 0, y: 0, z: 0 });
                return {
                    x: coords.x * sizeScale,
                    y: coords.y * sizeScale,
                    z: coords.z * sizeScale,
                    startX: coords.x * sizeScale,
                    startY: coords.y * sizeScale,
                    startZ: coords.z * sizeScale,
                    targetX: coords.x * sizeScale,
                    targetY: coords.y * sizeScale,
                    targetZ: coords.z * sizeScale,
                    size: (index % 13 === 0 ? 3.1 : 1.4 + (index % 5) * 0.4) * 0.5,
                    important: index % 13 === 0,
                    pulse: (index % 11) * (Math.PI / 5)
                };
            });
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            atlasState.width = canvas.clientWidth;
            atlasState.height = canvas.clientHeight;
            canvas.width = Math.floor(atlasState.width * dpr);
            canvas.height = Math.floor(atlasState.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        function project(point, time) {
            const sinX = Math.sin(atlasState.rotationX);
            const cosX = Math.cos(atlasState.rotationX);
            const sinY = Math.sin(atlasState.rotationY);
            const cosY = Math.cos(atlasState.rotationY);
            const wobble = Math.sin(time * 0.001 + point.pulse) * 3;
            const y1 = point.y * cosX - (point.z + wobble) * sinX;
            const z1 = point.y * sinX + (point.z + wobble) * cosX;
            const x2 = point.x * cosY + z1 * sinY;
            const z2 = -point.x * sinY + z1 * cosY;
            const depth = (projectionDistance * sizeScale) + z2;
            if (!Number.isFinite(depth) || depth <= minProjectionDepth * sizeScale) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            const scale = (projectionDistance * sizeScale) / depth;
            let vx = atlasState.width * 0.5 + x2 * scale;
            let vy = atlasState.height * 0.5 + y1 * scale;
            if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(scale)) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            if (atlasState.mouseX > -9000 && atlasState.mouseY > -9000) {
                const dx = vx - atlasState.mouseX;
                const dy = vy - atlasState.mouseY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > 0 && dist < 120) {
                    const pull = (1 - dist / 120) * 8 * scale;
                    vx -= (dx / dist) * pull;
                    vy -= (dy / dist) * pull;
                }
            }

            point.screenX = vx;
            point.screenY = vy;

            return {
                x: vx,
                y: vy,
                z: z2,
                scale,
                dim: getZoneDimFactor(vx, vy)
            };
        }

        function drawConnections(projected, rgb, time, spawnAlpha) {
            ctx.lineWidth = 0.6;
            const maxConnectDist = 86 * sizeScale;
            for (let i = 0; i < projected.length; i += 1) {
                for (let j = i + 1; j < projected.length; j += 1) {
                    const a = projected[i].view;
                    const b = projected[j].view;
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dz = a.z - b.z;
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz * 0.12);
                    if (distance > maxConnectDist) continue;
                    const alpha = Math.max(0, 1 - distance / maxConnectDist) * 0.22;
                    const pathDim = Math.min(
                        a.dim ?? 1,
                        b.dim ?? 1,
                        getZoneDimFactor(a.x + (b.x - a.x) * 0.5, a.y + (b.y - a.y) * 0.5)
                    );
                    const important = projected[i].point.important || projected[j].point.important;
                    ctx.strokeStyle = important ? `rgba(${rgb}, ${alpha * 1.7 * spawnAlpha * pathDim})` : `rgba(${rgb}, ${alpha * spawnAlpha * pathDim})`;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();

                    if (important && (i + j) % 4 === 0) {
                        const fraction = (time * 0.0006 + i * 0.17) % 1;
                        const px = a.x + (b.x - a.x) * fraction;
                        const py = a.y + (b.y - a.y) * fraction;
                        const pz = a.z + (b.z - a.z) * fraction;
                        const pScale = (projectionDistance * sizeScale) / ((projectionDistance * sizeScale) + pz);
                        const signalDim = getZoneDimFactor(px, py);

                        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 2.2 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 1.25 * pScale, 0, Math.PI * 2);
                        ctx.fill();

                        ctx.fillStyle = `rgba(${rgb}, ${alpha * 0.95 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 2.5 * pScale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        function drawNodes(projected, rgb, time, spawnAlpha) {
            projected.sort((a, b) => a.view.z - b.view.z).forEach(({ point, view }) => {
                const glow = Math.max(0.15, view.scale);
                const pulse = point.important ? 0.24 + Math.sin(time * 0.004 + point.pulse) * 0.12 : 0;
                const dim = view.dim ?? 1;

                if (point.important) {
                    const haloRadius = point.size * view.scale * 3.6;
                    const gradient = ctx.createRadialGradient(view.x, view.y, 0, view.x, view.y, haloRadius);
                    gradient.addColorStop(0, `rgba(${rgb}, ${(0.36 + pulse) * spawnAlpha * dim})`);
                    gradient.addColorStop(0.35, `rgba(${rgb}, ${(0.16 + pulse * 0.4) * spawnAlpha * dim})`);
                    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, haloRadius, 0, Math.PI * 2);
                    ctx.fill();
                }

                ctx.fillStyle = point.important
                    ? `rgba(${rgb}, ${(0.52 + pulse) * spawnAlpha * dim})`
                    : `rgba(255, 255, 255, ${(0.28 + glow * 0.32) * spawnAlpha * dim})`;
                ctx.beginPath();
                ctx.arc(view.x, view.y, point.size * view.scale, 0, Math.PI * 2);
                ctx.fill();

                if (point.important) {
                    ctx.strokeStyle = `rgba(${rgb}, ${(0.18 + pulse) * spawnAlpha * dim})`;
                    ctx.lineWidth = 0.6;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, point.size * view.scale * 2.8, 0, Math.PI * 2);
                    ctx.stroke();

                    const orbitRadius = point.size * view.scale * 4.2;
                    const speed = time * 0.0022 + point.pulse;
                    const sx = view.x + Math.cos(speed) * orbitRadius;
                    const sy = view.y + Math.sin(speed) * orbitRadius;
                    const satelliteDim = getZoneDimFactor(sx, sy);

                    ctx.fillStyle = `rgba(${rgb}, ${0.85 * spawnAlpha * satelliteDim})`;
                    ctx.beginPath();
                    ctx.arc(sx, sy, 0.8 * view.scale, 0, Math.PI * 2);
                    ctx.fill();
                }
            });
        }

        function frame(time) {
            if (!canvas.isConnected) {
                return;
            }

            if (!atlasState.spawnFadeStart) {
                atlasState.spawnFadeStart = time;
            }
            const spawnFadeDuration = 1800;
            const spawnAlpha = Math.min(1, Math.max(0, (time - atlasState.spawnFadeStart) / spawnFadeDuration));
            const rgb = activeColor(time);

            if (atlasState.transitionStart) {
                const elapsed = time - atlasState.transitionStart;
                const progress = Math.min(1, elapsed / 1000);
                const t = smoothStep(progress);

                atlasState.points.forEach((point) => {
                    point.x = point.startX + (point.targetX - point.startX) * t;
                    point.y = point.startY + (point.targetY - point.startY) * t;
                    point.z = point.startZ + (point.targetZ - point.startZ) * t;
                });

                if (progress >= 1) {
                    atlasState.transitionStart = 0;
                }
            }

            atlasState.rotationX += (atlasState.targetX - atlasState.rotationX) * 0.045;
            atlasState.rotationY += (atlasState.targetY - atlasState.rotationY) * 0.045;
            if (canvas.clientWidth !== atlasState.width || canvas.clientHeight !== atlasState.height) resize();
            if (!atlasState.width || !atlasState.height) {
                return;
            }
            refreshDimZones(time);
            // Clear the full backing buffer without the DPR transform. Mobile
            // Safari can otherwise retain stale connection lines between frames.
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
            const projected = atlasState.points
                .map((point) => ({ point, view: project(point, time) }))
                .filter(({ view }) => view);
            drawConnections(projected, rgb, time, spawnAlpha);
            drawNodes(projected, rgb, time, spawnAlpha);
            atlasState.targetY += 0.0018;
        }



        const handleMorph = (event) => {
            if (!canvas.isConnected) {
                window.removeEventListener('memora-welcome-theme-changed', handleMorph);
                return;
            }
            const nextTheme = event.detail.theme;
            const safeNextTheme = normalizeWelcomeNeuralTheme(nextTheme) || selectRandomWelcomeNeuralTheme({ excludeCurrent: false });
            atlasState.spawnFadeStart = performance.now();
            atlasState.transitionStart = performance.now();
            if (!atlasState.points.length) makePoints();
            atlasState.points.forEach((point, index) => {
                point.startX = point.x;
                point.startY = point.y;
                point.startZ = point.z;
                
                const targets = (getWelcomeThemeCoordinatesGlobal ? getWelcomeThemeCoordinatesGlobal(safeNextTheme, index) : { x: 0, y: 0, z: 0 });
                point.targetX = targets.x * sizeScale;
                point.targetY = targets.y * sizeScale;
                point.targetZ = targets.z * sizeScale;
            });
        };
        window.addEventListener('memora-welcome-theme-changed', handleMorph);

        makePoints();
        resize();
        animationController = createNeuralCanvasAnimationController(canvas, {
            isSurfaceActive: () => canvas.clientWidth > 0
                && canvas.clientHeight > 0
                && canvas.offsetParent !== null,
            renderFrame: frame
        });
        animationController.sync();
    }
    window.memoraInstallQuizEmptyNeuralAtlas = installQuizEmptyNeuralAtlas;

    function installMemoraNeuralAtlas(options = {}) {
        const canvas = options.canvas || document.getElementById(options.canvasId || 'pdf-empty-neural-canvas');
        if (!canvas || canvas.dataset.neuralAtlasBound === '1') return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.dataset.neuralAtlasBound = '1';

        const parentEl = options.parentEl
            || (options.parentId ? document.getElementById(options.parentId) : null);
        const dimSelector = options.dimSelector || '.pdf-empty-state-title';
        const dimTargets = Array.isArray(options.dimTargets) && options.dimTargets.length
            ? options.dimTargets
            : [{ selector: dimSelector, pad: 20, floor: 0.05 }];
        const shapeTheme = options.theme || 'dna';
        const sizeScale = Number.isFinite(options.sizeScale) ? options.sizeScale : 0.28;

        const atlasState = {
            width: 0,
            height: 0,
            rotationX: -0.15,
            rotationY: 0.32,
            targetX: -0.15,
            targetY: 0.32,
            dragging: false,
            lastX: 0,
            lastY: 0,
            points: [],
            paletteIndex: 0,
            colorFadeStart: performance.now(),
            spawnFadeStart: 0,
            mouseX: -9999,
            mouseY: -9999,
            transitionStart: 0,
            dimZones: [],
            dimZoneRefreshTime: 0
        };
        let animationController = null;
        const colorCache = new Map();
        const colorContext = document.createElement('canvas').getContext('2d');
        let colorThemeSignature = '';

        function resolveRgb(value, fallback = '#60a5fa') {
            if (colorCache.has(value)) return colorCache.get(value);
            const raw = value.startsWith('--')
                ? getComputedStyle(document.documentElement).getPropertyValue(value).trim()
                : value;
            // A detached canvas normalizes CSS colors without inserting a probe
            // into the page and forcing style/layout work on every frame.
            colorContext.fillStyle = fallback;
            colorContext.fillStyle = raw || fallback;
            const color = colorContext.fillStyle;
            const hex = color.match(/^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i);
            const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
            const rgb = hex
                ? { r: parseInt(hex[1].slice(0, 2), 16), g: parseInt(hex[1].slice(2, 4), 16), b: parseInt(hex[1].slice(4, 6), 16) }
                : match
                    ? { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) }
                    : { r: 114, g: 242, b: 255 };
            colorCache.set(value, rgb);
            return rgb;
        }

        function mixRgb(a, b, amount) {
            return {
                r: Math.round(a.r + (b.r - a.r) * amount),
                g: Math.round(a.g + (b.g - a.g) * amount),
                b: Math.round(a.b + (b.b - a.b) * amount)
            };
        }

        function smoothStep(value) {
            return value * value * (3 - (2 * value));
        }

        function getActivePalette() {
            return themePalettes.constellation;
        }

        function paletteColor(index) {
            const activePalette = getActivePalette();
            const [fromToken, toToken] = activePalette[index % activePalette.length];
            return mixRgb(resolveRgb(fromToken), resolveRgb(toToken), 0.42);
        }

        function activeColor(time) {
            const root = document.documentElement;
            const signature = JSON.stringify([
                root.getAttribute('style'), root.getAttribute('class'),
                root.getAttribute('data-theme-overhaul'),
                document.body.getAttribute('data-theme-overhaul')
            ]);
            if (signature !== colorThemeSignature) {
                colorThemeSignature = signature;
                colorCache.clear();
            }
            const cycleMs = 5000;
            const fadeMs = 1700;
            const elapsed = time - atlasState.colorFadeStart;
            const activePalette = getActivePalette();
            if (elapsed >= cycleMs) {
                const steps = Math.floor(elapsed / cycleMs);
                atlasState.paletteIndex = (atlasState.paletteIndex + steps) % activePalette.length;
                atlasState.colorFadeStart += steps * cycleMs;
            }
            const amount = smoothStep(Math.min(1, (time - atlasState.colorFadeStart) / fadeMs));
            const color = mixRgb(paletteColor(atlasState.paletteIndex), paletteColor(atlasState.paletteIndex + 1), amount);
            return `${color.r}, ${color.g}, ${color.b}`;
        }

        function isVisibleDimTarget(element) {
            if (!element || element.hidden) return false;
            const styles = window.getComputedStyle(element);
            if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) <= 0.01) return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 8 && rect.height > 8;
        }

        function refreshDimZones(time) {
            if (time - atlasState.dimZoneRefreshTime < 120) return;
            atlasState.dimZoneRefreshTime = time;
            const canvasRect = canvas.getBoundingClientRect();
            if (!canvasRect.width || !canvasRect.height) {
                atlasState.dimZones = [];
                return;
            }

            const targets = dimTargets.flatMap((spec) => {
                const pad = Number.isFinite(spec.pad) ? spec.pad : 20;
                const floor = Number.isFinite(spec.floor) ? spec.floor : 0.05;
                if (spec.element) return [{ element: spec.element, pad, floor }];
                const nodes = spec.all
                    ? Array.from(document.querySelectorAll(spec.selector))
                    : [document.querySelector(spec.selector)];
                return nodes.map((element) => ({ element, pad, floor }));
            });
            atlasState.dimZones = targets
                .filter(({ element }) => isVisibleDimTarget(element))
                .map(({ element, pad, floor }) => {
                    const rect = element.getBoundingClientRect();
                    return {
                        left: rect.left - canvasRect.left - pad,
                        right: rect.right - canvasRect.left + pad,
                        top: rect.top - canvasRect.top - pad,
                        bottom: rect.bottom - canvasRect.top + pad,
                        floor
                    };
                });
        }

        function getZoneDimFactor(x, y) {
            if (!atlasState.dimZones.length) return 1;
            const fadeDistance = 96;
            let factor = 1;
            atlasState.dimZones.forEach((zone) => {
                const outsideX = Math.max(zone.left - x, 0, x - zone.right);
                const outsideY = Math.max(zone.top - y, 0, y - zone.bottom);
                const distance = Math.sqrt(outsideX * outsideX + outsideY * outsideY);
                if (distance >= fadeDistance) return;
                const dimFloor = Number.isFinite(zone.floor) ? zone.floor : 0.12;
                const dimBlend = smoothStep(1 - (distance / fadeDistance));
                factor = Math.min(factor, 1 - dimBlend * (1 - dimFloor));
            });
            return factor;
        }

        function makePoints() {
            const currentTheme = shapeTheme;
            atlasState.points = Array.from({ length: 350 }, (_, index) => {
                const coords = (getWelcomeThemeCoordinatesGlobal ? getWelcomeThemeCoordinatesGlobal(currentTheme, index) : { x: 0, y: 0, z: 0 });
                return {
                    x: coords.x * sizeScale,
                    y: coords.y * sizeScale,
                    z: coords.z * sizeScale,
                    startX: coords.x * sizeScale,
                    startY: coords.y * sizeScale,
                    startZ: coords.z * sizeScale,
                    targetX: coords.x * sizeScale,
                    targetY: coords.y * sizeScale,
                    targetZ: coords.z * sizeScale,
                    size: (index % 13 === 0 ? 3.1 : 1.4 + (index % 5) * 0.4) * 0.5,
                    important: index % 13 === 0,
                    pulse: (index % 11) * (Math.PI / 5)
                };
            });
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            atlasState.width = canvas.clientWidth;
            atlasState.height = canvas.clientHeight;
            canvas.width = Math.floor(atlasState.width * dpr);
            canvas.height = Math.floor(atlasState.height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        function project(point, time) {
            const sinX = Math.sin(atlasState.rotationX);
            const cosX = Math.cos(atlasState.rotationX);
            const sinY = Math.sin(atlasState.rotationY);
            const cosY = Math.cos(atlasState.rotationY);
            const wobble = Math.sin(time * 0.001 + point.pulse) * 3;
            const y1 = point.y * cosX - (point.z + wobble) * sinX;
            const z1 = point.y * sinX + (point.z + wobble) * cosX;
            const x2 = point.x * cosY + z1 * sinY;
            const z2 = -point.x * sinY + z1 * cosY;
            const depth = (projectionDistance * sizeScale) + z2;
            if (!Number.isFinite(depth) || depth <= minProjectionDepth * sizeScale) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            const scale = (projectionDistance * sizeScale) / depth;
            let vx = atlasState.width * 0.5 + x2 * scale;
            let vy = atlasState.height * 0.5 + y1 * scale;
            if (!Number.isFinite(vx) || !Number.isFinite(vy) || !Number.isFinite(scale)) {
                point.screenX = undefined;
                point.screenY = undefined;
                return null;
            }

            if (atlasState.mouseX > -9000 && atlasState.mouseY > -9000) {
                const dx = vx - atlasState.mouseX;
                const dy = vy - atlasState.mouseY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > 0 && dist < 120) {
                    const pull = (1 - dist / 120) * 8 * scale;
                    vx -= (dx / dist) * pull;
                    vy -= (dy / dist) * pull;
                }
            }

            point.screenX = vx;
            point.screenY = vy;

            return {
                x: vx,
                y: vy,
                z: z2,
                scale,
                dim: getZoneDimFactor(vx, vy)
            };
        }

        function drawConnections(projected, rgb, time, spawnAlpha) {
            ctx.lineWidth = 0.6;
            const maxConnectDist = 86 * sizeScale;
            for (let i = 0; i < projected.length; i += 1) {
                for (let j = i + 1; j < projected.length; j += 1) {
                    const a = projected[i].view;
                    const b = projected[j].view;
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dz = a.z - b.z;
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz * 0.12);
                    if (distance > maxConnectDist) continue;
                    const alpha = Math.max(0, 1 - distance / maxConnectDist) * 0.22;
                    const pathDim = Math.min(
                        a.dim ?? 1,
                        b.dim ?? 1,
                        getZoneDimFactor(a.x + (b.x - a.x) * 0.5, a.y + (b.y - a.y) * 0.5)
                    );
                    const important = projected[i].point.important || projected[j].point.important;
                    ctx.strokeStyle = important ? `rgba(${rgb}, ${alpha * 1.7 * spawnAlpha * pathDim})` : `rgba(${rgb}, ${alpha * spawnAlpha * pathDim})`;
                    ctx.beginPath();
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                    ctx.stroke();

                    if (important && (i + j) % 4 === 0) {
                        const fraction = (time * 0.0006 + i * 0.17) % 1;
                        const px = a.x + (b.x - a.x) * fraction;
                        const py = a.y + (b.y - a.y) * fraction;
                        const pz = a.z + (b.z - a.z) * fraction;
                        const pScale = (projectionDistance * sizeScale) / ((projectionDistance * sizeScale) + pz);
                        const signalDim = getZoneDimFactor(px, py);

                        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 2.2 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 1.25 * pScale, 0, Math.PI * 2);
                        ctx.fill();

                        ctx.fillStyle = `rgba(${rgb}, ${alpha * 0.95 * spawnAlpha * signalDim})`;
                        ctx.beginPath();
                        ctx.arc(px, py, 2.5 * pScale, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        function drawNodes(projected, rgb, time, spawnAlpha) {
            projected.sort((a, b) => a.view.z - b.view.z).forEach(({ point, view }) => {
                const glow = Math.max(0.15, view.scale);
                const pulse = point.important ? 0.24 + Math.sin(time * 0.004 + point.pulse) * 0.12 : 0;
                const dim = view.dim ?? 1;

                if (point.important) {
                    const haloRadius = point.size * view.scale * 3.6;
                    const gradient = ctx.createRadialGradient(view.x, view.y, 0, view.x, view.y, haloRadius);
                    gradient.addColorStop(0, `rgba(${rgb}, ${(0.36 + pulse) * spawnAlpha * dim})`);
                    gradient.addColorStop(0.35, `rgba(${rgb}, ${(0.16 + pulse * 0.4) * spawnAlpha * dim})`);
                    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, haloRadius, 0, Math.PI * 2);
                    ctx.fill();
                }

                ctx.fillStyle = point.important
                    ? `rgba(${rgb}, ${(0.52 + pulse) * spawnAlpha * dim})`
                    : `rgba(255, 255, 255, ${(0.28 + glow * 0.32) * spawnAlpha * dim})`;
                ctx.beginPath();
                ctx.arc(view.x, view.y, point.size * view.scale, 0, Math.PI * 2);
                ctx.fill();

                if (point.important) {
                    ctx.strokeStyle = `rgba(${rgb}, ${(0.18 + pulse) * spawnAlpha * dim})`;
                    ctx.lineWidth = 0.6;
                    ctx.beginPath();
                    ctx.arc(view.x, view.y, point.size * view.scale * 2.8, 0, Math.PI * 2);
                    ctx.stroke();

                    const orbitRadius = point.size * view.scale * 4.2;
                    const speed = time * 0.0022 + point.pulse;
                    const sx = view.x + Math.cos(speed) * orbitRadius;
                    const sy = view.y + Math.sin(speed) * orbitRadius;
                    const satelliteDim = getZoneDimFactor(sx, sy);

                    ctx.fillStyle = `rgba(${rgb}, ${0.85 * spawnAlpha * satelliteDim})`;
                    ctx.beginPath();
                    ctx.arc(sx, sy, 0.8 * view.scale, 0, Math.PI * 2);
                    ctx.fill();
                }
            });
        }

        function frame(time) {
            if (!canvas.isConnected) {
                return;
            }

            if (parentEl && parentEl.style.display === 'none') {
                return;
            }

            if (!atlasState.spawnFadeStart) {
                atlasState.spawnFadeStart = time;
            }
            const spawnFadeDuration = 1800;
            const spawnAlpha = Math.min(1, Math.max(0, (time - atlasState.spawnFadeStart) / spawnFadeDuration));
            const rgb = activeColor(time);

            if (atlasState.transitionStart) {
                const elapsed = time - atlasState.transitionStart;
                const progress = Math.min(1, elapsed / 1000);
                const t = smoothStep(progress);

                atlasState.points.forEach((point) => {
                    point.x = point.startX + (point.targetX - point.startX) * t;
                    point.y = point.startY + (point.targetY - point.startY) * t;
                    point.z = point.startZ + (point.targetZ - point.startZ) * t;
                });

                if (progress >= 1) {
                    atlasState.transitionStart = 0;
                }
            }

            atlasState.rotationX += (atlasState.targetX - atlasState.rotationX) * 0.045;
            atlasState.rotationY += (atlasState.targetY - atlasState.rotationY) * 0.045;
            if (canvas.clientWidth !== atlasState.width || canvas.clientHeight !== atlasState.height) resize();
            if (!atlasState.width || !atlasState.height) {
                return;
            }
            refreshDimZones(time);
            // Clear the full backing buffer without the DPR transform. Mobile
            // Safari can otherwise retain stale connection lines between frames.
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
            const projected = atlasState.points
                .map((point) => ({ point, view: project(point, time) }))
                .filter(({ view }) => view);
            drawConnections(projected, rgb, time, spawnAlpha);
            drawNodes(projected, rgb, time, spawnAlpha);
            atlasState.targetY += 0.0018;
        }

        const handleMorph = (event) => {
            if (!canvas.isConnected) {
                window.removeEventListener('memora-welcome-theme-changed', handleMorph);
                return;
            }
            const nextTheme = event.detail.theme;
            const safeNextTheme = normalizeWelcomeNeuralTheme(nextTheme) || selectRandomWelcomeNeuralTheme({ excludeCurrent: false });
            atlasState.spawnFadeStart = performance.now();
            atlasState.transitionStart = performance.now();
            if (!atlasState.points.length) makePoints();
            atlasState.points.forEach((point, index) => {
                point.startX = point.x;
                point.startY = point.y;
                point.startZ = point.z;

                const targets = (getWelcomeThemeCoordinatesGlobal ? getWelcomeThemeCoordinatesGlobal(safeNextTheme, index) : { x: 0, y: 0, z: 0 });
                point.targetX = targets.x * sizeScale;
                point.targetY = targets.y * sizeScale;
                point.targetZ = targets.z * sizeScale;
            });
        };
        window.addEventListener('memora-welcome-theme-changed', handleMorph);

        makePoints();
        resize();
        animationController = createNeuralCanvasAnimationController(canvas, {
            isSurfaceActive: () => canvas.clientWidth > 0
                && canvas.clientHeight > 0
                && canvas.offsetParent !== null
                && (!parentEl || parentEl.style.display !== 'none'),
            renderFrame: frame
        });
        animationController.sync();
    }
    function installPdfEmptyNeuralAtlas() {
        installMemoraNeuralAtlas({
            canvasId: 'pdf-empty-neural-canvas',
            parentId: 'pdf-empty-state',
            dimSelector: '.pdf-empty-state-title'
        });
    }
    function installPdfStudyGuideNeuralAtlas(canvasId = 'pdf-study-guide-neural-canvas') {
        // Same shape/behaviour as the quiz card's atlas: constellation points, and
        // every piece of status content carves out a dim zone so nodes fade to near
        // nothing while they overlap the text, then fade back once they clear it.
        installMemoraNeuralAtlas({
            canvasId,
            theme: 'constellation',
            dimTargets: [
                { selector: '.pdf-study-guide-progress-title', pad: 24, floor: 0.03 },
                { selector: '.pdf-study-guide-progress-copy', pad: 20, floor: 0.05 },
                { selector: '.pdf-study-guide-progress-timers', pad: 18, floor: 0.07 },
                { selector: '.pdf-study-guide-progress-bar', pad: 18, floor: 0.05 },
                { selector: '.pdf-study-guide-spinner', pad: 20, floor: 0.05 },
                { selector: '.pdf-study-guide-loading-stack .pdf-study-guide-cancel-btn, .pdf-study-guide-loading-stack .welcome-btn', all: true, pad: 18, floor: 0.04 }
            ]
        });
    }
    window.memoraInstallMemoraNeuralAtlas = installMemoraNeuralAtlas;
    window.memoraInstallPdfEmptyNeuralAtlas = installPdfEmptyNeuralAtlas;
    window.memoraInstallPdfStudyGuideNeuralAtlas = installPdfStudyGuideNeuralAtlas;

    function getPublicTutorialGoalMeta(goal = '') {
        switch (goal) {
            case 'manual':
                return {
                    mode: 'full',
                    label: 'Manual AI Workflow',
                    summary: 'Enable Manual Import in Accessibility, then use your own ChatGPT or Gemini chat and paste the quiz JSON back into Memora.',
                    intro: 'This guide shows where automatic import lives and where Manual Import can be re-enabled from Accessibility.',
                    finish: 'Manual flow: enable Manual Import in Accessibility, open Import, launch the manual route, paste the Memora prompt into ChatGPT or Gemini, then bring the JSON back here.'
                };
            case 'study':
                return {
                    mode: 'full',
                    label: 'Study And Review',
                    summary: 'Focus on reviewing content, reopening old work, and keeping the PDF beside the quiz when it helps.',
                    intro: 'This guide focuses on reopening work, using the PDF pane, and finding questions quickly.',
                    finish: 'Review flow: load existing work or import a file, study in the main card, then use PDF, search, and tools when they help.'
                };
            case 'auto':
            default:
                return {
                    mode: 'full',
                    label: 'Automatic PDF To Quiz',
                    summary: 'Use the built-in automatic route to turn PDFs into quizzes with the fewest steps.',
                    intro: 'This guide starts with PDF-to-quiz generation, then covers the rest of the app.',
                    finish: 'Automatic flow: import, save the Gemini key, choose a PDF, review the generator defaults, and click Generate.'
                };
        }
    }

    function describePublicTutorialPrefs(prefs = getPublicTutorialPrefs()) {
        const goalMeta = getPublicTutorialGoalMeta(prefs.goal);
        const difficultyLabel = prefs.difficulty === 'mixed'
            ? 'mixed difficulty'
            : `${prefs.difficulty} difficulty`;
        return `${goalMeta.label}, ${prefs.questionCount}-question default, ${difficultyLabel}.`;
    }

    function restorePublicTourWelcomeActions() {
        const restoreState = publicTourWelcomeActionRestore;
        if (!restoreState) return;

        restoreState.forEach(({ button, parent, nextSibling, hadTourAction, tourAction }) => {
            if (!button || !parent?.isConnected) return;
            if (nextSibling?.parentNode === parent) parent.insertBefore(button, nextSibling);
            else parent.appendChild(button);
            if (hadTourAction) button.setAttribute('data-tour-action', tourAction || '');
            else button.removeAttribute('data-tour-action');
            delete button.dataset.publicTourReturnAction;
        });
        publicTourWelcomeActionRestore = null;
    }

    function mountPublicTourWelcomeActions(card) {
        const host = card?.querySelector('[data-tour-role="return-actions"]');
        const welcomeActions = document.querySelector('#welcome-screen .welcome-actions');
        if (!host || !welcomeActions) return;

        const buttons = [
            [document.getElementById('new-session-btn'), 'start-new-session'],
            [document.getElementById('continue-session-btn'), 'continue-session']
        ].filter(([button]) => button && button.parentElement === welcomeActions);
        if (!buttons.length) return;

        publicTourWelcomeActionRestore = buttons.map(([button, action]) => ({
            button,
            parent: button.parentElement,
            nextSibling: button.nextSibling,
            hadTourAction: button.hasAttribute('data-tour-action'),
            tourAction: button.getAttribute('data-tour-action')
        }));
        buttons.forEach(([button, action]) => {
            button.dataset.publicTourReturnAction = '1';
            button.dataset.tourAction = action;
            host.appendChild(button);
        });
    }

    function applyPublicTutorialPrefsToApp(prefs = getPublicTutorialPrefs()) {
        if (typeof setQuizGenQuestionCountUi === 'function') {
            setQuizGenQuestionCountUi(prefs.questionCount, { autoSave: false });
        }
        if (typeof setQuizGenDifficultyUi === 'function') {
            setQuizGenDifficultyUi(prefs.difficulty, { autoSave: false });
        }
        if (typeof queueQuizGenSessionPreferencesSave === 'function') {
            queueQuizGenSessionPreferencesSave();
        }
    }

    function closePublicTourHome() {
        releasePublicOnboardingPrepaint();
        const { card, launcher } = getPublicTourUi();
        restorePublicTourWelcomeActions();
        stopPublicHomeStoryPlayback();
        stopPublicHomeDemoLoop();
        publicTourState.homeTransitioning = false;
        publicTourState.homeOpen = false;
        publicTourState.homeEmphasized = false;
        publicTourState.homeVariant = '';
        publicTourState.homeOnboardingStage = '';
        publicTourState.homeDemoRevealRunId += 1;
        closePublicWelcomeTipModal();
        clearPublicHomeOnboardingLayoutSchedule();
        clearPublicHomeOnboardingLayout();
        if (card) {
            card.classList.remove(
                'active',
                'public-tour-card-home-onboarding',
                'public-tour-card-home-choice',
                'public-tour-card-home-returning',
                'public-tour-card-home-guide',
                'public-tour-home-leaving',
                'public-tour-home-choice-exiting',
                'public-tour-home-demo-entering',
                'public-tour-home-demo-entered',
                'public-tour-card-placement-left',
                'public-tour-card-placement-below',
                'public-tour-card-placement-pdf-left',
                'public-tour-card-anchor-top',
                'public-tour-step-exit-next',
                'public-tour-step-exit-prev',
                'public-tour-step-enter-next',
                'public-tour-step-enter-prev'
            );
            card.hidden = true;
            card.innerHTML = '';
        }
        if (launcher) launcher.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('public-tour-home-transitioning');
        syncPublicTourChromeState();
    }

    function getPublicTourUi() {
        return {
            widget: document.getElementById('public-tour-widget'),
            launcher: document.getElementById('public-tour-launcher'),
            card: document.getElementById('public-tour-card'),
            overlay: document.getElementById('public-tour-overlay')
        };
    }

    function clearPublicHomeOnboardingLayout() {
        const { card } = getPublicTourUi();
        const welcome = document.getElementById('welcome-screen');
        if (welcome) {
            welcome.style.removeProperty('--welcome-shift-x');
            welcome.style.removeProperty('--welcome-shift-y');
        }
        if (card) {
            card.style.left = '';
            card.style.top = '';
            card.style.right = '';
            card.style.bottom = '';
        }
    }

    function clearPublicHomeOnboardingLayoutSchedule() {
        if (publicHomeOnboardingLayoutRaf) {
            window.cancelAnimationFrame(publicHomeOnboardingLayoutRaf);
            publicHomeOnboardingLayoutRaf = 0;
        }
        if (Array.isArray(publicHomeOnboardingLayoutTimers) && publicHomeOnboardingLayoutTimers.length) {
            publicHomeOnboardingLayoutTimers.forEach((timerId) => window.clearTimeout(timerId));
        }
        publicHomeOnboardingLayoutTimers = [];
    }

    function ensurePublicHomeOnboardingWelcomeResizeObserver() {
        if (typeof ResizeObserver !== 'function') return;
        const welcome = document.getElementById('welcome-screen');
        if (welcome === publicHomeOnboardingWelcomeResizeTarget) return;

        if (publicHomeOnboardingWelcomeResizeObserver) {
            publicHomeOnboardingWelcomeResizeObserver.disconnect();
            publicHomeOnboardingWelcomeResizeObserver = null;
        }

        publicHomeOnboardingWelcomeResizeTarget = welcome;
        if (!welcome) return;

        publicHomeOnboardingWelcomeResizeObserver = new ResizeObserver(() => {
            if (!publicTourState.homeOpen || publicTourState.homeVariant !== 'onboarding') return;
            schedulePublicHomeOnboardingLayoutSync();
        });
        publicHomeOnboardingWelcomeResizeObserver.observe(welcome);
    }

    function syncPublicHomeOnboardingLayout() {
        const { card } = getPublicTourUi();
        const welcome = document.getElementById('welcome-screen');

        if (!card || !welcome || publicTourState.homeVariant !== 'onboarding' || !publicTourState.homeOpen) {
            clearPublicHomeOnboardingLayout();
            return;
        }

        welcome.style.removeProperty('--welcome-shift-x');
        welcome.style.removeProperty('--welcome-shift-y');
        card.style.left = '';
        card.style.top = '';
        card.style.right = '';
        card.style.bottom = '';
    }

    function schedulePublicHomeOnboardingLayoutSync(options = {}) {
        const immediate = !!options.immediate;
        clearPublicHomeOnboardingLayoutSchedule();

        const runSync = () => {
            publicHomeOnboardingLayoutRaf = 0;
            syncPublicHomeOnboardingLayout();
        };

        publicHomeOnboardingLayoutRaf = window.requestAnimationFrame(runSync);
        if (immediate) return;

        publicHomeOnboardingLayoutTimers = [90, 220].map((delayMs) => window.setTimeout(() => {
            window.requestAnimationFrame(() => {
                if (!publicTourState.homeOpen || publicTourState.homeVariant !== 'onboarding') return;
                syncPublicHomeOnboardingLayout();
            });
        }, delayMs));
    }

    function syncPublicTourWelcomeLauncherOffset() {
        const donateToggle = document.querySelector('.welcome-donate-toggle');
        const donateWidth = donateToggle ? Math.ceil(donateToggle.getBoundingClientRect().width) : 0;
        document.documentElement.style.setProperty('--welcome-donate-toggle-width', `${donateWidth}px`);
        return donateToggle;
    }

    function ensurePublicDonateControlAnchored() {
        const donate = document.querySelector('.welcome-donate');
        const workArea = document.getElementById('work-area');
        if (!donate || !workArea) return;
        if (donate.parentElement === document.body) return;
        document.body.insertBefore(donate, workArea);
    }

    function ensurePublicTourWelcomeLauncherOffset() {
        const donateToggle = syncPublicTourWelcomeLauncherOffset();
        ensurePublicHomeOnboardingWelcomeResizeObserver();
        if (!publicTourWelcomeToggleResizeBound) {
            publicTourWelcomeToggleResizeBound = true;
            window.addEventListener('resize', syncPublicTourWelcomeLauncherOffset, { passive: true });
        }
        if (!publicHomeOnboardingLayoutResizeBound) {
            publicHomeOnboardingLayoutResizeBound = true;
            window.addEventListener('resize', () => schedulePublicHomeOnboardingLayoutSync(), { passive: true });
        }
        if (!publicHomeOnboardingViewportResizeBound) {
            publicHomeOnboardingViewportResizeBound = true;
            window.addEventListener('orientationchange', () => schedulePublicHomeOnboardingLayoutSync(), { passive: true });
            if (window.visualViewport?.addEventListener) {
                window.visualViewport.addEventListener('resize', () => schedulePublicHomeOnboardingLayoutSync(), { passive: true });
            }
        }
        if (typeof ResizeObserver !== 'function' || donateToggle === publicTourWelcomeToggleResizeTarget) return;
        if (publicTourWelcomeToggleResizeObserver) {
            publicTourWelcomeToggleResizeObserver.disconnect();
            publicTourWelcomeToggleResizeObserver = null;
        }
        publicTourWelcomeToggleResizeTarget = donateToggle;
        if (!donateToggle) return;
        publicTourWelcomeToggleResizeObserver = new ResizeObserver(() => {
            syncPublicTourWelcomeLauncherOffset();
        });
        publicTourWelcomeToggleResizeObserver.observe(donateToggle);
    }

    function shouldHidePublicFloatingUiForLayout() {
        const workArea = document.getElementById('work-area');
        if (!workArea) return false;
        // Keep Guide and Support hidden until pane-close cleanup releases the transition classes.
        return ['split-view', 'split-view-ai', 'trio-view', 'pane-close-transitioning', 'pane-close-crushing']
            .some((className) => workArea.classList.contains(className));
    }

    function syncPublicFloatingUiVisibility() {
        const currentTourStep = publicTourState.steps[publicTourState.stepIndex];
        const guideOwnsPdfTransition = !!(
            publicTourState.active
            && (currentTourStep?.surface === 'pdf-demo' || publicTourState.pdfDemoClosing)
        );
        const shouldHide = shouldHidePublicFloatingUiForLayout() && !guideOwnsPdfTransition;
        const shouldCloseHome = shouldHide || !isPublicTourWelcomeHomeVisible();
        const hiddenStateChanged = document.body.classList.contains('public-floating-hidden') !== shouldHide;
        document.body.classList.toggle('public-floating-hidden', shouldHide);
        if (hiddenStateChanged) {
            window.requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'));
            });
        }
        const donate = document.querySelector('.welcome-donate');
        if (shouldHide && donate?.open) donate.open = false;
        if (shouldHide && publicTourState.active) {
            stopPublicTour();
        } else if (shouldCloseHome && publicTourState.homeOpen) {
            dismissPublicTourHome();
        }
    }

    function installPublicFloatingUiVisibilityWatcher() {
        const workArea = document.getElementById('work-area');
        if (!workArea) {
            window.setTimeout(installPublicFloatingUiVisibilityWatcher, 120);
            return;
        }
        syncPublicFloatingUiVisibility();
        if (publicFloatingVisibilityObserver) {
            publicFloatingVisibilityObserver.disconnect();
            publicFloatingVisibilityObserver = null;
        }
        publicFloatingVisibilityObserver = new MutationObserver(() => {
            syncPublicFloatingUiVisibility();
        });
        publicFloatingVisibilityObserver.observe(workArea, { attributes: true, attributeFilter: ['class'] });

        if (publicFloatingBodyObserver) {
            publicFloatingBodyObserver.disconnect();
            publicFloatingBodyObserver = null;
        }
        publicFloatingBodyObserver = new MutationObserver(() => {
            syncPublicFloatingUiVisibility();
        });
        publicFloatingBodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        const welcome = document.getElementById('welcome-screen');
        if (publicFloatingWelcomeObserver) {
            publicFloatingWelcomeObserver.disconnect();
            publicFloatingWelcomeObserver = null;
        }
        if (welcome) {
            publicFloatingWelcomeObserver = new MutationObserver(() => {
                syncPublicFloatingUiVisibility();
            });
            publicFloatingWelcomeObserver.observe(welcome, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        }
    }

    function clearPublicTourHighlight() {
        const highlightedEls = Array.isArray(publicTourState.highlightedEls) && publicTourState.highlightedEls.length
            ? publicTourState.highlightedEls
            : (publicTourState.highlightedEl ? [publicTourState.highlightedEl] : []);
        highlightedEls.forEach((el) => {
            if (el?.dataset?.publicTourHighlightShadow !== undefined) {
                el.style.setProperty(
                    'box-shadow',
                    el.dataset.publicTourHighlightShadow,
                    el.dataset.publicTourHighlightShadowPriority || ''
                );
                delete el.dataset.publicTourHighlightShadow;
                delete el.dataset.publicTourHighlightShadowPriority;
            }
            el?.classList?.remove(
                'public-tour-target-active',
                'public-tour-target-entering',
                'public-tour-target-leaving'
            );
            if (el?.dataset?.publicTourPreviousMaxHeight !== undefined) {
                el.style.setProperty('max-height', el.dataset.publicTourPreviousMaxHeight, el.dataset.publicTourPreviousMaxHeightPriority || '');
                el.style.setProperty('height', el.dataset.publicTourPreviousHeight || '', el.dataset.publicTourPreviousHeightPriority || '');
                el.style.setProperty('min-height', el.dataset.publicTourPreviousMinHeight || '', el.dataset.publicTourPreviousMinHeightPriority || '');
                el.style.setProperty('overflow-y', el.dataset.publicTourPreviousOverflowY || '', el.dataset.publicTourPreviousOverflowYPriority || '');
                delete el.dataset.publicTourPreviousMaxHeight;
                delete el.dataset.publicTourPreviousMaxHeightPriority;
                delete el.dataset.publicTourPreviousHeight;
                delete el.dataset.publicTourPreviousHeightPriority;
                delete el.dataset.publicTourPreviousMinHeight;
                delete el.dataset.publicTourPreviousMinHeightPriority;
                delete el.dataset.publicTourPreviousOverflowY;
                delete el.dataset.publicTourPreviousOverflowYPriority;
            }
        });
        publicTourState.highlightedEl = null;
        publicTourState.highlightedEls = [];
        if (publicTourState.highlightedRoot) {
            publicTourState.highlightedRoot.classList.remove('public-tour-layer-active');
            publicTourState.highlightedRoot = null;
        }
        if (publicTourState.highlightedStackRoot) {
            publicTourState.highlightedStackRoot.classList.remove('public-tour-stack-root');
            publicTourState.highlightedStackRoot = null;
        }
        if (Array.isArray(publicTourState.highlightedEffectEls)) {
            publicTourState.highlightedEffectEls.forEach((el) => {
                el?.classList?.remove('public-tour-clear-effects');
            });
        }
        publicTourState.highlightedEffectEls = [];
    }

    function setPublicTourHighlightShadowFaded(el) {
        if (!(el instanceof HTMLElement)) return;
        if (el.dataset.publicTourHighlightShadow === undefined) {
            el.dataset.publicTourHighlightShadow = el.style.getPropertyValue('box-shadow') || '';
            el.dataset.publicTourHighlightShadowPriority = el.style.getPropertyPriority('box-shadow') || '';
        }
        el.style.setProperty('box-shadow', '0 0 0 2px transparent, 0 0 0 8px transparent, 0 0 0 transparent, 0 0 0 transparent', 'important');
    }

    function restorePublicTourHighlightShadow(el) {
        if (!(el instanceof HTMLElement) || el.dataset.publicTourHighlightShadow === undefined) return;
        el.style.setProperty(
            'box-shadow',
            el.dataset.publicTourHighlightShadow,
            el.dataset.publicTourHighlightShadowPriority || ''
        );
        delete el.dataset.publicTourHighlightShadow;
        delete el.dataset.publicTourHighlightShadowPriority;
    }

    function beginPublicTourHighlightExit() {
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
        const highlightedEls = Array.isArray(publicTourState.highlightedEls) && publicTourState.highlightedEls.length
            ? publicTourState.highlightedEls
            : (publicTourState.highlightedEl ? [publicTourState.highlightedEl] : []);
        highlightedEls.forEach((el) => {
            if (el instanceof Element) {
                el.classList.add('public-tour-target-leaving');
                setPublicTourHighlightShadowFaded(el);
            }
        });
    }

    function resolvePublicTourTarget(step) {
        if (!step || !step.target) return null;
        if (typeof step.target === 'function') {
            return step.target();
        }
        if (typeof step.target === 'string') {
            return document.querySelector(step.target);
        }
        return step.target || null;
    }

    function resolvePublicTourExtraTargets(step, primaryTarget) {
        const refs = step?.extraTargets;
        if (!refs) return [];
        const resolvedRefs = typeof refs === 'function'
            ? refs(primaryTarget)
            : refs;
        const list = Array.isArray(resolvedRefs) ? resolvedRefs : [resolvedRefs];
        const seen = new Set();
        return list
            .map((ref) => resolvePublicTourAnchor(ref, primaryTarget))
            .filter((el) => el instanceof Element && isElementVisible(el))
            .filter((el) => {
                if (seen.has(el)) return false;
                seen.add(el);
                return true;
            });
    }

    function resolvePublicTourAnchor(ref, target) {
        if (!ref) return null;
        if (typeof ref === 'function') {
            return ref(target) || null;
        }
        if (typeof ref === 'string') {
            return document.querySelector(ref);
        }
        return ref || null;
    }

    function resolvePublicTourLayerRoot(step, target) {
        const explicitRoot = resolvePublicTourAnchor(step?.layerRoot, target);
        if (explicitRoot) return explicitRoot;
        if (!target || !target.closest) return null;
        return target.closest(
            '#import-modal, #quiz-gen-modal, #history-modal, #stats-modal, #achievements-modal, #level-roadmap-modal, #export-modal, #database-modal, #saved-modal, #adaptive-snapshot-modal, #theme-modal, #ask-ai-modal, #voice-agent-modal, #api-key-modal, #notes-modal, #canvas-settings-modal, #canvas-help-modal, #canvas-browser-modal, #public-auth-overlay'
        ) || target.closest(
            '.menu-wrapper, .search-wrapper, .source-wrapper, #controls-bar, #quiz-pane, #pdf-pane, #ai-pane, #welcome-screen, #flashcard-container, #public-account-pill'
        ) || null;
    }

    function resolvePublicTourStackRoot(step, target) {
        return resolvePublicTourAnchor(step?.stackRoot, target);
    }

    function shouldClearPublicTourEffects(el) {
        if (!(el instanceof Element)) return false;
        const style = window.getComputedStyle(el);
        const opacity = Number.parseFloat(style.opacity || '1');
        const filter = style.filter || 'none';
        const backdropFilter = style.backdropFilter || style.webkitBackdropFilter || 'none';
        return opacity < 0.999 || filter !== 'none' || backdropFilter !== 'none';
    }

    function collectPublicTourEffectClearEls(target, root, stackRoot) {
        const collected = [];
        const seen = new Set();
        const pushIfNeeded = (el) => {
            if (!(el instanceof Element) || el === target || seen.has(el)) return;
            seen.add(el);
            if (shouldClearPublicTourEffects(el)) {
                collected.push(el);
            }
        };

        pushIfNeeded(root);
        pushIfNeeded(stackRoot);

        let current = target?.parentElement || null;
        while (current && current !== document.body && current !== document.documentElement) {
            pushIfNeeded(current);
            current = current.parentElement;
        }

        return collected;
    }

    async function waitForPublicTourTarget(step, attempts = 8, delayMs = 80) {
        let target = resolvePublicTourTarget(step);
        if (isElementVisible(target)) return target;

        for (let attempt = 0; attempt < attempts; attempt += 1) {
            await wait(delayMs);
            target = resolvePublicTourTarget(step);
            if (isElementVisible(target)) return target;
        }

        return target;
    }

    async function focusPublicTourTarget(step) {
        clearPublicTourHighlight();

        const target = await waitForPublicTourTarget(step);
        if (!isElementVisible(target)) return;

        const root = step?.skipLayerLift ? null : resolvePublicTourLayerRoot(step, target);
        if (root && root !== target) {
            root.classList.add('public-tour-layer-active');
            publicTourState.highlightedRoot = root;
        }

        const stackRoot = resolvePublicTourStackRoot(step, target);
        if (stackRoot && stackRoot !== target) {
            stackRoot.classList.add('public-tour-stack-root');
            publicTourState.highlightedStackRoot = stackRoot;
        }

        const effectClearEls = collectPublicTourEffectClearEls(target, root, stackRoot);
        effectClearEls.forEach((el) => {
            el.classList.add('public-tour-clear-effects');
        });
        publicTourState.highlightedEffectEls = effectClearEls;

        const highlightedTargets = [target, ...resolvePublicTourExtraTargets(step, target)]
            .filter((el, index, arr) => arr.indexOf(el) === index);
        const animateHighlight = !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        highlightedTargets.forEach((el) => {
            if (animateHighlight) {
                el.classList.add('public-tour-target-entering');
                setPublicTourHighlightShadowFaded(el);
            }
            el.classList.add('public-tour-target-active');
        });
        if (animateHighlight) {
            void target.getBoundingClientRect();
            window.requestAnimationFrame(() => {
                highlightedTargets.forEach((el) => {
                    restorePublicTourHighlightShadow(el);
                    el.classList.remove('public-tour-target-entering');
                });
            });
        }
        publicTourState.highlightedEl = target;
        publicTourState.highlightedEls = highlightedTargets;

        try {
            const isPhoneGuide = document.documentElement.classList.contains('phone-device');
            target.scrollIntoView({ behavior: isPhoneGuide ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
        } catch (_err) {
            target.scrollIntoView();
        }
        const focusedStepIndex = publicTourState.stepIndex;
        window.setTimeout(() => {
            if (!publicTourState.active || publicTourState.stepIndex !== focusedStepIndex) return;
            syncPublicTourPhoneCardAnchor();
        }, 350);
    }

    function syncPublicTourPhoneCardAnchor() {
        if (!document.documentElement.classList.contains('phone-device') || !publicTourState.active) return;
        const { card } = getPublicTourUi();
        if (!card || card.hidden || card.classList.contains('public-tour-card-home-onboarding')) return;

        const currentStep = publicTourState.steps[publicTourState.stepIndex] || null;
        const forcedPhoneAnchor = String(currentStep?.phoneCardAnchor || '').trim().toLowerCase();
        if (forcedPhoneAnchor === 'top' || forcedPhoneAnchor === 'bottom') {
            card.classList.toggle('public-tour-card-anchor-top', forcedPhoneAnchor === 'top');
            return;
        }

        const targetEls = (publicTourState.highlightedEls.length
            ? publicTourState.highlightedEls
            : [publicTourState.highlightedEl])
            .filter((el) => el instanceof Element);
        targetEls.forEach((el) => {
            if (el.dataset.publicTourPreviousMaxHeight === undefined) return;
            el.style.setProperty('max-height', el.dataset.publicTourPreviousMaxHeight, el.dataset.publicTourPreviousMaxHeightPriority || '');
            el.style.setProperty('height', el.dataset.publicTourPreviousHeight || '', el.dataset.publicTourPreviousHeightPriority || '');
            el.style.setProperty('min-height', el.dataset.publicTourPreviousMinHeight || '', el.dataset.publicTourPreviousMinHeightPriority || '');
            el.style.setProperty('overflow-y', el.dataset.publicTourPreviousOverflowY || '', el.dataset.publicTourPreviousOverflowYPriority || '');
            delete el.dataset.publicTourPreviousMaxHeight;
            delete el.dataset.publicTourPreviousMaxHeightPriority;
            delete el.dataset.publicTourPreviousHeight;
            delete el.dataset.publicTourPreviousHeightPriority;
            delete el.dataset.publicTourPreviousMinHeight;
            delete el.dataset.publicTourPreviousMinHeightPriority;
            delete el.dataset.publicTourPreviousOverflowY;
            delete el.dataset.publicTourPreviousOverflowYPriority;
        });
        const targets = targetEls
            .map((el) => el.getBoundingClientRect())
            .filter((rect) => rect.width > 0 && rect.height > 0);
        if (!targets.length) {
            card.classList.remove('public-tour-card-anchor-top');
            return;
        }

        const targetRect = targets.reduce((union, rect) => ({
            left: Math.min(union.left, rect.left),
            top: Math.min(union.top, rect.top),
            right: Math.max(union.right, rect.right),
            bottom: Math.max(union.bottom, rect.bottom)
        }), { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity });
        const overlapArea = (cardRect) => {
            const gap = 8;
            const width = Math.max(0, Math.min(cardRect.right + gap, targetRect.right) - Math.max(cardRect.left - gap, targetRect.left));
            const height = Math.max(0, Math.min(cardRect.bottom + gap, targetRect.bottom) - Math.max(cardRect.top - gap, targetRect.top));
            return width * height;
        };
        const preferTop = ((targetRect.top + targetRect.bottom) / 2) >= (window.innerHeight / 2);

        card.classList.toggle('public-tour-card-anchor-top', preferTop);
        const preferredOverlap = overlapArea(card.getBoundingClientRect());
        if (preferredOverlap <= 0) return;

        card.classList.toggle('public-tour-card-anchor-top', !preferTop);
        const alternateOverlap = overlapArea(card.getBoundingClientRect());
        if (preferredOverlap <= alternateOverlap) {
            card.classList.toggle('public-tour-card-anchor-top', preferTop);
        }

        const finalCardRect = card.getBoundingClientRect();
        const toolsDropdown = targetEls.find((el) => el.id === 'menu-dropdown');
        if (toolsDropdown && !card.classList.contains('public-tour-card-anchor-top')) {
            const dropdownRect = toolsDropdown.getBoundingClientRect();
            const availableHeight = Math.floor(finalCardRect.top - dropdownRect.top - 8);
            if (availableHeight >= 120 && dropdownRect.height > availableHeight) {
                toolsDropdown.dataset.publicTourPreviousMaxHeight = toolsDropdown.style.maxHeight || '';
                toolsDropdown.dataset.publicTourPreviousMaxHeightPriority = toolsDropdown.style.getPropertyPriority('max-height') || '';
                toolsDropdown.dataset.publicTourPreviousHeight = toolsDropdown.style.height || '';
                toolsDropdown.dataset.publicTourPreviousHeightPriority = toolsDropdown.style.getPropertyPriority('height') || '';
                toolsDropdown.dataset.publicTourPreviousMinHeight = toolsDropdown.style.minHeight || '';
                toolsDropdown.dataset.publicTourPreviousMinHeightPriority = toolsDropdown.style.getPropertyPriority('min-height') || '';
                toolsDropdown.dataset.publicTourPreviousOverflowY = toolsDropdown.style.overflowY || '';
                toolsDropdown.dataset.publicTourPreviousOverflowYPriority = toolsDropdown.style.getPropertyPriority('overflow-y') || '';
                toolsDropdown.style.setProperty('max-height', `${availableHeight}px`, 'important');
                toolsDropdown.style.setProperty('height', `${availableHeight}px`, 'important');
                toolsDropdown.style.setProperty('min-height', '0', 'important');
                toolsDropdown.style.setProperty('overflow-y', 'auto', 'important');
            }
        }
    }

    function closePublicTourImportModal() {
        const modal = document.getElementById('import-modal');
        const isOpen = !!(modal && modal.classList.contains('active'));
        if (isOpen) {
            if (typeof window.closeImportModal === 'function') {
                window.closeImportModal({ target: modal });
            }
            if (modal.classList.contains('active')) {
                modal.classList.remove('active');
            }
        }
        publicTourState.openedImportModal = false;
        return isOpen;
    }

    function closePublicTourQuizGenModal() {
        const modal = document.querySelector('[data-quiz-gen-production-modal]');
        const isOpen = !!(modal && modal.classList.contains('active'));
        if (isOpen) {
            if (typeof window.handleQuizGenClose === 'function') {
                window.handleQuizGenClose();
            }
            if (modal.classList.contains('active')) {
                modal.classList.remove('active');
            }
        }
        publicTourState.openedQuizGenModal = false;
        return isOpen;
    }

    function closePublicTourMenuDropdown() {
        const dropdown = document.getElementById('menu-dropdown');
        const trigger = document.getElementById('menu-trigger-btn');
        const wasOpen = !!(dropdown && dropdown.classList.contains('active'));
        if (wasOpen && typeof window.closeToolsMenuDropdown === 'function') {
            window.closeToolsMenuDropdown();
        } else if (dropdown) {
            dropdown.classList.remove('active');
            if (wasOpen && publicTourState.openedMenuDropdown && trigger) {
                trigger.blur?.();
            }
        }
        if (publicTourState.savedMenuGroupIds && typeof window.setOpenToolsMenuGroups === 'function') {
            window.setOpenToolsMenuGroups(publicTourState.savedMenuGroupIds);
        }
        publicTourState.savedMenuGroupIds = null;
        if (typeof window.syncFloatingQuizLayering === 'function') {
            window.syncFloatingQuizLayering();
        }
        publicTourState.openedMenuDropdown = false;
        return wasOpen;
    }

    async function ensurePublicTourImportModal() {
        const modal = document.getElementById('import-modal');
        if (!modal) return;
        if (!modal.classList.contains('active')) {
            publicTourState.openedImportModal = true;
            if (typeof window.openImportModal === 'function') {
                window.openImportModal();
            } else {
                modal.classList.add('active');
            }
            await wait(120);
        }
    }

    async function ensurePublicTourQuizGenModal() {
        const modal = document.querySelector('[data-quiz-gen-production-modal]');
        if (!modal) return;
        if (!modal.classList.contains('active')) {
            publicTourState.openedQuizGenModal = true;
            const previewFile = typeof File === 'function'
                ? new File(['Memora public tour preview'], 'Memora Tour Preview.pdf', { type: 'application/pdf' })
                : { name: 'Memora Tour Preview.pdf', type: 'application/pdf' };

            if (typeof window.showQuizGenerationModal === 'function') {
                try {
                    window.showQuizGenerationModal([previewFile]);
                } catch (err) {
                    console.warn('Public tour could not open quiz generator preview:', err);
                    modal.classList.add('active');
                }
            } else {
                modal.classList.add('active');
            }
            await wait(140);
        }
    }

    async function ensurePublicTourMenuDropdown() {
        const dropdown = document.getElementById('menu-dropdown');
        const trigger = document.getElementById('menu-trigger-btn');
        if (!dropdown) return;
        if (!publicTourState.savedMenuGroupIds && typeof window.getOpenToolsMenuGroups === 'function') {
            publicTourState.savedMenuGroupIds = window.getOpenToolsMenuGroups();
        }
        if (!dropdown.classList.contains('active')) {
            publicTourState.openedMenuDropdown = true;
            if (typeof window.openToolsMenuDropdown === 'function') {
                window.openToolsMenuDropdown();
            } else if (trigger) {
                trigger.click();
            } else {
                dropdown.classList.add('active');
                if (typeof window.syncFloatingQuizLayering === 'function') {
                    window.syncFloatingQuizLayering();
                }
            }
            await wait(120);
        }
        if (typeof window.collapseAllToolsMenuGroups === 'function') {
            window.collapseAllToolsMenuGroups();
        }
        dropdown.scrollTop = 0;
        await wait(80);
    }

    function syncPublicTourPlaybackControls() {
        const { card } = getPublicTourUi();
        const locked = !!publicTourState.stepPlaybackActive;
        const isFirstStep = publicTourState.stepIndex <= 0;
        document.body.classList.toggle('public-tour-step-playback', locked);
        card?.classList.toggle('public-tour-step-playback', locked);

        const prevBtn = card?.querySelector('[data-tour-action="prev"]');
        const nextBtn = card?.querySelector('[data-tour-action="next"]');
        if (prevBtn) {
            prevBtn.disabled = locked || isFirstStep;
            prevBtn.setAttribute('aria-disabled', prevBtn.disabled ? 'true' : 'false');
        }
        if (nextBtn) {
            nextBtn.disabled = locked;
            nextBtn.setAttribute('aria-disabled', locked ? 'true' : 'false');
        }
        const replayBtn = card?.querySelector('[data-tour-action="replay-step"]');
        if (replayBtn) {
            replayBtn.hidden = locked;
            replayBtn.disabled = locked;
            replayBtn.setAttribute('aria-disabled', locked ? 'true' : 'false');
        }

    }

    function setPublicTourPlaybackLocked(locked) {
        publicTourState.stepPlaybackActive = !!locked;
        syncPublicTourPlaybackControls();
    }

    function isPublicTourPlaybackCurrent(runId, stepId = '') {
        const currentStep = publicTourState.steps[publicTourState.stepIndex];
        return !!(
            publicTourState.active
            && publicTourState.stepPlaybackRunId === runId
            && (!stepId || currentStep?.id === stepId)
        );
    }

    function createPublicTourPointerEvent(type, point, pointerId, buttons) {
        return new PointerEvent(type, {
            bubbles: true,
            cancelable: true,
            composed: true,
            pointerId,
            pointerType: 'mouse',
            isPrimary: true,
            button: type === 'pointerdown' || type === 'pointerup' ? 0 : -1,
            buttons,
            clientX: point.x,
            clientY: point.y
        });
    }

    function sendPublicTourPdfDemoPointer(type, target, point, pointerId, buttons) {
        const gestureEvent = {
            target,
            pointerId,
            pointerType: 'mouse',
            isPrimary: true,
            button: type === 'pointerdown' || type === 'pointerup' ? 0 : -1,
            buttons,
            clientX: point.x,
            clientY: point.y,
            preventDefault() { },
            stopPropagation() { },
            stopImmediatePropagation() { }
        };
        if (type === 'pointerdown' && typeof handlePdfEvidenceAskSelectionPointerDown === 'function') {
            handlePdfEvidenceAskSelectionPointerDown(gestureEvent);
            return true;
        }
        if (type === 'pointermove' && typeof handlePdfEvidenceAskSelectionPointerMove === 'function') {
            handlePdfEvidenceAskSelectionPointerMove(gestureEvent);
            return true;
        }
        if (type === 'pointerup' && typeof handlePdfEvidenceAskSelectionPointerUp === 'function') {
            handlePdfEvidenceAskSelectionPointerUp(gestureEvent);
            return true;
        }
        if (type === 'pointercancel' && typeof handlePdfEvidenceAskSelectionPointerCancel === 'function') {
            handlePdfEvidenceAskSelectionPointerCancel(gestureEvent);
            return true;
        }
        const destination = target instanceof EventTarget ? target : document;
        destination.dispatchEvent(createPublicTourPointerEvent(type, point, pointerId, buttons));
        return true;
    }

    function clearPublicTourPdfDemoArtifacts() {
        const pointerId = Number(publicTourState.pdfDemoPointerId) || 0;
        if (pointerId) {
            try {
                sendPublicTourPdfDemoPointer('pointercancel', document.body, { x: 0, y: 0 }, pointerId, 0);
            } catch (_err) { }
        }
        publicTourState.pdfDemoPointerId = 0;

        window.closePdfEvidenceAskCardForPaneClose?.();
        window.notifyAskMemoraPdfPaneClosing?.();
        publicTourState.pdfDemoAskCard?.remove?.();
        publicTourState.pdfDemoSelection?.remove?.();
        publicTourState.pdfDemoCursor?.remove?.();
        document.querySelectorAll('[data-public-tour-pdf-demo="true"]').forEach((element) => element.remove());
        publicTourState.pdfDemoAskCard = null;
        publicTourState.pdfDemoSelection = null;
        publicTourState.pdfDemoCursor = null;
        document.body.classList.remove('pdf-evidence-ask-selecting', 'pdf-evidence-highlights-visible');
        document.body.style.userSelect = '';
    }

    function closePublicTourPdfDemo() {
        const restore = publicTourState.pdfDemoRestore;
        const hadDemo = !!restore || !!publicTourState.pdfDemoCursor || !!publicTourState.pdfDemoSelection;
        clearPublicTourPdfDemoArtifacts();
        document.body.classList.remove('public-tour-pdf-demo');
        if (!restore) return hadDemo;

        const workArea = document.getElementById('work-area');
        const viewerOpen = (typeof isViewerOpen === 'boolean' && isViewerOpen)
            || workArea?.classList.contains('split-view')
            || workArea?.classList.contains('trio-view');
        if (restore.viewerTouched && viewerOpen && typeof toggleViewer === 'function') {
            publicTourState.pdfDemoClosing = true;
            try {
                toggleViewer(false);
            } catch (err) {
                console.warn('Public tour could not close the sample PDF:', err);
            }
        }

        if (typeof loadedPdfFiles === 'object' && loadedPdfFiles) {
            if (restore.hadSample) loadedPdfFiles[restore.fileName] = restore.sampleValue;
            else delete loadedPdfFiles[restore.fileName];
        }
        if (restore.lastPdfState && typeof lastPdfState !== 'undefined') {
            lastPdfState = { ...restore.lastPdfState };
        }
        if (typeof currentPdfBlobUrl !== 'undefined') {
            currentPdfBlobUrl = restore.currentPdfBlobUrl;
        }
        const title = document.getElementById('pdf-title');
        if (title) title.innerText = restore.pdfTitle;
        publicTourState.pdfDemoRestore = null;
        if (publicTourState.pdfDemoClosing) {
            window.setTimeout(() => {
                publicTourState.pdfDemoClosing = false;
                syncPublicFloatingUiVisibility();
            }, 900);
        }
        return true;
    }

    async function ensurePublicTourPdfDemo() {
        if (publicTourState.pdfDemoRestore) return true;
        if (typeof loadedPdfFiles !== 'object' || !loadedPdfFiles) return false;
        if (typeof createDemoPdfBlobUrl !== 'function' || typeof loadPdfInPane !== 'function') return false;

        const fileName = 'Memora Walkthrough Sample.pdf';
        const hadSample = Object.prototype.hasOwnProperty.call(loadedPdfFiles, fileName);
        const restore = {
            fileName,
            hadSample,
            sampleValue: hadSample ? loadedPdfFiles[fileName] : null,
            lastPdfState: typeof lastPdfState === 'object' && lastPdfState ? { ...lastPdfState } : null,
            currentPdfBlobUrl: typeof currentPdfBlobUrl !== 'undefined' ? currentPdfBlobUrl : null,
            pdfTitle: document.getElementById('pdf-title')?.innerText || 'Add a file to see it here',
            viewerTouched: false
        };
        publicTourState.pdfDemoRestore = restore;

        try {
            loadedPdfFiles[fileName] = createDemoPdfBlobUrl();
            if (typeof lastPdfState !== 'undefined') {
                lastPdfState = {
                    ...(restore.lastPdfState || {}),
                    fileName,
                    title: fileName,
                    page: 1,
                    searchTerm: ''
                };
            }
            document.body.classList.add('public-tour-pdf-demo');
            await wait(80);
            return true;
        } catch (err) {
            console.warn('Public tour sample PDF failed to prepare:', err);
            closePublicTourPdfDemo();
            return false;
        }
    }

    async function waitForPublicTourPdfDemoPage(runId, stepId, timeoutMs = 7000) {
        const startedAt = Date.now();
        while (Date.now() - startedAt < timeoutMs) {
            if (!isPublicTourPlaybackCurrent(runId, stepId)) return null;
            const page = document.querySelector('#pdfjs-pages .page[data-page-number="1"]');
            const rect = page?.getBoundingClientRect?.();
            let askAvailable = false;
            try {
                askAvailable = !!window.updateMemoraPdfEvidenceAskControl?.();
            } catch (_err) { }
            if (page && rect?.width > 80 && rect?.height > 80 && askAvailable) return page;
            await wait(90);
        }
        return null;
    }

    function createPublicTourPdfDemoCursor(point, { label = 'Left-click + drag' } = {}) {
        const cursor = document.createElement('div');
        cursor.className = 'public-tour-pdf-demo-cursor';
        cursor.dataset.publicTourPdfDemo = 'true';
        cursor.setAttribute('aria-hidden', 'true');
        cursor.innerHTML = `
            <span class="public-tour-pdf-demo-mouse"><span></span></span>
            <span class="public-tour-pdf-demo-label">${escapeHtml(label)}</span>
        `;
        cursor.style.setProperty('--public-tour-cursor-x', `${Math.round(point.x)}px`);
        cursor.style.setProperty('--public-tour-cursor-y', `${Math.round(point.y)}px`);
        document.body.appendChild(cursor);
        publicTourState.pdfDemoCursor = cursor;
        window.requestAnimationFrame(() => cursor.classList.add('is-visible'));
        return cursor;
    }

    async function animatePublicTourPdfDemoCursor(cursor, from, to, duration, runId, stepId, onFrame = null) {
        const startedAt = performance.now();
        const safeDuration = Math.max(1, Number(duration) || 1);
        while (true) {
            if (!isPublicTourPlaybackCurrent(runId, stepId) || !cursor?.isConnected) return false;
            const progress = Math.min(1, (performance.now() - startedAt) / safeDuration);
            const eased = 1 - Math.pow(1 - progress, 3);
            const point = {
                x: from.x + ((to.x - from.x) * eased),
                y: from.y + ((to.y - from.y) * eased)
            };
            cursor.style.setProperty('--public-tour-cursor-x', `${Math.round(point.x)}px`);
            cursor.style.setProperty('--public-tour-cursor-y', `${Math.round(point.y)}px`);
            onFrame?.(point);
            if (progress >= 1) return true;
            await wait(32);
        }
    }

    function clearPublicTourSayMoreDemoArtifacts({ resetQuiz = true } = {}) {
        publicTourState.stepPlaybackDispatchingClick = false;
        if (typeof closeActiveAnchoredPopup === 'function') closeActiveAnchoredPopup();
        window.clearPublicTourAskMemoraSampleAnswer?.();
        if (publicTourState.sayMoreDemoOpenedAsk) {
            window.closeAskAIModal?.('button');
        }
        publicTourState.sayMoreDemoActive = false;
        publicTourState.sayMoreDemoOpenedAsk = false;

        const cursor = publicTourState.pdfDemoCursor;
        document.querySelectorAll('[data-public-tour-say-more-demo="true"]').forEach((element) => element.remove());
        if (cursor?.dataset?.publicTourSayMoreDemo === 'true') {
            cursor.remove();
            publicTourState.pdfDemoCursor = null;
        }
        if (resetQuiz && window.isPublicTourQuizDemoActive?.()) {
            window.loadPublicTourSampleQuiz?.();
        }
    }

    function dispatchPublicTourPlaybackClick(target) {
        if (!(target instanceof HTMLElement)) return false;
        publicTourState.stepPlaybackDispatchingClick = true;
        try {
            target.click();
            return true;
        } finally {
            publicTourState.stepPlaybackDispatchingClick = false;
        }
    }

    async function playPublicTourSayMoreDemo(step, runId) {
        const stepId = String(step?.id || '');
        const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        publicTourState.sayMoreDemoActive = true;
        window.loadPublicTourSampleQuiz?.();
        await wait(reduceMotion ? 20 : 90);
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;

        const correctChoice = document.querySelector('#choices .choice[data-idx="1"]');
        const choiceRect = correctChoice?.getBoundingClientRect?.();
        if (!correctChoice || !choiceRect?.width || !choiceRect?.height) {
            throw new Error('The sample answer choice was not available for playback.');
        }

        const choicePoint = {
            x: choiceRect.left + (choiceRect.width * 0.5),
            y: choiceRect.top + (choiceRect.height * 0.5)
        };
        const approach = {
            x: Math.max(22, choicePoint.x - 76),
            y: Math.max(22, choicePoint.y - 54)
        };
        const cursor = createPublicTourPdfDemoCursor(approach, { label: 'Click the correct choice' });
        cursor.dataset.publicTourSayMoreDemo = 'true';
        const label = cursor.querySelector('.public-tour-pdf-demo-label');

        const reachedChoice = await animatePublicTourPdfDemoCursor(
            cursor,
            approach,
            choicePoint,
            reduceMotion ? 90 : 430,
            runId,
            stepId
        );
        if (!reachedChoice) return;
        cursor.classList.add('is-pressing');
        await wait(reduceMotion ? 25 : 120);
        dispatchPublicTourPlaybackClick(correctChoice);
        cursor.classList.remove('is-pressing');

        await wait(reduceMotion ? 200 : 900);
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
        if (label) label.textContent = 'Click the choice again';
        cursor.classList.add('is-pressing');
        await wait(reduceMotion ? 25 : 120);
        dispatchPublicTourPlaybackClick(correctChoice);
        cursor.classList.remove('is-pressing');

        let sayMoreButton = null;
        const popupStartedAt = Date.now();
        while (Date.now() - popupStartedAt < 1600) {
            if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
            sayMoreButton = document.querySelector('#wrong-popup [data-wrong-say-more="true"]');
            if (isElementVisible(sayMoreButton)) break;
            await wait(50);
        }
        if (!sayMoreButton) throw new Error('The Say more action did not become ready in time.');

        const sayMoreRect = sayMoreButton.getBoundingClientRect();
        const sayMorePoint = {
            x: sayMoreRect.left + (sayMoreRect.width * 0.5),
            y: sayMoreRect.top + (sayMoreRect.height * 0.5)
        };
        if (label) label.textContent = 'Say more';
        const reachedSayMore = await animatePublicTourPdfDemoCursor(
            cursor,
            choicePoint,
            sayMorePoint,
            reduceMotion ? 140 : 760,
            runId,
            stepId
        );
        if (!reachedSayMore) return;

        const askWasVisible = !!window.isAskMemoraInterfaceVisible?.();
        cursor.classList.add('is-pressing');
        await wait(reduceMotion ? 25 : 120);
        dispatchPublicTourPlaybackClick(sayMoreButton);
        cursor.classList.remove('is-pressing');

        const askStartedAt = Date.now();
        while (Date.now() - askStartedAt < 1600) {
            if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
            if (window.isAskMemoraInterfaceVisible?.()) break;
            await wait(50);
        }
        publicTourState.sayMoreDemoOpenedAsk = !askWasVisible && !!window.isAskMemoraInterfaceVisible?.();
        if (label) label.textContent = 'Ask Memoraful opens';
        cursor.classList.add('is-complete');
        await wait(reduceMotion ? 240 : 900);
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
        cursor.classList.remove('is-visible');
        await wait(reduceMotion ? 20 : 180);
        cursor.remove();
        if (publicTourState.pdfDemoCursor === cursor) publicTourState.pdfDemoCursor = null;
    }

    async function waitForPublicTourStableElementRect(element, runId, stepId, timeoutMs = 2400) {
        const startedAt = Date.now();
        let previousRect = null;
        let stableSamples = 0;
        while (Date.now() - startedAt < timeoutMs) {
            if (!isPublicTourPlaybackCurrent(runId, stepId)) return null;
            const rect = element?.getBoundingClientRect?.();
            if (rect?.width > 1 && rect?.height > 1) {
                const nextRect = {
                    left: rect.left,
                    top: rect.top,
                    right: rect.right,
                    bottom: rect.bottom,
                    width: rect.width,
                    height: rect.height
                };
                const movement = previousRect
                    ? Math.max(
                        Math.abs(nextRect.left - previousRect.left),
                        Math.abs(nextRect.top - previousRect.top),
                        Math.abs(nextRect.width - previousRect.width),
                        Math.abs(nextRect.height - previousRect.height)
                    )
                    : Infinity;
                stableSamples = movement <= 0.75 ? stableSamples + 1 : 0;
                previousRect = nextRect;
                if (stableSamples >= 4) return nextRect;
            }
            await wait(90);
        }
        return previousRect;
    }

    async function playPublicTourPdfSelectionDemo(step, runId) {
        const stepId = String(step?.id || '');
        const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        const prepared = await ensurePublicTourPdfDemo();
        if (!prepared || !isPublicTourPlaybackCurrent(runId, stepId)) {
            throw new Error('The sample PDF could not be prepared for playback.');
        }

        const workArea = document.getElementById('work-area');
        const viewerOpen = (typeof isViewerOpen === 'boolean' && isViewerOpen)
            || workArea?.classList.contains('split-view')
            || workArea?.classList.contains('trio-view');
        if (viewerOpen && typeof toggleViewer === 'function') {
            toggleViewer(false);
        }

        const toggleButton = document.getElementById('toggle-viewer-btn');
        if (!toggleButton) {
            throw new Error('The Show PDF button was not available for playback.');
        }
        await focusPublicTourTarget({
            ...step,
            target: () => toggleButton,
            stackRoot: '#controls-bar',
            extraTargets: []
        });
        await wait(reduceMotion ? 40 : (viewerOpen ? 720 : 520));
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;

        const toggleRect = await waitForPublicTourStableElementRect(toggleButton, runId, stepId);
        if (!toggleButton || !toggleRect?.width || !toggleRect?.height) {
            throw new Error('The Show PDF button was not available for playback.');
        }
        let showPdfPoint = {
            x: toggleRect.left + (toggleRect.width * 0.5),
            y: toggleRect.top + (toggleRect.height * 0.5)
        };
        const showPdfApproach = {
            x: Math.max(22, showPdfPoint.x - 82),
            y: Math.max(22, showPdfPoint.y + 46)
        };
        const cursor = createPublicTourPdfDemoCursor(showPdfApproach, { label: 'Show PDF' });
        const reachedShowPdf = await animatePublicTourPdfDemoCursor(
            cursor,
            showPdfApproach,
            showPdfPoint,
            reduceMotion ? 120 : 620,
            runId,
            stepId
        );
        if (!reachedShowPdf) return;

        const liveToggleRect = toggleButton.getBoundingClientRect();
        const liveShowPdfPoint = {
            x: liveToggleRect.left + (liveToggleRect.width * 0.5),
            y: liveToggleRect.top + (liveToggleRect.height * 0.5)
        };
        const finalCorrectionDistance = Math.hypot(
            liveShowPdfPoint.x - showPdfPoint.x,
            liveShowPdfPoint.y - showPdfPoint.y
        );
        if (finalCorrectionDistance > 1.5) {
            const corrected = await animatePublicTourPdfDemoCursor(
                cursor,
                showPdfPoint,
                liveShowPdfPoint,
                reduceMotion ? 60 : 240,
                runId,
                stepId
            );
            if (!corrected) return;
            showPdfPoint = liveShowPdfPoint;
        }
        cursor.classList.add('is-pressing');
        await wait(reduceMotion ? 25 : 140);
        dispatchPublicTourPlaybackClick(toggleButton);
        cursor.classList.remove('is-pressing');
        if (publicTourState.pdfDemoRestore) publicTourState.pdfDemoRestore.viewerTouched = true;
        const cursorLabel = cursor.querySelector('.public-tour-pdf-demo-label');
        if (cursorLabel) cursorLabel.textContent = 'Left-click + drag';

        const page = await waitForPublicTourPdfDemoPage(runId, stepId);
        if (!page || !isPublicTourPlaybackCurrent(runId, stepId)) {
            throw new Error('The sample PDF page did not become ready in time.');
        }
        await focusPublicTourTarget({
            ...step,
            target: () => page,
            stackRoot: '#pdf-pane',
            extraTargets: []
        });
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;

        // Replays reuse the existing PDF page, so the page can be found while
        // the viewer is still expanding. Wait for its geometry to settle before
        // deriving drag coordinates or the moving layer will distort the region.
        const rect = await waitForPublicTourStableElementRect(page, runId, stepId, 4200);
        if (!rect?.width || !rect?.height) {
            throw new Error('The sample PDF page did not settle for playback.');
        }
        const visibleLeft = Math.max(16, rect.left + 18);
        const visibleRight = Math.min(window.innerWidth - 16, rect.right - 18);
        const visibleTop = Math.max(16, rect.top + 18);
        const visibleBottom = Math.min(window.innerHeight - 16, rect.bottom - 18);
        const visibleWidth = Math.max(90, visibleRight - visibleLeft);
        const visibleHeight = Math.max(100, visibleBottom - visibleTop);
        let sampleTextBounds = null;
        const textStartedAt = Date.now();
        while (Date.now() - textStartedAt < 4000 && isPublicTourPlaybackCurrent(runId, stepId)) {
            const textSpans = Array.from(page.querySelectorAll('.textLayer span'))
                .filter((span) => String(span.textContent || '').trim());
            const renderedText = textSpans
                .map((span) => String(span.textContent || '').trim())
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase();
            const exactTextSpans = textSpans.filter((span) => String(span.textContent || '')
                .replace(/\s+/g, ' ')
                .trim()
                .toLowerCase()
                .includes('sample lecture'));
            const textRects = (exactTextSpans.length ? exactTextSpans : textSpans)
                .map((span) => span.getBoundingClientRect())
                .filter((spanRect) => spanRect.width > 1 && spanRect.height > 1);
            if (renderedText.includes('sample lecture') && textRects.length) {
                sampleTextBounds = {
                    left: Math.min(...textRects.map((spanRect) => spanRect.left)),
                    top: Math.min(...textRects.map((spanRect) => spanRect.top)),
                    right: Math.max(...textRects.map((spanRect) => spanRect.right)),
                    bottom: Math.max(...textRects.map((spanRect) => spanRect.bottom))
                };
                break;
            }
            await wait(60);
        }
        const selectionPadX = 18;
        const selectionPadY = 14;
        const selectionLeadX = sampleTextBounds
            ? Math.max(selectionPadX, (sampleTextBounds.right - sampleTextBounds.left) * 0.45)
            : selectionPadX;
        const start = sampleTextBounds ? {
            x: Math.max(visibleLeft + 2, sampleTextBounds.left - selectionLeadX),
            y: Math.max(visibleTop + 2, sampleTextBounds.top - selectionPadY)
        } : {
            x: visibleLeft + (visibleWidth * 0.10),
            y: visibleTop + (visibleHeight * 0.45)
        };
        const end = sampleTextBounds ? {
            x: Math.min(visibleRight - 2, sampleTextBounds.right + selectionPadX),
            y: Math.min(visibleBottom - 2, sampleTextBounds.bottom + selectionPadY)
        } : {
            x: visibleLeft + (visibleWidth * 0.80),
            y: visibleTop + (visibleHeight * 0.55)
        };
        const approach = { x: start.x - 52, y: start.y - 44 };
        const pointerId = 7714;

        const reachedPage = await animatePublicTourPdfDemoCursor(
            cursor,
            showPdfPoint,
            approach,
            reduceMotion ? 160 : 720,
            runId,
            stepId
        );
        if (!reachedPage) return;

        const approached = await animatePublicTourPdfDemoCursor(
            cursor,
            approach,
            start,
            reduceMotion ? 140 : 620,
            runId,
            stepId
        );
        if (!approached) return;

        cursor.classList.add('is-pressing');
        publicTourState.pdfDemoPointerId = pointerId;
        const pointerTarget = page.querySelector('canvas') || page;
        sendPublicTourPdfDemoPointer('pointerdown', pointerTarget, start, pointerId, 1);
        await wait(reduceMotion ? 40 : 180);

        const selections = Array.from(document.querySelectorAll('#pdfjs-pages .pdf-evidence-selection-hitbox.is-drawing'));
        const selection = selections[selections.length - 1] || null;
        if (!selection) throw new Error('The PDF selection gesture did not start.');
        selection.dataset.publicTourPdfDemo = 'true';
        publicTourState.pdfDemoSelection = selection;

        const dragged = await animatePublicTourPdfDemoCursor(
            cursor,
            start,
            end,
            reduceMotion ? 240 : 1450,
            runId,
            stepId,
            (point) => sendPublicTourPdfDemoPointer('pointermove', pointerTarget, point, pointerId, 1)
        );
        if (!dragged) return;

        sendPublicTourPdfDemoPointer('pointerup', pointerTarget, end, pointerId, 0);
        publicTourState.pdfDemoPointerId = 0;
        cursor.classList.remove('is-pressing');
        cursor.classList.add('is-complete');
        const label = cursor.querySelector('.public-tour-pdf-demo-label');
        if (label) label.textContent = 'Ask Memoraful opens';

        let askCard = null;
        const askStartedAt = Date.now();
        while (Date.now() - askStartedAt < 1600 && isPublicTourPlaybackCurrent(runId, stepId)) {
            askCard = selection.querySelector('.pdf-tutor-focus-ask-card')
                || document.querySelector('.pdf-tutor-focus-ask-card[data-focus-source="evidence-pdf"]');
            if (askCard) {
                askCard.dataset.publicTourPdfDemo = 'true';
                publicTourState.pdfDemoAskCard = askCard;
                const settingsButton = askCard.querySelector('[data-open-memora-ai-settings]');
                if (settingsButton) {
                    settingsButton.disabled = true;
                    settingsButton.tabIndex = -1;
                    settingsButton.setAttribute('aria-disabled', 'true');
                    settingsButton.title = 'Settings are unavailable during the guide demo';
                }
                cursor.classList.remove('is-visible', 'is-pressing', 'is-complete');
                await wait(reduceMotion ? 20 : 180);
                cursor.remove();
                if (publicTourState.pdfDemoCursor === cursor) publicTourState.pdfDemoCursor = null;
                break;
            }
            await wait(60);
        }

        const askInput = askCard?.querySelector('textarea, input[type="text"], input:not([type])');
        if (askInput && isPublicTourPlaybackCurrent(runId, stepId)) {
            askInput.focus?.();
            await wait(reduceMotion ? 20 : 100);
            askInput.value = '';
            askInput.dispatchEvent(new Event('input', { bubbles: true }));
            const sampleQuestion = 'i dont understand this section, explain it';
            for (const character of sampleQuestion) {
                if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
                askInput.value += character;
                askInput.dispatchEvent(new Event('input', { bubbles: true }));
                await wait(reduceMotion ? 18 : 72);
            }
            askInput.dispatchEvent(new Event('change', { bubbles: true }));
            askCard.dataset.publicTourTypedQuestion = sampleQuestion;

            const submitButton = askCard.querySelector('.pdf-tutor-focus-ask-submit');
            if (submitButton) {
                submitButton.classList.add('is-demo-pressing');
                await wait(reduceMotion ? 30 : 140);
                submitButton.classList.remove('is-demo-pressing');
            }

            askInput.value = '';
            askInput.dispatchEvent(new Event('input', { bubbles: true }));
            if (typeof markPdfTutorFocusAskCardHasThread === 'function') {
                markPdfTutorFocusAskCardHasThread(askCard);
            }
            if (typeof appendPdfTutorFocusAskMessage === 'function') {
                appendPdfTutorFocusAskMessage(askCard, 'user', sampleQuestion);
                const pendingMessage = appendPdfTutorFocusAskMessage(askCard, 'assistant', 'Thinking...', {
                    question: sampleQuestion,
                    pending: true
                });
                if (typeof setPdfTutorFocusAskCardState === 'function') {
                    setPdfTutorFocusAskCardState(askCard, 'loading', '');
                }
                await wait(reduceMotion ? 120 : 620);
                if (!isPublicTourPlaybackCurrent(runId, stepId)) return;
                const sampleResponse = '**Sample Lecture** is placeholder text for this demonstration. In your own PDF, Memora uses the exact section you highlight as context and explains it in simpler language.';
                if (typeof updatePdfTutorFocusAskMessage === 'function') {
                    updatePdfTutorFocusAskMessage(pendingMessage, sampleResponse, {
                        expandable: false,
                        detail: 'brief',
                        animate: true,
                        actions: false
                    });
                }
                if (typeof setPdfTutorFocusAskCardState === 'function') {
                    setPdfTutorFocusAskCardState(askCard, 'answered', '');
                }
                askCard.dataset.publicTourSampleResponse = sampleResponse;
                const responseStartedAt = Date.now();
                while (
                    Date.now() - responseStartedAt < 5000
                    && isPublicTourPlaybackCurrent(runId, stepId)
                    && askCard.querySelector('.pdf-tutor-focus-ask-response.is-revealing')
                ) {
                    await wait(60);
                }
            }
        }

        await wait(reduceMotion ? 300 : 900);
        if (isPublicTourPlaybackCurrent(runId, stepId) && cursor.isConnected) {
            cursor.classList.remove('is-visible');
            await wait(reduceMotion ? 20 : 180);
            cursor.remove();
            if (publicTourState.pdfDemoCursor === cursor) publicTourState.pdfDemoCursor = null;
        }
    }

    async function playPublicTourApiBenefitReveal(step, runId) {
        const stepId = String(step?.id || '');
        const panel = document.querySelector('#import-modal .api-benefit-panel');
        const toggle = panel?.querySelector('.api-benefit-toggle');
        if (!panel || !toggle) return;

        const reduceMotion = document.body.classList.contains('a11y-reduce-motion')
            || !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        await wait(reduceMotion ? 320 : 2000);
        if (!isPublicTourPlaybackCurrent(runId, stepId)) return;

        let invitation = null;
        if (!reduceMotion && typeof toggle.animate === 'function') {
            invitation = toggle.animate([
                { transform: 'scale(1)', filter: 'brightness(1)' },
                { transform: 'scale(1.035)', filter: 'brightness(1.16)' },
                { transform: 'scale(1)', filter: 'brightness(1)' }
            ], {
                duration: 720,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)'
            });
        }

        await wait(reduceMotion ? 0 : 240);
        if (!isPublicTourPlaybackCurrent(runId, stepId)) {
            invitation?.cancel?.();
            return;
        }

        if (typeof setApiBenefitPanelExpanded === 'function') {
            await setApiBenefitPanelExpanded(panel, true);
        } else {
            dispatchPublicTourPlaybackClick(toggle);
            await wait(reduceMotion ? 0 : 620);
        }
        await invitation?.finished?.catch?.(() => { });
    }

    function beginPublicTourStepPlayback(step) {
        publicTourState.stepPlaybackRunId += 1;
        publicTourState.stepPlaybackStepId = String(step?.id || '');
        setPublicTourPlaybackLocked(true);
        return publicTourState.stepPlaybackRunId;
    }

    async function runPublicTourStepPlayback(step, runId) {
        try {
            if (step?.playback === 'pdf-selection') {
                await playPublicTourPdfSelectionDemo(step, runId);
            } else if (step?.playback === 'say-more') {
                await playPublicTourSayMoreDemo(step, runId);
            } else if (step?.playback === 'api-benefit') {
                await playPublicTourApiBenefitReveal(step, runId);
            }
        } catch (err) {
            console.warn('Public tour step playback could not finish:', err);
        } finally {
            if (isPublicTourPlaybackCurrent(runId, step?.id)) {
                setPublicTourPlaybackLocked(false);
            }
        }
    }

    function replayPublicTourStepPlayback() {
        const step = publicTourState.steps[publicTourState.stepIndex];
        if (!publicTourState.active || publicTourState.stepPlaybackActive || !step?.playback) return false;
        if (step.playback === 'say-more') clearPublicTourSayMoreDemoArtifacts();
        else if (step.playback === 'pdf-selection') clearPublicTourPdfDemoArtifacts();
        const runId = beginPublicTourStepPlayback(step);
        void runPublicTourStepPlayback(step, runId);
        return true;
    }

    function stopPublicTourStepPlayback({ closePdf = true, resetQuiz = true } = {}) {
        const wasSayMoreDemo = publicTourState.stepPlaybackStepId === 'say-more-demo'
            || publicTourState.sayMoreDemoActive
            || !!document.querySelector('[data-public-tour-say-more-demo="true"]')
            || publicTourState.sayMoreDemoOpenedAsk;
        publicTourState.stepPlaybackRunId += 1;
        publicTourState.stepPlaybackStepId = '';
        publicTourState.stepPlaybackDispatchingClick = false;
        setPublicTourPlaybackLocked(false);
        if (wasSayMoreDemo) clearPublicTourSayMoreDemoArtifacts({ resetQuiz });
        if (closePdf) closePublicTourPdfDemo();
        else if (!wasSayMoreDemo) clearPublicTourPdfDemoArtifacts();
    }

    async function setPublicTourSurface(surface) {
        const normalized = surface || 'none';
        let closedSurface = false;
        const playbackStep = publicTourState.steps.find((step) => step?.id === publicTourState.stepPlaybackStepId);

        if (normalized !== 'say-more-demo' && (
            publicTourState.stepPlaybackStepId === 'say-more-demo'
            || publicTourState.sayMoreDemoActive
            || publicTourState.sayMoreDemoOpenedAsk
            || document.querySelector('[data-public-tour-say-more-demo="true"]')
        )) {
            stopPublicTourStepPlayback({ closePdf: false });
            closedSurface = true;
        }
        if (normalized !== 'pdf-demo' && (
            publicTourState.pdfDemoRestore
            || playbackStep?.playback === 'pdf-selection'
            || (publicTourState.pdfDemoCursor?.dataset?.publicTourSayMoreDemo !== 'true' && publicTourState.pdfDemoCursor)
            || publicTourState.pdfDemoSelection
            || publicTourState.pdfDemoAskCard
        )) {
            stopPublicTourStepPlayback({ closePdf: true });
            closedSurface = true;
        }
        if (normalized !== 'import') closedSurface = closePublicTourImportModal() || closedSurface;
        if (normalized !== 'quizgen') closedSurface = closePublicTourQuizGenModal() || closedSurface;
        if (normalized !== 'menu') closedSurface = closePublicTourMenuDropdown() || closedSurface;

        if (closedSurface) {
            await wait(140);
        }

        if (normalized === 'import') {
            await ensurePublicTourImportModal();
        } else if (normalized === 'quizgen') {
            await ensurePublicTourQuizGenModal();
        } else if (normalized === 'menu') {
            await ensurePublicTourMenuDropdown();
        } else if (normalized === 'pdf-demo') {
            await ensurePublicTourPdfDemo();
        }
    }

    function humanizePopupGuideId(modalId = '') {
        return String(modalId || '')
            .replace(/-(modal|panel)$/i, '')
            .replace(/-/g, ' ')
            .replace(/\b\w/g, (match) => match.toUpperCase())
            .trim() || 'Popup';
    }

    function getPopupGuideModal(modalId) {
        return document.getElementById(modalId);
    }

    function getPopupGuideHost(modalId) {
        const modal = getPopupGuideModal(modalId);
        return modal?.firstElementChild || modal || null;
    }

    function extractPopupGuideTitle(modalId) {
        const configured = POPUP_GUIDE_DEFINITIONS[modalId];
        if (configured?.title) return configured.title;

        const host = getPopupGuideHost(modalId);
        const titleEl = host?.querySelector('h1, h2, h3');
        const titleText = titleEl ? String(titleEl.textContent || '').replace(/\s+/g, ' ').trim() : '';
        return titleText || humanizePopupGuideId(modalId);
    }

    function getOpenPopupGuideId() {
        for (const modalId of POPUP_GUIDE_MODAL_IDS) {
            const modal = getPopupGuideModal(modalId);
            if (!modal || !modal.classList.contains('active')) continue;
            return modalId;
        }
        return '';
    }

    function isAskAIModalOpen() {
        const modal = document.getElementById('ask-ai-modal');
        return !!(modal && modal.classList.contains('active'));
    }

    function getPopupGuideSurface(modalId) {
        if (modalId === 'import-modal') return 'import';
        if (modalId === 'quiz-gen-modal') return 'quizgen';
        if (modalId === 'menu-dropdown') return 'menu';
        return 'none';
    }

    function buildPopupGuideIntroStep(modalId, title, definition = null) {
        return {
            id: `${modalId}-guide-intro`,
            surface: getPopupGuideSurface(modalId),
            target: () => getPopupGuideHost(modalId),
            title: `${title} Guide`,
            body: definition?.intro || `Here is a quick tour of ${title}.`,
            note: definition?.note || 'Use Next to move through the main controls.'
        };
    }

    function getPopupGuideControlLabel(control) {
        if (!control) return '';

        const ariaLabel = String(control.getAttribute('aria-label') || '').trim();
        if (ariaLabel) return ariaLabel;

        const title = String(control.getAttribute('title') || '').trim();
        if (title) return title;

        if (control.matches('input, textarea') && control.placeholder) {
            const placeholder = String(control.placeholder || '').trim();
            if (placeholder) return placeholder;
        }

        const wrappingLabel = control.closest('label');
        const wrappingLabelText = wrappingLabel ? String(wrappingLabel.textContent || '').replace(/\s+/g, ' ').trim() : '';
        if (wrappingLabelText) return wrappingLabelText;

        const text = String(control.textContent || '').replace(/\s+/g, ' ').trim();
        if (text) return text;

        return humanizePopupGuideId(control.id || control.name || control.tagName.toLowerCase());
    }

    function describeGenericPopupGuideControl(control, label, popupTitle) {
        const normalizedLabel = String(label || '').toLowerCase();
        const popupName = String(popupTitle || 'this popup').toLowerCase();

        if (control.matches('textarea')) {
            return 'Type or paste your content here.';
        }
        if (control.matches('select')) {
            return `Use this menu to choose a ${popupName} option.`;
        }
        if (control.matches('input[type="checkbox"]')) {
            return `Turn this on to use ${label.toLowerCase()}.`;
        }
        if (control.matches('input:not([type="hidden"])')) {
            return `Enter the value needed for ${popupName} here.`;
        }
        if (normalizedLabel.includes('save')) return 'Click this to save your changes.';
        if (normalizedLabel.includes('cancel') || normalizedLabel.includes('close')) return 'Click this to close the popup.';
        if (normalizedLabel.includes('delete') || normalizedLabel.includes('clear') || normalizedLabel.includes('remove')) return 'This removes stored data, so it should be used with care.';
        if (normalizedLabel.includes('download') || normalizedLabel.includes('export')) return 'Click this to download your data from Memora.';
        if (normalizedLabel.includes('guide')) return 'Click this for a short walkthrough.';
        if (normalizedLabel.includes('start') || normalizedLabel.includes('load') || normalizedLabel.includes('open') || normalizedLabel.includes('generate')) {
            return `Click this to continue with ${popupName}.`;
        }
        return `Use this control while working in ${popupName}.`;
    }

    function buildGenericPopupGuideSteps(modalId, title) {
        const host = getPopupGuideHost(modalId);
        if (!host) return [];

        const steps = [];
        const seenLabels = new Set();
        const controls = Array.from(host.querySelectorAll('button, input:not([type="hidden"]), textarea, select'))
            .filter((control) => {
                if (!isElementVisible(control)) return false;
                return true;
            });

        controls.forEach((control, index) => {
            if (steps.length >= 6) return;
            const label = getPopupGuideControlLabel(control);
            if (!label) return;
            const dedupeKey = label.toLowerCase();
            if (seenLabels.has(dedupeKey)) return;
            seenLabels.add(dedupeKey);
            steps.push({
                id: `${modalId}-generic-${index + 1}`,
                surface: getPopupGuideSurface(modalId),
                target: control,
                title: label,
                body: describeGenericPopupGuideControl(control, label, title)
            });
        });

        return steps;
    }

    function buildPopupGuideSteps(modalId) {
        const definition = POPUP_GUIDE_DEFINITIONS[modalId] || null;
        const title = extractPopupGuideTitle(modalId);
        const specificSteps = Array.isArray(definition?.steps) ? definition.steps : [];
        const detailSteps = specificSteps.length ? specificSteps : buildGenericPopupGuideSteps(modalId, title);

        return [
            buildPopupGuideIntroStep(modalId, title, definition),
            ...detailSteps.map((step, index) => ({
                ...step,
                id: step.id || `${modalId}-step-${index + 1}`,
                surface: step.surface || getPopupGuideSurface(modalId)
            }))
        ];
    }

    function startPopupGuide(modalId) {
        if (!isPublicGuideAvailable()) return;
        if (!modalId || !getPopupGuideModal(modalId)) return;

        const steps = buildPopupGuideSteps(modalId);
        if (!steps.length) return;

        setPublicTutorialPrefs(getPublicTutorialPrefs(), { persist: true, markSeen: true });
        if (publicTourState.active) stopPublicTour();
        closePublicTourHome();

        publicTourState.mode = 'popup';
        publicTourState.guideLabel = 'Popup Guide';
        publicTourState.activePopupGuideId = modalId;
        publicTourState.activePopupGuideTitle = extractPopupGuideTitle(modalId);
        publicTourState.steps = steps;
        publicTourState.stepIndex = 0;
        publicTourState.active = true;
        publicTourState.homeOpen = false;
        publicTourState.homeEmphasized = false;
        publicTourState.homeOnboardingStage = '';

        const { overlay, widget } = getPublicTourUi();
        if (overlay) overlay.hidden = false;
        if (widget) widget.classList.add('tour-running');
        document.body.classList.add('public-tour-active');

        void syncPublicTourStep(true);
    }

    function getPublicTourAiRowTarget() {
        const firstButton = document.querySelector('#quiz-card .ai-quick-btn-inline');
        return firstButton ? firstButton.parentElement : null;
    }

    function buildPublicTourSteps(mode) {
        const steps = [];
        const isApiMode = mode === 'api';
        const accountPillVisible = isElementVisible(document.getElementById('public-account-pill'));
        const isPhoneGuide = document.documentElement.classList.contains('phone-device');
        const prefs = getPublicTutorialPrefs();
        const goalMeta = getPublicTutorialGoalMeta(prefs.goal);

        steps.push({
            id: 'intro',
            surface: 'none',
            title: 'Memora Walkthrough',
            body: 'This walkthrough shows you around your study space first, then walks you through getting set up.',
            note: ''
        });

        if (PUBLIC_EDITION_ENABLED && state.locked) {
            steps.push({
                id: 'auth',
                surface: 'none',
                target: '#public-auth-overlay .public-auth-card',
                title: 'Sign In To Use The Public App',
                body: 'Sign in with Google to use the public version, track your credits, and save your access.',
                note: 'You can still go through this walkthrough before signing in.'
            });
        }

        if (prefs.goal === 'study' && isElementVisible(document.getElementById('welcome-history-btn'))) {
            steps.push({
                id: 'history',
                surface: 'none',
                target: '#welcome-history-btn',
                title: 'Reload Old Work Fast',
                body: 'If you have used this browser before, this button reopens an earlier session.'
            });
        }

        if (!isApiMode) {
            steps.push({
                id: 'study-pane',
                surface: 'none',
                targetOnly: true,
                stackRoot: '#quiz-pane',
                target: '#quiz-card',
                title: 'Your Quiz Card Lives Here',
                body: 'This card is where you answer questions, open explanations, save items, and move through the session.'
            });

            if (isElementVisible(getPublicTourAiRowTarget())) {
                steps.push({
                    id: 'ai-tools',
                    surface: 'none',
                    targetOnly: true,
                    stackRoot: () => document.getElementById('quiz-card') || getPublicTourAiRowTarget(),
                    target: getPublicTourAiRowTarget,
                    title: 'Quick Help Lives Here',
                    body: isPhoneGuide
                        ? 'Ask opens Memora chat about the current question. The Auto menu can open helpful sources for you as you move through the quiz.'
                        : 'Hint gives you a small nudge. Explain gives you a clearer breakdown. The Auto menu can open helpful sources for you as you move through the quiz.'
                });
            }

            steps.push({
                id: 'say-more-demo',
                surface: isPhoneGuide ? 'none' : 'say-more-demo',
                targetOnly: true,
                stackRoot: '#quiz-pane',
                target: '#quiz-card',
                ...(!isPhoneGuide ? { playback: 'say-more', cardPlacement: 'say-more-left' } : {}),
                title: 'Click Answers To Learn More',
                body: 'After you answer, click any choice to see why it is right or wrong. Say more opens Ask Memoraful for a deeper breakdown of that exact choice.',
                note: 'You can also press Ask at any time for a hint or an explanation of the current question.'
            });

            steps.push(
                ...(!isPhoneGuide ? [{
                    id: 'pdf-toggle',
                    surface: 'pdf-demo',
                    targetOnly: true,
                    stackRoot: '#controls-bar',
                    target: '#toggle-viewer-btn',
                    playback: 'pdf-selection',
                    cardPlacement: 'pdf-left',
                    title: 'Highlight Your Lecture And Ask',
                    body: 'Show PDF opens your lecture beside the quiz. Left-click and drag over any passage, diagram, or slide segment to open Ask Memoraful with that exact selection as context.'
                }] : []),
                {
                    id: 'tutor',
                    surface: 'none',
                    targetOnly: true,
                    stackRoot: '#controls-bar',
                    target: '#header-tutor-btn',
                    title: 'Learn From Your Lecture',
                    body: 'Learn opens Memora\'s guided, slide-by-slide Tutor and Study Guide tools so you can understand the material before quizzing yourself.'
                },
                {
                    id: 'tools',
                    surface: 'menu',
                    target: '#menu-dropdown',
                    layerRoot: '#controls-bar',
                    stackRoot: '.menu-wrapper',
                    extraTargets: ['#menu-trigger-btn'],
                    title: 'Settings',
                    body: isPhoneGuide
                        ? 'Open Settings to manage study modes, reopen work, view stats, export, or update your AI key.'
                        : 'Open Settings to manage study modes, see saved sessions, check stats, export your work, and update your AI key.'
                }
            );

            if (accountPillVisible) {
                steps.push({
                    id: 'account',
                    surface: 'none',
                    target: '#public-account-pill',
                    title: 'Plan And Credits Stay Visible',
                    body: 'This pill shows your current plan and remaining credits.'
                });
            }

            steps.push({
                id: 'get-started',
                surface: 'none',
                title: 'Now To Get You Started',
                body: 'That is the study space. Next, add a file, choose what you want to make, and connect Gemini only when you are ready to create it.'
            });
        }

        steps.push(
            {
                id: 'import',
                surface: 'none',
                targetOnly: true,
                stackRoot: '#controls-bar',
                target: '#header-import-btn',
                title: 'Start Here',
                body: prefs.goal === 'study'
                    ? 'Import opens your file browser immediately so you can add a file or load a saved quiz file.'
                    : 'Import opens your file browser immediately. Choose a PDF to continue to quiz setup.'
            }
        );

        steps.push({
            id: 'quizgen-combined',
            surface: 'quizgen',
            target: '#quiz-gen-modal .modal-content',
            extraTargets: [() => document.getElementById('quiz-gen-generate-btn') || document.getElementById('quiz-gen-generate-wrap')],
            phoneCardAnchor: 'top',
            title: 'Customize Only When You Want To',
            body: 'Customize opens these detailed quiz controls. Default skips them and uses a 25-question Moderate quiz with the regular prompt.',
            note: 'After Generate, connecting Gemini is the final step if you have not saved a key yet.'
        });

        steps.push({
            id: 'api-key-final',
            surface: 'quizgen',
            target: '#quiz-gen-generate-wrap',
            title: 'Connect Gemini Last',
            body: 'Once you press Generate, Memora asks for a free Gemini key only if this browser does not already have one. Save & Continue resumes the action you just chose.',
            note: 'The key stays in this browser on this device.'
        });

        if (isApiMode && accountPillVisible) {
            steps.push({
                id: 'credits',
                surface: 'none',
                target: '#public-account-pill',
                title: 'Watch Your Credits Here',
                body: 'In the public version, this pill shows your plan and remaining credits.'
            });
        }

        steps.push({
            id: 'finish',
            surface: 'none',
            target: '#public-tour-launcher',
            title: 'You Are Ready To Use The App',
            body: `${goalMeta.finish} You can reopen Guide from the welcome screen or the main study screen whenever you need it.`,
            note: 'Guide stays out of the way while popups are open.'
        });

        return steps;
    }

    async function showPublicHomeDemo() {
        const { card } = getPublicTourUi();
        if (!card
            || card.hidden
            || !publicTourState.homeOpen
            || publicTourState.homeVariant !== 'onboarding'
            || publicTourState.homeOnboardingStage !== 'choice'
            || card.classList.contains('public-tour-home-choice-exiting')) {
            return;
        }

        // Choosing either first-run action completes the one-time choice. Keep
        // the walkthrough available on the next visit unless its own checkbox
        // is used to hide it entirely.
        markPublicTutorialSeen();
        const runId = ++publicTourState.homeDemoRevealRunId;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        card.classList.add('public-tour-home-choice-exiting');
        if (!reduceMotion) await wait(220);
        if (publicTourState.homeDemoRevealRunId !== runId
            || !publicTourState.homeOpen
            || publicTourState.homeVariant !== 'onboarding') {
            return;
        }

        publicTourState.homeOnboardingStage = 'demo';
        renderPublicTourHome();
        const nextCard = getPublicTourUi().card;
        nextCard?.classList.add('public-tour-home-demo-entering');
        window.requestAnimationFrame(() => {
            if (publicTourState.homeDemoRevealRunId !== runId
                || !publicTourState.homeOpen
                || publicTourState.homeOnboardingStage !== 'demo'
                || !nextCard?.isConnected) {
                return;
            }
            nextCard.classList.add('public-tour-home-demo-entered');
            window.setTimeout(() => {
                if (publicTourState.homeDemoRevealRunId === runId) {
                    nextCard.classList.remove('public-tour-home-demo-entering');
                }
            }, reduceMotion ? 0 : 900);
        });
    }

    function startPublicHomeOnboardingWithFiles(files) {
        const selectedFiles = Array.from(files || []).filter((file) => file?.name);
        if (!selectedFiles.length || typeof window.startNewSession !== 'function') return;

        try {
            markPublicTutorialSeen();
            window.startNewSession(true, {
                isTutorial: true,
                pulseImport: false
            });
            void dismissPublicTourHome({ markSeen: true });
            void window.handleImportFiles?.(selectedFiles, {
                pdfMode: 'automatic',
                skipPdfRoutePrompt: true
            });
        } catch (error) {
            console.warn('Unable to import dropped onboarding files:', error);
        }
    }

    function setupPublicHomeChoiceDropZone(card) {
        const startButton = card?.querySelector('[data-tour-action="start-studying"]');
        const fileInput = card?.querySelector('.public-tour-home-file-input');
        if (!startButton || !fileInput || startButton.dataset.publicHomeDropBound === '1') return;
        startButton.dataset.publicHomeDropBound = '1';

        startButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            markPublicTutorialSeen();
            fileInput.click();
        });
        fileInput.addEventListener('change', () => {
            startPublicHomeOnboardingWithFiles(fileInput.files);
            fileInput.value = '';
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            startButton.addEventListener(eventName, (event) => {
                event.preventDefault();
                event.stopPropagation();
                startButton.classList.add('drag-over');
            });
        });
        startButton.addEventListener('dragleave', (event) => {
            if (!event.relatedTarget || !startButton.contains(event.relatedTarget)) {
                startButton.classList.remove('drag-over');
            }
        });
        startButton.addEventListener('drop', (event) => {
            event.preventDefault();
            event.stopPropagation();
            startButton.classList.remove('drag-over');
            startPublicHomeOnboardingWithFiles(event.dataTransfer?.files);
        });
    }

    function renderPublicTourHome({ emphasized = false, forceOnboardingDemo = false, showReturningSessionActions = false } = {}) {
        const { card, launcher, overlay } = getPublicTourUi();
        if (!card) return;

        restorePublicTourWelcomeActions();
        stopPublicHomeDemoLoop();
        document.body.classList.remove('public-tour-home-revealing');

        const firstRun = !hasSeenPublicTutorial();
        const showOnboarding = shouldRenderPublicHomeOnboarding();
        const guideAvailable = isPublicGuideAvailable();
        const isPhone = document.documentElement.classList.contains('phone-device');
        if (!showOnboarding && !guideAvailable) {
            closePublicTourHome();
            return;
        }
        if (showOnboarding && forceOnboardingDemo) {
            publicTourState.homeOnboardingStage = 'demo';
        } else if (showOnboarding && !publicTourState.homeOnboardingStage) {
            publicTourState.homeOnboardingStage = isPhone || hasSeenPublicTutorial() ? 'demo' : 'choice';
        }
        const showOnboardingChoice = showOnboarding && publicTourState.homeOnboardingStage === 'choice';
        // Any other landing wants the normal Welcome surfaces back.
        if (!showOnboardingChoice) releasePublicOnboardingPrepaint();
        const showReturningActions = showOnboarding && showReturningSessionActions && !isPhone;
        let hasSavedThemeOverhaul = false;
        try {
            hasSavedThemeOverhaul = !!localStorage.getItem('memora_theme_overhaul_v1')
                || !!sessionStorage.getItem('memora_theme_overhaul_v1');
        } catch (_error) { }
        hasSavedThemeOverhaul = hasSavedThemeOverhaul
            || !!String(window.__MEMORA_EARLY_THEME_OVERHAUL__ || '').trim();
        if (showOnboardingChoice && firstRun && !hasSavedThemeOverhaul
            && typeof window.setThemeOverhaul === 'function') {
            // New users get the Editorial landing surface, but a selected theme
            // must survive the next load instead of being overwritten by onboarding.
            window.setThemeOverhaul('editorial-night', {
                persist: false,
                allowLandingEditorial: true
            });
        }
        if (showOnboarding) document.body.classList.remove('public-tour-home-revealed');
        const title = firstRun
            ? 'Learn The Basics'
            : 'Need A Quick Walkthrough?';
        const body = firstRun
            ? 'Memora helps you turn your study files into quizzes and review them in one place.'
            : 'This walkthrough shows the main steps for adding files, making a quiz, and studying.';
        const note = 'Open Guide from the welcome screen or the main single-pane study screen whenever you want a quick refresher.';
        publicTourState.homeOpen = true;
        publicTourState.homeVariant = showOnboarding ? 'onboarding' : 'guide';
        publicTourState.homeEmphasized = !!emphasized && !showOnboarding;

        if (!publicTourState.active) {
            if (overlay) overlay.hidden = !publicTourState.homeEmphasized;
            syncPublicTourChromeState();
        }

        card.classList.remove('active');
        card.classList.toggle('public-tour-card-home-onboarding', showOnboarding);
        card.classList.toggle('public-tour-card-home-choice', showOnboardingChoice);
        card.classList.toggle('public-tour-card-home-returning', showReturningActions);
        card.classList.toggle('public-tour-card-home-guide', !showOnboarding);
        card.classList.remove(
            'public-tour-home-choice-exiting',
            'public-tour-home-demo-entering',
            'public-tour-home-demo-entered'
        );
        card.hidden = false;
        if (launcher) launcher.setAttribute('aria-expanded', 'true');
        if (showOnboarding) {
            if (showOnboardingChoice) {
                card.innerHTML = `
                    <div class="public-tour-home-shell public-tour-home-choice-shell editorial-choice-shell">
                        <div class="ambient-glow-ring" aria-hidden="true"></div>
                        <main class="hero-container">
                            <h1 class="brand-title" aria-label="Memoraful">
                                <span class="brand-hero-word">
                                    <span class="brand-hero-m">M</span><span class="brand-hero-rest"><span>em</span><span class="letter-o-wrap">o<span class="inner-dot"></span></span><span>raful</span></span>
                                </span>
                            </h1>
                            <p class="subheading">Built For Better Studying</p>
                            <div class="btn-group public-tour-home-choice-actions" aria-label="Choose how to begin">
                                <button type="button" class="btn-primary public-tour-start-btn" data-tour-action="start-studying"
                                    aria-label="Get Me Started. Click to upload or drop a lecture file here">
                                    <span>Get Me Started</span>
                                </button>
                                <input class="public-tour-home-file-input" type="file"
                                    accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                    multiple aria-label="Upload lecture files">
                                <button type="button" class="btn-secondary public-tour-demo-btn" data-tour-action="show-home-demo"
                                    aria-label="Show Me a Demo">
                                    <span>Show Me a Demo</span>
                                </button>
                            </div>
                        </main>
                    </div>
                `;
                setupPublicHomeChoiceDropZone(card);
                card.classList.add('active');
                return;
            }
            if (showReturningActions) {
                card.innerHTML = `
                    <div class="public-tour-home-shell public-tour-home-returning-shell">
                        <div class="public-tour-home-hero">
                            <div class="public-tour-home-hero-copy public-tour-home-returning-copy">
                                <div class="public-tour-home-top">
                                    <div class="public-tour-home-heading">
                                        <h3 class="public-tour-title">Welcome Back</h3>
                                    </div>
                                    <button type="button" class="public-tour-home-close" data-tour-action="skip-home" aria-label="Dismiss onboarding">&#215;</button>
                                </div>
                                <p class="public-tour-home-returning-intro">Choose how you want to study today.</p>
                                <div class="public-tour-home-actions public-tour-return-actions is-visible"
                                    data-tour-role="return-actions" aria-label="Session actions"></div>
                                <div class="public-tour-home-footer is-visible">
                                    <div class="public-tour-home-footer-copy">
                                        <label class="public-tour-home-pref">
                                            <input type="checkbox" data-tour-role="hide-home-toggle">
                                            <span>Don't show on startup</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="public-tour-home-hero-demo" role="region" aria-label="Interactive Memora preview">
                                ${getPublicHomeDemoMarkup()}
                            </div>
                        </div>
                    </div>
                `;
                const hideCheckbox = getPublicHomeDismissCheckbox(card);
                if (hideCheckbox) hideCheckbox.checked = getPublicHomeOnboardingHidden();
                mountPublicTourWelcomeActions(card);
                card.classList.add('active');
                window.requestAnimationFrame(() => {
                    schedulePublicHomeOnboardingLayoutSync();
                    ensurePublicHomeDemoLifecycle();
                    const demoRoot = card.querySelector('.public-tour-home-hero-demo');
                    demoRoot?.classList.add('is-revealed', 'is-started');
                    startPublicHomeDemoLoop(card, 0);
                });
                return;
            }
            card.innerHTML = `
                <div class="public-tour-home-shell">
                    <div class="public-tour-home-hero">
                        <div class="public-tour-home-hero-copy">
                            <div class="public-tour-home-top">
                                <div class="public-tour-home-heading">
                                    <h3 class="public-tour-title">What Is Memora?</h3>
                                </div>
                                <button type="button" class="public-tour-home-close" data-tour-action="skip-home" aria-label="Dismiss onboarding">&#215;</button>
                            </div>
                            <div class="public-tour-story" data-tour-role="home-story" aria-hidden="true">
                                <div class="public-tour-story-scroll" data-tour-role="story-scroll">
                                    <div class="public-tour-story-output" data-tour-role="story-output" aria-live="polite"></div>
                                </div>
                            </div>
                            <div class="public-tour-home-footer">
                                <div class="public-tour-home-footer-copy">
                                    <label class="public-tour-home-pref">
                                        <input type="checkbox" data-tour-role="hide-home-toggle">
                                        <span>Don't show again</span>
                                    </label>
                                </div>
                                <div class="public-tour-home-actions" data-tour-role="story-actions">
                                    ${guideAvailable
                                        ? `<button type="button" class="public-tour-start-btn" data-tour-action="start-guide" disabled>
                                            <span>Get me started</span>
                                        </button>`
                                        : `<button type="button" class="public-tour-start-btn" data-tour-action="start-studying" disabled>
                                            <span>Start studying</span>
                                        </button>`}
                                </div>
                            </div>
                        </div>
                        <div class="public-tour-home-hero-demo" role="region" aria-label="Interactive Memora preview">
                            ${getPublicHomeDemoMarkup()}
                        </div>
                    </div>
                </div>
            `;
            const hideCheckbox = getPublicHomeDismissCheckbox(card);
            if (hideCheckbox) hideCheckbox.checked = getPublicHomeOnboardingHidden();
            setPublicHomeStoryActionsVisible(card, false);
            // Populate first so the centered transform is calculated from the
            // card's real height, then activate its entrance animation.
            if (document.documentElement.classList.contains('phone-device')) {
                void card.getBoundingClientRect();
            }
            card.classList.add('active');
            window.requestAnimationFrame(() => {
                schedulePublicHomeOnboardingLayoutSync();
                ensurePublicHomeDemoLifecycle();
                const demoRoot = card.querySelector('.public-tour-home-hero-demo');
                demoRoot?.classList.add('is-revealed', 'is-started');
                startPublicHomeDemoLoop(card, 0);
                startPublicHomeStoryPlayback(card);
            });
            return;
        }

        stopPublicHomeStoryPlayback();
        stopPublicHomeDemoLoop();
        card.innerHTML = `
            <div class="public-tour-kicker">${firstRun ? 'First Look' : 'Guide'}</div>
            <h3 class="public-tour-title">${title}</h3>
            <p class="public-tour-body">${body}</p>
            <div class="public-tour-note">${note}</div>
            <div class="public-tour-home-actions">
                <button type="button" class="theme-outline-btn" data-tour-action="start-guide">Start Walkthrough</button>
            </div>
            <button type="button" class="public-tour-close-link" data-tour-action="skip-home">Close</button>
        `;
        card.classList.add('active');
    }

    function renderPublicTourStep() {
        const { card, launcher } = getPublicTourUi();
        const step = publicTourState.steps[publicTourState.stepIndex];
        if (!card || !step) return;

        const stepNumber = publicTourState.stepIndex + 1;
        const totalSteps = publicTourState.steps.length;
        const progressPct = Math.max(8, Math.round((stepNumber / totalSteps) * 100));
        const modeLabel = publicTourState.mode === 'popup' && publicTourState.activePopupGuideTitle
            ? `${publicTourState.activePopupGuideTitle} Guide`
            : (publicTourState.guideLabel || 'Full Walkthrough');
        const isLastStep = stepNumber >= totalSteps;
        const nextLabel = isLastStep ? 'Done' : 'Next';
        const playbackLocked = !!publicTourState.stepPlaybackActive;
        const prevDisabled = stepNumber === 1 || playbackLocked ? 'disabled aria-disabled="true"' : 'aria-disabled="false"';
        const nextDisabled = playbackLocked ? 'disabled aria-disabled="true"' : 'aria-disabled="false"';
        const noteHtml = step.note
            ? `<div class="public-tour-note">${step.note}</div>`
            : '';
        const replayHtml = step.playback && step.replay !== false
            ? `<button type="button" class="glass-btn public-tour-replay-btn" data-tour-action="replay-step" ${playbackLocked ? 'hidden disabled aria-disabled="true"' : 'aria-disabled="false"'}><span class="gradient-text">Show animation again</span></button>`
            : '';
        const placement = String(step.cardPlacement || '').trim().toLowerCase();
        const transitionDirection = publicTourState.stepTransitionDirection;
        const isInitialStepReveal = card.hidden || !card.classList.contains('active');
        const enterDirection = transitionDirection || (isInitialStepReveal ? 'next' : '');

        card.hidden = false;
        card.dataset.publicTourStepId = step.id || '';
        card.classList.add('active');
        card.classList.remove(
            'public-tour-card-placement-left',
            'public-tour-card-placement-below',
            'public-tour-card-placement-pdf-left',
            'public-tour-card-placement-say-more-left',
            'public-tour-card-clamped',
            'public-tour-card-anchor-top',
            'public-tour-step-exit-next',
            'public-tour-step-exit-prev',
            'public-tour-step-enter-next',
            'public-tour-step-enter-prev'
        );
        if (enterDirection) {
            card.classList.add(`public-tour-step-enter-${enterDirection}`);
        }
        if (placement === 'left') {
            card.classList.add('public-tour-card-placement-left');
        } else if (placement === 'below') {
            card.classList.add('public-tour-card-placement-below');
        } else if (placement === 'pdf-left') {
            card.classList.add('public-tour-card-placement-pdf-left');
        } else if (placement === 'say-more-left') {
            card.classList.add('public-tour-card-placement-say-more-left');
        }
        if (launcher) launcher.setAttribute('aria-expanded', 'true');
        card.innerHTML = `
            <div class="public-tour-kicker">${modeLabel}</div>
            <div class="public-tour-progress-meta">
                <span>Step ${stepNumber} of ${totalSteps}</span>
            </div>
            <div class="public-tour-progress-track" aria-hidden="true">
                <div class="public-tour-progress-fill" style="width:${progressPct}%;"></div>
            </div>
            <h3 class="public-tour-title">${step.title}</h3>
            <p class="public-tour-body">${step.body}</p>
            ${noteHtml}
            ${replayHtml}
            <div class="public-tour-step-actions">
                <button type="button" class="glass-btn" data-tour-action="prev" ${prevDisabled}><span class="gradient-text">Back</span></button>
                <button type="button" class="theme-outline-btn" data-tour-action="next" ${nextDisabled}><span class="gradient-text">${nextLabel}</span></button>
            </div>
            <button type="button" class="public-tour-close-link" data-tour-action="exit-tour"><span class="gradient-text">Stop Guide</span></button>
        `;
        syncPublicTourPlaybackControls();
        // Anchor before the browser can paint the newly rendered step. Waiting
        // for the measurement frame makes top-anchored steps flash at bottom.
        syncPublicTourPhoneCardAnchor();
        if (isInitialStepReveal && !transitionDirection) {
            const initialStepId = step.id || '';
            window.setTimeout(() => {
                if (!publicTourState.active || card.dataset.publicTourStepId !== initialStepId) return;
                card.classList.remove('public-tour-step-enter-next', 'public-tour-step-enter-prev');
            }, 260);
        }
        window.requestAnimationFrame(() => {
            if (!publicTourState.active || card.hidden) return;
            const rect = card.getBoundingClientRect();
            const isOutsideViewport = rect.bottom > window.innerHeight
                || rect.top < 0
                || rect.left < 0
                || rect.right > window.innerWidth;
            card.classList.toggle('public-tour-card-clamped', isOutsideViewport);
            syncPublicTourPhoneCardAnchor();
        });
    }

    function syncPublicTourQuizGenGuideState(step = null) {
        if (typeof window.setQuizGenAdvancedStepGuideOverride !== 'function') return;

        const stepTarget = typeof step?.target === 'string' ? step.target : '';
        const shouldRevealAdvancedStep = !!(
            publicTourState.active
            && publicTourState.activePopupGuideId === 'quiz-gen-modal'
            && stepTarget.includes('[data-flow-step="advanced"]')
        );

        window.setQuizGenAdvancedStepGuideOverride(shouldRevealAdvancedStep);
    }

    async function syncPublicTourStep(allowRender = true) {
        if (!publicTourState.active) return;
        const step = publicTourState.steps[publicTourState.stepIndex];
        if (!step) return;
        const stepIndex = publicTourState.stepIndex;
        const shouldStartPlayback = !!step.playback && publicTourState.stepPlaybackStepId !== step.id;

        syncPublicTourQuizGenGuideState(step);
        await setPublicTourSurface(step.surface);
        if (!publicTourState.active || publicTourState.stepIndex !== stepIndex) return;
        const playbackRunId = shouldStartPlayback ? beginPublicTourStepPlayback(step) : 0;
        await focusPublicTourTarget(step);
        if (!publicTourState.active || publicTourState.stepIndex !== stepIndex) return;
        if (allowRender) renderPublicTourStep();
        else syncPublicTourPlaybackControls();
        if (shouldStartPlayback && playbackRunId) {
            void runPublicTourStepPlayback(step, playbackRunId);
        }
    }

    function prepareFreshSessionForPublicTour() {
        const shouldStartFresh = document.body.classList.contains('welcome-mode')
            && isPublicTourWelcomeHomeVisible()
            && (!PUBLIC_EDITION_ENABLED || !state.locked)
            && typeof window.startNewSession === 'function';

        if (!shouldStartFresh) return false;

        try {
            window.startNewSession(true, { isTutorial: true });
            return true;
        } catch (err) {
            console.warn('Unable to start a fresh session for the public tour:', err);
            return false;
        }
    }

    async function startPublicTour() {
        if (!isPublicGuideAvailable()) return false;
        closePublicTourHome();
        prepareFreshSessionForPublicTour();
        window.loadPublicTourSampleQuiz?.();
        setPublicTutorialPrefs(getPublicTutorialPrefs(), { persist: true, markSeen: true });
        applyPublicTutorialPrefsToApp();
        publicTourState.mode = 'full';
        publicTourState.guideLabel = 'Full Walkthrough';
        publicTourState.activePopupGuideId = '';
        publicTourState.activePopupGuideTitle = '';
        publicTourState.steps = buildPublicTourSteps('full');
        publicTourState.stepIndex = 0;
        publicTourState.stepTransitioning = false;
        publicTourState.stepTransitionDirection = '';
        publicTourState.active = true;
        publicTourState.homeOpen = false;
        publicTourState.homeEmphasized = false;
        publicTourState.homeVariant = '';
        const { card, widget } = getPublicTourUi();
        if (card) card.classList.remove(
            'public-tour-card-home-onboarding',
            'public-tour-card-home-guide',
            'public-tour-card-placement-left',
            'public-tour-card-placement-below',
            'public-tour-card-placement-pdf-left'
        );
        if (widget) widget.classList.add('tour-running');
        syncPublicTourChromeState();

        await syncPublicTourStep(true);
    }

    async function movePublicTourStep(delta) {
        if (!publicTourState.active || publicTourState.stepTransitioning || publicTourState.stepPlaybackActive) return;
        const nextIndex = publicTourState.stepIndex + delta;
        if (nextIndex < 0) return;
        if (nextIndex >= publicTourState.steps.length) {
            return;
        }

        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduceMotion) {
            publicTourState.stepIndex = nextIndex;
            await syncPublicTourStep(true);
            return;
        }

        const { card } = getPublicTourUi();
        const direction = delta < 0 ? 'prev' : 'next';
        publicTourState.stepTransitioning = true;
        publicTourState.stepTransitionDirection = direction;
        document.body.classList.add('public-tour-step-transitioning');

        try {
            beginPublicTourHighlightExit();
            card?.classList.add(`public-tour-step-exit-${direction}`);
            await wait(180);

            if (!publicTourState.active) return;

            publicTourState.stepIndex = nextIndex;
            await syncPublicTourStep(true);
            await wait(260);
        } finally {
            card?.classList.remove(
                'public-tour-step-exit-next',
                'public-tour-step-exit-prev',
                'public-tour-step-enter-next',
                'public-tour-step-enter-prev'
            );
            document.body.classList.remove('public-tour-step-transitioning');
            publicTourState.stepTransitioning = false;
            publicTourState.stepTransitionDirection = '';
        }
    }

    function stopPublicTour(options = {}) {
        const preserveSurface = !!options.preserveSurface;
        restorePublicTourWelcomeActions();
        stopPublicTourStepPlayback({ closePdf: true, resetQuiz: false });
        syncPublicTourQuizGenGuideState(null);
        publicTourState.active = false;
        window.restorePublicTourSampleQuiz?.();
        publicTourState.mode = '';
        publicTourState.guideLabel = 'Full Walkthrough';
        publicTourState.activePopupGuideId = '';
        publicTourState.activePopupGuideTitle = '';
        publicTourState.steps = [];
        publicTourState.stepIndex = -1;
        publicTourState.homeOpen = false;
        publicTourState.homeEmphasized = false;
        publicTourState.homeVariant = '';
        publicTourState.homeOnboardingStage = '';
        publicTourState.homeDemoRevealRunId += 1;
        publicTourState.stepTransitioning = false;
        publicTourState.stepTransitionDirection = '';
        document.body.classList.remove('public-tour-step-transitioning');
        stopPublicHomeStoryPlayback();
        stopPublicHomeDemoLoop();
        clearPublicHomeOnboardingLayoutSchedule();
        clearPublicHomeOnboardingLayout();

        if (!preserveSurface) {
            closePublicTourImportModal();
            closePublicTourQuizGenModal();
            closePublicTourMenuDropdown();
        }
        clearPublicTourHighlight();

        const { card, launcher, widget } = getPublicTourUi();
        if (widget) widget.classList.remove('tour-running');
        syncPublicTourChromeState();

        if (card) {
            card.classList.remove(
                'active',
                'public-tour-card-home-onboarding',
                'public-tour-card-home-returning',
                'public-tour-card-home-guide',
                'public-tour-card-placement-left',
                'public-tour-card-placement-below',
                'public-tour-card-placement-pdf-left',
                'public-tour-card-clamped',
                'public-tour-card-attention',
                'public-tour-card-anchor-top',
                'public-tour-step-exit-next',
                'public-tour-step-exit-prev',
                'public-tour-step-enter-next',
                'public-tour-step-enter-prev'
            );
            card.hidden = true;
            card.innerHTML = '';
        }
        if (launcher) launcher.setAttribute('aria-expanded', 'false');
    }

    function installPublicTourSmokeHooks() {
        const smokeRaw = String(new URLSearchParams(window.location.search || '').get('smoke') || '').trim().toLowerCase();
        const isSmokeMode = smokeRaw === '1' || smokeRaw === 'true' || smokeRaw === 'yes' || smokeRaw === 'on';
        if (!isSmokeMode) return;

        window.__MEMORA_PUBLIC_TOUR_TEST__ = {
            startGuide: () => startPublicTour(),
            stopGuide: () => stopPublicTour(),
            goToStep: async (stepId) => {
                if (!publicTourState.active) await startPublicTour();
                const nextIndex = publicTourState.steps.findIndex((step) => step?.id === stepId);
                if (nextIndex < 0) return false;
                publicTourState.stepIndex = nextIndex;
                publicTourState.stepTransitioning = false;
                publicTourState.stepTransitionDirection = '';
                await syncPublicTourStep(true);
                return true;
            },
            getState: () => ({
                active: publicTourState.active,
                stepId: publicTourState.steps[publicTourState.stepIndex]?.id || '',
                stepIds: publicTourState.steps.map((step) => step?.id || ''),
                playbackActive: publicTourState.stepPlaybackActive,
                pdfDemoOpen: !!publicTourState.pdfDemoRestore
            }),
            openHomeGuide: () => renderPublicTourHome({ forceOnboardingDemo: true }),
            openHomeChoice: () => {
                publicTourState.homeOnboardingStage = '';
                return renderPublicTourHome();
            },
            showHomeDemo: () => showPublicHomeDemo(),
            dismissHomeGuide: () => dismissPublicTourHome(),
            revealHomeStory: () => revealPublicHomeStory(),
            startHomeDemo: (sceneIndex = 0) => startPublicHomeDemoLoop(getPublicTourUi().card, sceneIndex),
            stopHomeDemo: () => stopPublicHomeDemoLoop({ preserveScene: true, preserveRoot: true }),
            getHomeDemoState: () => ({
                running: publicTourState.homeDemoRunning,
                sceneIndex: publicTourState.homeDemoSceneIndex,
                scene: publicTourState.homeDemoRoot?.dataset.homeDemoScene || '',
                rootConnected: !!publicTourState.homeDemoRoot?.isConnected
            })
        };
    }

    function installMemoraDemoDriver() {
        const isDemo = (typeof IS_EMBED_DEMO !== 'undefined' && IS_EMBED_DEMO)
            || String(new URLSearchParams(window.location.search || '').get('demo') || '').trim().toLowerCase() === 'editorial';
        if (!isDemo) return;

        function installDemoChromeMask() {
            if (document.getElementById('demo-chrome-mask')) return;

            const mask = document.createElement('div');
            mask.id = 'demo-chrome-mask';
            mask.setAttribute('aria-hidden', 'true');
            mask.innerHTML = `
                <span class="demo-chrome-mask-corner is-home" hidden></span>
                <span class="demo-chrome-mask-corner is-fullscreen" hidden></span>
            `;
            document.body.appendChild(mask);

            const targets = [
                ['global-home-btn', mask.querySelector('.is-home')],
                ['global-fullscreen-btn', mask.querySelector('.is-fullscreen')]
            ];
            const sync = () => {
                let hasVisibleTarget = false;
                targets.forEach(([targetId, cover]) => {
                    const target = document.getElementById(targetId);
                    if (!target || !cover) return;
                    const rect = target.getBoundingClientRect();
                    const style = window.getComputedStyle(target);
                    const visible = style.display !== 'none'
                        && style.visibility !== 'hidden'
                        && Number(style.opacity || 1) > 0
                        && rect.width > 0
                        && rect.height > 0;
                    cover.hidden = !visible;
                    if (!visible) return;
                    const padding = 4;
                    cover.style.left = `${Math.max(0, rect.left - padding)}px`;
                    cover.style.top = `${Math.max(0, rect.top - padding)}px`;
                    cover.style.width = `${rect.width + (padding * 2)}px`;
                    cover.style.height = `${rect.height + (padding * 2)}px`;
                    hasVisibleTarget = true;
                });
                mask.hidden = !hasVisibleTarget;
            };

            const observer = new MutationObserver((mutations) => {
                if (mutations.some((mutation) => !mask.contains(mutation.target))) sync();
            });
            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ['class', 'style', 'hidden'],
                childList: true,
                subtree: true
            });
            window.addEventListener('resize', sync, { passive: true });
            window.requestAnimationFrame(sync);
        }

        installDemoChromeMask();

        window.__MEMORA_DEMO_DRIVER__ = {
            isReady: () => {
                const card = document.getElementById('quiz-card');
                const welcome = document.getElementById('welcome-screen');
                return !!(card || welcome);
            },
            getWelcomeTarget: () => {
                return document.getElementById('continue-session-btn') || document.getElementById('new-session-btn');
            },
            getOptionTarget: (index = 1) => {
                const opts = document.querySelectorAll('.option-btn, .answer-choice, [data-choice-index]');
                return opts[index] || opts[0] || null;
            },
            getCheckAnswerTarget: () => {
                return document.getElementById('flip-card-btn')
                    || document.getElementById('save-card-btn')
                    || document.querySelector('.quiz-action-primary');
            },
            getPdfTarget: () => {
                return document.getElementById('pdf-viewer') || document.getElementById('pdf-pane');
            },
            getAiTarget: () => {
                return document.getElementById('ai-pane') || document.getElementById('ask-ai-input');
            },
            getQuizGeneratorTarget: () => document.getElementById('quiz-gen-generate-btn'),
            getAskMemoraTarget: () => document.getElementById('ask-memora-send-btn'),
            startSession: () => {
                const continueBtn = document.getElementById('continue-session-btn');
                const newBtn = document.getElementById('new-session-btn');
                if (continueBtn && window.getComputedStyle(continueBtn).display !== 'none') {
                    continueBtn.click();
                } else if (newBtn) {
                    newBtn.click();
                }
            },
            openTrio: () => {
                if (typeof toggleTrio === 'function') toggleTrio(true);
            },
            selectOption: (index = 1) => {
                const opts = document.querySelectorAll('.option-btn, .answer-choice');
                if (opts[index]) opts[index].click();
            },
            checkAnswer: () => {
                const flip = document.getElementById('flip-card-btn');
                if (flip) flip.click();
            }
        };

        function getDemoVisiblePdfPage(expectedPageNumber = null) {
            const pages = Array.from(document.querySelectorAll('#pdfjs-pages .page[data-page-number]'));
            const candidates = expectedPageNumber === null
                ? pages
                : pages.filter((page) => Number(page.dataset.pageNumber) === Number(expectedPageNumber));
            const pdfHost = document.getElementById('pdfjs-host') || document.querySelector('#pdfjs-pages')?.parentElement;
            const hostRect = pdfHost?.getBoundingClientRect?.();
            const viewTop = Math.max(0, Number(hostRect?.top) || 0);
            const viewBottom = Math.min(
                window.innerHeight || Number.MAX_SAFE_INTEGER,
                Number(hostRect?.bottom) || Number.MAX_SAFE_INTEGER
            );
            return candidates.map((page) => {
                const rect = page.getBoundingClientRect();
                const style = window.getComputedStyle(page);
                const visibleTop = Math.max(rect.top, viewTop);
                const visibleBottom = Math.min(rect.bottom, viewBottom);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                return {
                    page,
                    visibleArea: visibleHeight * Math.max(0, rect.width),
                    eligible: style.display !== 'none'
                    && style.visibility !== 'hidden'
                    && rect.width > 140
                    && rect.height > 140
                    && visibleHeight > 40
                };
            }).filter((entry) => entry.eligible)
                .sort((a, b) => b.visibleArea - a.visibleArea)[0]?.page || null;
        }

        function dispatchDemoPdfPointer(type, point, pointerId) {
            const options = {
                bubbles: true,
                cancelable: true,
                composed: true,
                pointerId,
                pointerType: 'mouse',
                isPrimary: true,
                button: 0,
                buttons: type === 'pointerup' ? 0 : 1,
                clientX: point.x,
                clientY: point.y
            };
            try {
                return new PointerEvent(type, options);
            } catch (_) {
                return new MouseEvent(type.replace('pointer', 'mouse'), options);
            }
        }

        function typeDemoInput(input, text, callback) {
            if (!input) {
                callback?.();
                return;
            }
            const value = String(text || '');
            input.focus?.();
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            let index = 0;
            const typeNext = () => {
                if (index >= value.length) {
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                    callback?.();
                    return;
                }
                input.value += value[index];
                index += 1;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                window.setTimeout(typeNext, 28);
            };
            typeNext();
        }

        function waitForDemoCondition(predicate, timeout = 8000) {
            return new Promise((resolve) => {
                const startedAt = Date.now();
                const poll = () => {
                    let value = null;
                    try { value = predicate?.(); } catch (_) { }
                    if (value || Date.now() - startedAt >= timeout) {
                        resolve(value || null);
                        return;
                    }
                    window.setTimeout(poll, 120);
                };
                poll();
            });
        }

        function animateDemoStudyGuideScroll(host, fraction = 0.66, duration = 3000) {
            if (!host) return Promise.resolve(false);
            const maxScrollTop = Math.max(0, host.scrollHeight - host.clientHeight);
            const startScrollTop = Math.max(0, Number(host.scrollTop) || 0);
            const endScrollTop = Math.max(startScrollTop, maxScrollTop * Math.max(0, Math.min(1, fraction)));
            if (endScrollTop <= startScrollTop + 1) return Promise.resolve(false);
            return new Promise((resolve) => {
                const startedAt = performance.now();
                const tick = () => {
                    const progress = Math.min(1, (performance.now() - startedAt) / Math.max(1, duration));
                    const eased = 1 - Math.pow(1 - progress, 3);
                    host.scrollTop = startScrollTop + ((endScrollTop - startScrollTop) * eased);
                    if (progress < 1) {
                        window.requestAnimationFrame(tick);
                        return;
                    }
                    resolve(true);
                };
                window.requestAnimationFrame(tick);
            });
        }

        function installDemoInteractionGuard() {
            if (window.__MEMORA_DEMO_INTERACTION_GUARD__) return;
            const blockTrustedInteraction = (event) => {
                if (!document.body.classList.contains('demo-auto-locked') || !event.isTrusted) return;
                event.preventDefault();
                event.stopImmediatePropagation?.();
            };
            [
                'pointerdown', 'pointerup', 'pointermove', 'pointercancel',
                'click', 'dblclick', 'contextmenu', 'wheel',
                'touchstart', 'touchmove', 'touchend', 'dragstart',
                'keydown', 'keypress', 'keyup'
            ].forEach((type) => document.addEventListener(type, blockTrustedInteraction, true));
            window.__MEMORA_DEMO_INTERACTION_GUARD__ = true;
        }

        function setDemoAutoInteractionLock(locked) {
            const isLocked = !!locked;
            installDemoInteractionGuard();
            document.body.classList.toggle('demo-auto-locked', isLocked);
            document.body.dataset.demoInteraction = isLocked ? 'locked' : 'manual';
            if (isLocked && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }

        function jumpDemoSourceToPageOne() {
            if (typeof jumpToPdfPage === 'function') {
                jumpToPdfPage(1, 'Demo Lecture.pdf', 'autonomic outflow', {
                    smooth: false,
                    maintainScale: false
                });
                return true;
            }
            const pageInput = document.getElementById('pdf-page-input');
            if (!pageInput) return false;
            pageInput.value = '1';
            pageInput.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
        }

        function getDemoPdfSelectionSurface(page) {
            if (!page) return null;
            let layer = page.querySelector(':scope > .pdf-tutor-focus-layer');
            if (!layer) {
                try { layer = ensurePdfEvidenceAskPageLayer?.(page) || null; } catch (_) { }
            }
            return layer || page;
        }

        function selectDemoPdfSegment(onComplete = null) {
            const page = getDemoVisiblePdfPage(1);
            if (!page) return false;
            const surface = getDemoPdfSelectionSurface(page);
            const rect = surface.getBoundingClientRect();
            const start = {
                x: rect.left + (rect.width * 0.06),
                y: rect.top + (rect.height * 0.24)
            };
            const end = {
                x: rect.left + (rect.width * 0.43),
                y: rect.top + (rect.height * 0.43)
            };
            const pointerId = Math.max(1, Date.now() % 2147483000);
            page.dispatchEvent(dispatchDemoPdfPointer('pointerdown', start, pointerId));
            const startedAt = performance.now();
            const draw = () => {
                const progress = Math.min(1, (performance.now() - startedAt) / 1800);
                const point = {
                    x: start.x + ((end.x - start.x) * progress),
                    y: start.y + ((end.y - start.y) * progress)
                };
                document.dispatchEvent(dispatchDemoPdfPointer('pointermove', point, pointerId));
                if (progress < 1) {
                    window.requestAnimationFrame(draw);
                    return;
                }
                document.dispatchEvent(dispatchDemoPdfPointer('pointerup', end, pointerId));
                onComplete?.();
            };
            window.requestAnimationFrame(draw);
            return true;
        }

        function closeDemoSourceAndAskSurfaces() {
            const focusClose = document.querySelector('.pdf-tutor-focus-ask-card .pdf-tutor-focus-ask-close');
            if (focusClose) focusClose.click();
            if (document.getElementById('ask-ai-modal')?.classList.contains('active')) {
                window.closeAskAIModal?.('button');
            }

            const workArea = document.getElementById('work-area');
            if (!workArea) return false;
            if (workArea.classList.contains('trio-view')) {
                const trioToggle = document.getElementById('toggle-trio-btn');
                if (trioToggle) trioToggle.click();
                else window.toggleTrio?.(false);
                return true;
            }
            if (workArea.classList.contains('split-view-ai')) {
                document.getElementById('close-ai-btn')?.click();
            }
            if (workArea.classList.contains('split-view')) {
                document.getElementById('close-viewer-btn')?.click();
            }
            return true;
        }

        // Same-origin in production (X-Frame-Options: SAMEORIGIN). Stay
        // permissive only for the file:// bridge this channel also supports,
        // where location.origin reports 'null' and a real target origin
        // cannot be named.
        const demoBridgeOrigin = (() => {
            try {
                return window.location.origin && window.location.origin !== 'null' ? window.location.origin : null;
            } catch (_) {
                return null;
            }
        })();

        window.addEventListener('message', (event) => {
            if (demoBridgeOrigin && event.origin !== demoBridgeOrigin) return;
            const data = event.data;
            if (!data || data.type !== 'MEMORA_DEMO_CMD') return;

            const sendAck = (action, extra = {}) => {
                try {
                    window.parent?.postMessage({
                        type: 'MEMORA_DEMO_ACK',
                        action,
                        requestId: data.requestId,
                        ...extra
                    }, demoBridgeOrigin || '*');
                } catch (_) {}
            };

            switch (data.action) {
                case 'SET_AUTO_LOCK':
                    setDemoAutoInteractionLock(data.locked);
                    sendAck('SET_AUTO_LOCK', { locked: !!data.locked });
                    break;
                case 'RESET_DEMO_STEP': {
                    // Pill changes are a scene transition, not just a cursor
                    // jump. Close every transient surface owned by the prior
                    // walkthrough step before the next scene starts.
                    let closed = false;
                    try {
                        if (typeof closePdfTutorSession === 'function') {
                            closePdfTutorSession({
                                keepPdf: false,
                                forceDisableAgent: true,
                                skipGuidedPrepConfirm: true,
                                cancelGuidedPrep: true
                            });
                            closed = true;
                        }
                        if (typeof closePdfTutorModal === 'function') {
                            closePdfTutorModal('button');
                            closed = true;
                        }
                        if (typeof closePdfTutorEntryChoiceModal === 'function') {
                            closePdfTutorEntryChoiceModal({ markSeen: false });
                            closed = true;
                        }
                        if (typeof hidePdfTutorStartProgress === 'function') {
                            hidePdfTutorStartProgress();
                            closed = true;
                        }
                        closeDemoSourceAndAskSurfaces();
                        if (typeof quizGenRunning !== 'undefined' && quizGenRunning
                            && typeof confirmCancelQuizGen === 'function') {
                            // A walkthrough pill change is an explicit scene
                            // switch, so cancel generation without surfacing
                            // the normal user confirmation popup. The cancel
                            // path preserves quiz banks from before this run.
                            confirmCancelQuizGen();
                            document.getElementById('quiz-gen-cancel-confirm')?.remove();
                            document.getElementById('quiz-gen-modal')?.classList.remove('active', 'no-backdrop-fade');
                            closed = true;
                        } else if (typeof handleQuizGenClose === 'function') {
                            handleQuizGenClose({ preserveCompletionOptions: true });
                            document.getElementById('quiz-gen-cancel-confirm')?.remove();
                            closed = true;
                        }
                        if (typeof closeAskAIModal === 'function') closeAskAIModal('button');
                    } catch (_) {}
                    sendAck('RESET_DEMO_STEP', { closed });
                    break;
                }
                case 'HYDRATE_DEMO_STEP': {
                    const step = Math.max(0, Math.min(5, Number(data.step) || 0));
                    const loadDemoSession = async (options = {}) => {
                        if (typeof window.loadEmbeddedDemoSession !== 'function') {
                            throw new Error('The embedded demo session loader is unavailable.');
                        }
                        window.loadEmbeddedDemoSession(false, options);
                        await window.loadEmbeddedDemoSession._pdfAssetReady;
                    };
                    const hydrate = async () => {
                        if (step === 0) {
                            window.startNewSession?.(true, { customName: 'Demo Session', demoLanding: true, isTutorial: true, pulseImport: false });
                            await loadDemoSession({ openViewer: false, loadQuestions: false });
                        } else if (step === 1) {
                            await loadDemoSession({ openViewer: false, loadQuestions: false });
                            if (typeof window.handleImportOrGenerate !== 'function') {
                                throw new Error('The quiz generator entry point is unavailable.');
                            }
                            await window.handleImportOrGenerate();
                            const generatorReady = await waitForDemoCondition(() => (
                                document.getElementById('quiz-gen-modal')?.classList.contains('active')
                                    ? document.getElementById('quiz-gen-modal')
                                    : null
                            ), 12000);
                            if (!generatorReady) throw new Error('The quiz generator did not finish opening.');
                            document.querySelector('#quiz-gen-preset-picker .quiz-gen-preset-option[data-preset="quick"]')?.click();
                        } else if (step === 2 || step === 3) {
                            await loadDemoSession({ openViewer: true, loadQuestions: true });
                            jumpDemoSourceToPageOne();
                            const pageReady = await waitForDemoCondition(() => getDemoVisiblePdfPage(1), 10000);
                            if (!pageReady) throw new Error('The demo lecture source page did not finish loading.');
                            if (step === 3) {
                                const selected = await new Promise((resolve) => {
                                    const started = selectDemoPdfSegment(() => resolve(true));
                                    if (!started) resolve(false);
                                });
                                if (!selected) throw new Error('The demo source segment could not be selected.');
                            }
                        } else if (step === 4) {
                            await loadDemoSession({ openViewer: false, loadQuestions: true });
                        } else if (step === 5) {
                            await loadDemoSession({ openViewer: false, loadQuestions: true });
                        }
                    };
                    void hydrate()
                        .then(() => sendAck('HYDRATE_DEMO_STEP', { step, hydrated: true }))
                        .catch((error) => {
                            console.warn(`[Memora demo] Failed to hydrate step ${step}.`, error);
                            sendAck('HYDRATE_DEMO_STEP', {
                                step,
                                hydrated: false,
                                error: error?.message || 'The live app could not hydrate this step.'
                            });
                        });
                    break;
                }
                case 'SET_DEMO_VIEW_MODE': {
                    const mode = String(data.mode || 'desktop').trim().toLowerCase();
                    const normalizedMode = mode === 'tablet' || mode === 'mobile' ? mode : 'desktop';
                    document.documentElement.dataset.demoViewMode = normalizedMode;
                    document.body.dataset.demoViewMode = normalizedMode;
                    if (normalizedMode === 'tablet'
                        && document.getElementById('ask-ai-modal')?.classList.contains('active')
                        && !window.isAskMemoraAiPaneDuoActive?.()) {
                        void window.openAskMemoraInAiPane?.();
                    }
                    sendAck('SET_DEMO_VIEW_MODE', { mode: normalizedMode });
                    break;
                }
                case 'CLICK_CREATE': {
                    // Stage the real five-slide lecture immediately before the
                    // real Create/Import control opens its generator modal.
                    // This keeps the landing screen empty while preserving the
                    // production modal and source-list code path.
                    const hasExistingQuizCard = typeof questions !== 'undefined'
                        && Array.isArray(questions)
                        && questions.length > 0;
                    if (!hasExistingQuizCard && typeof loadEmbeddedDemoSession === 'function') {
                        loadEmbeddedDemoSession(false, {
                            openViewer: false,
                            loadQuestions: false
                        });
                    }
                    const createButton = document.getElementById('header-import-btn');
                    const clicked = !!createButton && !createButton.disabled;
                    if (!clicked) {
                        sendAck('CLICK_CREATE', { clicked, modalReady: false });
                        break;
                    }
                    createButton.click();
                    const startedAt = performance.now();
                    const acknowledgeWhenReady = () => {
                        const modalReady = document.getElementById('quiz-gen-modal')?.classList.contains('active') === true;
                        if (modalReady || performance.now() - startedAt >= 12000) {
                            sendAck('CLICK_CREATE', { clicked, modalReady });
                            return;
                        }
                        window.setTimeout(acknowledgeWhenReady, 100);
                    };
                    acknowledgeWhenReady();
                    break;
                }
                case 'OPEN_QUIZ_GENERATOR':
                    const hasExistingQuizCard = typeof questions !== 'undefined'
                        && Array.isArray(questions)
                        && questions.length > 0;
                    if (!hasExistingQuizCard && typeof loadEmbeddedDemoSession === 'function') {
                        loadEmbeddedDemoSession(false, {
                            openViewer: false,
                            loadQuestions: false
                        });
                    }
                    void Promise.resolve(window.handleImportOrGenerate?.()).then(() => sendAck('OPEN_QUIZ_GENERATOR'));
                    break;
                case 'RUN_QUIZ_GENERATION':
                    void Promise.resolve(window.startUnifiedGenerationWithMode?.('quiz_only'))
                        .then(() => sendAck('RUN_QUIZ_GENERATION'));
                    break;
                case 'CLICK_QUIZ_GENERATE': {
                    const generateButton = document.getElementById('quiz-gen-generate-btn');
                    const canClick = !!generateButton && !generateButton.disabled;
                    if (canClick) generateButton.click();
                    sendAck('CLICK_QUIZ_GENERATE', {
                        clicked: canClick
                    });
                    break;
                }
                case 'CLOSE_QUIZ_GENERATOR': {
                    // The walkthrough owns this transition. Cancel the demo
                    // run silently instead of routing through the user-facing
                    // close handler, which intentionally opens the
                    // “Generation Running” confirmation dialog.
                    if (typeof quizGenRunning !== 'undefined' && quizGenRunning
                        && typeof confirmCancelQuizGen === 'function') {
                        document.getElementById('quiz-gen-cancel-confirm')?.remove();
                        confirmCancelQuizGen();
                        document.getElementById('quiz-gen-modal')?.classList.remove('active', 'no-backdrop-fade');
                    } else {
                        window.handleQuizGenClose?.({ preserveCompletionOptions: true });
                    }
                    document.getElementById('quiz-gen-cancel-confirm')?.remove();
                    sendAck('CLOSE_QUIZ_GENERATOR');
                    break;
                }
                case 'SET_SOLO': {
                    const collapsed = closeDemoSourceAndAskSurfaces();
                    sendAck('SET_SOLO', { collapsed });
                    break;
                }
                case 'CLICK_SOURCE': {
                    const sourceButton = document.getElementById('toggle-viewer-btn');
                    const clicked = !!sourceButton && !sourceButton.disabled;
                    if (clicked) sourceButton.click();
                    sendAck('CLICK_SOURCE', { clicked });
                    break;
                }
                case 'OPEN_SOURCE': {
                    // #toggle-viewer-btn is a bare toggle -- clicking it is
                    // only correct when Source is known to be closed. Force
                    // the open state explicitly instead (see MD-04).
                    const opened = typeof toggleViewer === 'function';
                    if (opened) toggleViewer(true);
                    else document.getElementById('toggle-viewer-btn')?.click();
                    sendAck('OPEN_SOURCE', { opened });
                    break;
                }
                case 'JUMP_TO_SOURCE_PAGE': {
                    const jumped = jumpDemoSourceToPageOne();
                    sendAck('JUMP_TO_SOURCE_PAGE', { jumped });
                    break;
                }
                case 'SELECT_SOURCE_SEGMENT': {
                    const selected = selectDemoPdfSegment(() => sendAck('SELECT_SOURCE_SEGMENT', { selected: true }));
                    if (!selected) sendAck('SELECT_SOURCE_SEGMENT', { selected: false });
                    break;
                }
                case 'RUN_SOURCE_ASK': {
                    const card = document.querySelector('.pdf-evidence-selection-hitbox .pdf-tutor-focus-ask-card')
                        || document.querySelector('.pdf-tutor-focus-ask-card');
                    const input = card?.querySelector('.pdf-tutor-focus-ask-input');
                    const submit = card?.querySelector('.pdf-tutor-focus-ask-submit');
                    const question = String(data.question || 'Can you explain this?').trim();
                    const submitQuestion = async () => {
                        const clicked = !!submit && !submit.disabled;
                        if (clicked) submit.click();
                        const answered = clicked
                            ? await waitForDemoCondition(() => {
                                const liveCard = document.querySelector('.pdf-evidence-selection-hitbox .pdf-tutor-focus-ask-card')
                                    || document.querySelector('.pdf-tutor-focus-ask-card');
                                return liveCard?.dataset?.state === 'answered' ? liveCard : null;
                            }, 10000)
                            : null;
                        sendAck('RUN_SOURCE_ASK', { clicked, answered: !!answered });
                    };
                    typeDemoInput(input, question, submitQuestion);
                    break;
                }
                case 'CLOSE_SOURCE_AND_DUO': {
                    const collapsed = closeDemoSourceAndAskSurfaces();
                    sendAck('CLOSE_SOURCE_AND_DUO', { collapsed });
                    break;
                }
                case 'CLICK_ASK_MEMORA': {
                    const askButton = document.getElementById('ask-memora-inline-btn');
                    const clicked = !!askButton && !askButton.disabled;
                    if (clicked) askButton.click();
                    sendAck('CLICK_ASK_MEMORA', { clicked });
                    break;
                }
                case 'CLICK_ASK_MEMORA_DUO': {
                    const duoButton = document.getElementById('ask-ai-duo-btn');
                    const clicked = !!duoButton && !duoButton.disabled;
                    if (clicked) duoButton.click();
                    sendAck('CLICK_ASK_MEMORA_DUO', { clicked });
                    break;
                }
                case 'OPEN_ASK_MEMORA': {
                    const prefill = String(data.prefill || 'Why is the lateral gray column limited to specific spinal levels?').trim();
                    void Promise.resolve(window.openAskAIModal?.(prefill)).then(() => sendAck('OPEN_ASK_MEMORA'));
                    break;
                }
                case 'RUN_ASK_MEMORA': {
                    const input = document.getElementById('ai-user-input');
                    const question = String(data.question || '').trim();
                    typeDemoInput(input, question, () => {
                        window.handleAskMemoraSendButtonClick?.();
                        sendAck('RUN_ASK_MEMORA');
                    });
                    break;
                }
                case 'CLOSE_ASK_MEMORA':
                    window.closeAskAIModal?.('button');
                    sendAck('CLOSE_ASK_MEMORA');
                    break;
                case 'CLICK_LEARN': {
                    const learnButton = document.getElementById('header-tutor-btn');
                    const clicked = !!learnButton && !learnButton.disabled;
                    if (clicked) learnButton.click();
                    sendAck('CLICK_LEARN', { clicked });
                    break;
                }
                case 'CLICK_LEARN_PDF': {
                    const clickLecture = async () => {
                        const choice = await waitForDemoCondition(() => {
                            const choices = Array.from(document.querySelectorAll('#pdf-tutor-pdf-list .pdf-tutor-pdf-choice'));
                            return choices.find((item) => String(item.dataset.pdfName || '').trim() === 'Demo Lecture.pdf')
                                || choices[0]
                                || null;
                        }, 8000);
                        const startButton = choice?.querySelector('[data-pdf-tutor-action="start"]');
                        const clicked = !!startButton && !startButton.disabled;
                        if (clicked) startButton.click();
                        const entryChoiceModal = await waitForDemoCondition(() => {
                            const modal = document.getElementById('pdf-tutor-entry-choice-modal');
                            const visibleChoices = modal
                                ? Array.from(modal.querySelectorAll('[data-pdf-tutor-entry-choice]'))
                                    .filter((item) => !item.hidden && item.getAttribute('aria-hidden') !== 'true')
                                : [];
                            return modal?.classList.contains('active') && visibleChoices.length >= 4
                                ? modal
                                : null;
                        }, 16000);
                        sendAck('CLICK_LEARN_PDF', { clicked, entryReady: !!entryChoiceModal });
                    };
                    void clickLecture();
                    break;
                }
                case 'SELECT_LEARN_BRIEF': {
                    const selectBrief = async () => {
                        const surface = await waitForDemoCondition(() => {
                            const entryChoiceModal = document.getElementById('pdf-tutor-entry-choice-modal');
                            if (entryChoiceModal?.classList.contains('active')) {
                                return { type: 'entry', element: entryChoiceModal };
                            }
                            const workspace = document.querySelector('#work-area.pdf-tutor-view');
                            return workspace ? { type: 'workspace', element: workspace } : null;
                        }, 10000);
                        const entryChoiceModal = surface?.type === 'entry' ? surface.element : null;
                        const briefButton = entryChoiceModal?.querySelector('[data-pdf-tutor-entry-guide-detail="brief"]')
                            || entryChoiceModal?.querySelector('[data-pdf-tutor-entry-choice="guide"]');
                        let clicked = false;
                        if (briefButton) {
                            // Click the actual visible Brief control. The nested
                            // detail span owns the production handler and keeps
                            // the saved-guide route on the real app path.
                            briefButton.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
                            clicked = true;
                        } else if (typeof window.openPdfTutorDemoSavedStudyGuide === 'function') {
                            await window.openPdfTutorDemoSavedStudyGuide('brief');
                        }
                        const content = await waitForDemoCondition(() => {
                            const guideContent = document.querySelector('#pdf-study-guide-host .pdf-study-guide-content');
                            return guideContent && (guideContent.children.length || guideContent.textContent.trim())
                                ? guideContent
                                : null;
                        }, 15000);
                        sendAck('SELECT_LEARN_BRIEF', { clicked, ready: !!content });
                    };
                    void selectBrief();
                    break;
                }
                case 'RESET_DEMO_STUDY_GUIDE_SCROLL': {
                    const host = document.getElementById('pdf-study-guide-host');
                    if (host) {
                        host.scrollLeft = 0;
                        host.scrollTop = 0;
                    }
                    sendAck('RESET_DEMO_STUDY_GUIDE_SCROLL', { reset: !!host });
                    break;
                }
                case 'SCROLL_DEMO_STUDY_GUIDE': {
                    const host = document.getElementById('pdf-study-guide-host');
                    void animateDemoStudyGuideScroll(host).then((scrolled) => {
                        sendAck('SCROLL_DEMO_STUDY_GUIDE', { scrolled });
                    });
                    break;
                }
                case 'SELECT_QUIZ_PRESET': {
                    const preset = String(data.preset || 'standard').trim().toLowerCase();
                    const presetButton = document.querySelector(`#quiz-gen-preset-picker .quiz-gen-preset-option[data-preset="${preset}"]`);
                    if (presetButton) presetButton.click();
                    sendAck('SELECT_QUIZ_PRESET', { preset });
                    break;
                }
                case 'OPEN_TRIO':
                    if (typeof toggleTrio === 'function') toggleTrio(true);
                    sendAck('OPEN_TRIO');
                    break;
                case 'SELECT_CHOICE': {
                    const choices = document.querySelectorAll('#choices button.choice, button.choice, .option-btn');
                    const idx = data.index ?? 0;
                    const btn = choices[idx] || choices[0];
                    if (btn) btn.click();
                    sendAck('SELECT_CHOICE', { index: idx });
                    break;
                }
                case 'NEXT_QUESTION': {
                    const nextBtn = document.getElementById('next-btn')
                        || Array.from(document.querySelectorAll('button')).find(b => b.textContent && b.textContent.includes('Next'));
                    if (nextBtn) nextBtn.click();
                    sendAck('NEXT_QUESTION');
                    break;
                }
                case 'RESTART_DEMO':
                    if (typeof startNewSession === 'function') {
                        startNewSession(true, {
                            customName: 'Demo Session',
                            demoLanding: true,
                            isTutorial: true,
                            pulseImport: false
                        });
                    }
                    sendAck('RESTART_DEMO');
                    break;
                default:
                    break;
            }
        });
    }

    function getPublicTourInteractiveTarget(target) {
        if (!(target instanceof Element) || !target.closest) return null;
        return target.closest(
            'button, a[href], input, select, textarea, summary, label[for], [role="button"], [role="menuitem"], [data-action], [data-tour-action], [onclick], .import-drop-zone, .welcome-btn, .glass-btn, .theme-outline-btn'
        );
    }

    function shouldKeepPublicTourForTarget(target) {
        if (!(target instanceof Element) || !target.closest) return false;
        return !!target.closest(
            '#import-modal .api-key-section, #import-modal .api-benefit-panel'
        );
    }

    function isWithinActivePublicTourArea(target) {
        if (!(target instanceof Element)) return false;
        const highlightedEls = Array.isArray(publicTourState.highlightedEls) ? publicTourState.highlightedEls : [];
        if (highlightedEls.some((el) => el instanceof Element && el.contains(target))) return true;
        if (publicTourState.highlightedRoot instanceof Element && publicTourState.highlightedRoot.contains(target)) return true;
        if (publicTourState.highlightedStackRoot instanceof Element && publicTourState.highlightedStackRoot.contains(target)) return true;
        if (Array.isArray(publicTourState.highlightedEffectEls)
            && publicTourState.highlightedEffectEls.some((el) => el instanceof Element && el.contains(target))) {
            return true;
        }
        return false;
    }

    function handlePublicTourCardClick(event) {
        const actionEl = event.target.closest('[data-tour-action]');
        if (!actionEl) return;
        event.preventDefault();
        event.stopPropagation();

        const action = actionEl.dataset.tourAction;
        if (publicTourState.stepPlaybackActive && (action === 'prev' || action === 'next')) return;
        if (action === 'start-new-session' || action === 'continue-session') {
            commitPublicHomeDismissPreference();
            void dismissPublicTourHome({ markSeen: true });
            return;
        }
        if (action === 'show-home-demo') {
            void showPublicHomeDemo();
            return;
        }
        if (action === 'toggle-home-demo') {
            const { card } = getPublicTourUi();
            const root = publicTourState.homeDemoRoot || card?.querySelector('.public-tour-home-hero-demo');
            if (!root) return;
            if (!publicTourState.homeDemoRunning) {
                const resumeIndex = publicTourState.homeDemoSceneIndex;
                startPublicHomeDemoLoop(card, resumeIndex);
                return;
            }
            publicTourState.homeDemoPaused = !publicTourState.homeDemoPaused;
            root.classList.toggle('is-paused', publicTourState.homeDemoPaused);
            const toggleButton = root.querySelector('[data-tour-action="toggle-home-demo"]');
            const toggleLabel = root.querySelector('[data-home-demo-toggle-label]');
            if (toggleLabel) toggleLabel.textContent = publicTourState.homeDemoPaused ? 'Resume' : 'Pause';
            if (toggleButton) toggleButton.setAttribute('aria-label', publicTourState.homeDemoPaused ? 'Resume preview' : 'Pause preview');
            return;
        }
        if (action === 'replay-step') {
            replayPublicTourStepPlayback();
            return;
        }
        if (action === 'close-home') {
            dismissPublicTourHome();
            return;
        }
        if (actionEl.dataset.tourPrefKey) {
            const key = actionEl.dataset.tourPrefKey;
            const value = actionEl.dataset.tourPrefValue;
            const nextPrefs = {
                ...getPublicTutorialPrefs(),
                [key]: key === 'questionCount' ? Number(value) : value
            };
            setPublicTutorialPrefs(nextPrefs);
            renderPublicTourHome({ emphasized: publicTourState.homeEmphasized });
            return;
        }
        if (action === 'save-home' || action === 'continue-home') {
            dismissPublicTourHome();
            return;
        }
        if (action === 'skip-home') {
            const isOnboarding = publicTourState.homeVariant === 'onboarding';
            void dismissPublicTourHome({
                animateLanding: isOnboarding,
                revealPhoneTip: isOnboarding
            });
            return;
        }
        if (action === 'reveal-story') {
            if (!publicTourState.homeStoryDone) revealPublicHomeStory();
            return;
        }
        if (action === 'open-ask') {
            dismissPublicTourHome();
            if (typeof openAskAIModal === 'function') {
                openAskAIModal();
            }
            return;
        }
        if (action === 'start-studying') {
            markPublicTutorialSeen();
            const isPhone = document.documentElement.classList.contains('phone-device');
            if (!isPhone && typeof window.startNewSession === 'function') {
                try {
                    window.startNewSession(true, {
                        isTutorial: true,
                        pulseImport: false
                    });
                    commitPublicHomeDismissPreference();
                    void dismissPublicTourHome({ markSeen: true });
                    const pickerOpened = window.openPdfPickerForQuizGeneration?.({ skipAutoPdfOpen: true });
                    if (!pickerOpened) window.setTimeout(() => clickExistingControl('#header-import-btn'), 80);
                } catch (error) {
                    console.warn('Unable to start studying from onboarding:', error);
                }
                return;
            }
            void (async () => {
                const started = typeof window.startNewSessionWithNamePrompt === 'function'
                    ? await window.startNewSessionWithNamePrompt(true, {
                        isTutorial: true,
                        pulseImport: false,
                        skipNamePrompt: true
                    })
                    : false;
                if (!started) return;
                commitPublicHomeDismissPreference();
                await dismissPublicTourHome({ markSeen: true });
                window.setTimeout(() => clickExistingControl('#header-import-btn'), 80);
            })();
            return;
        }
        if (action === 'start-guide' || action === 'start-tailored' || action === 'start-full' || action === 'start-api') {
            commitPublicHomeDismissPreference();
            startPublicTour();
            return;
        }
        if (action === 'exit-tour') {
            // Let the activating click finish before the guide removes its own
            // button and closes any guide-owned pane beneath the pointer.
            window.setTimeout(() => stopPublicTour(), 0);
            return;
        }
        if (action === 'prev') {
            movePublicTourStep(-1);
            return;
        }
        if (action === 'next') {
            const isLastStep = publicTourState.stepIndex >= (publicTourState.steps.length - 1);
            if (isLastStep) {
                stopPublicTour();
                return;
            }
            movePublicTourStep(1);
        }
    }

    function handlePublicTourCardChange(event) {
        const target = event?.target;
        if (!target || !target.matches) return;
        if (target.matches('[data-home-demo-slider]')) {
            const { card } = getPublicTourUi();
            const root = publicTourState.homeDemoRoot || card?.querySelector('.public-tour-home-hero-demo');
            if (!root) return;
            const sceneIndex = Math.max(0, Math.min(PUBLIC_HOME_DEMO_SCENES.length - 1, Math.round(Number(target.value)) - 1));
            if (event.type === 'input') {
                if (publicTourState.homeDemoRunning) {
                    stopPublicHomeDemoLoop({ preserveScene: true, preserveRoot: true, preservePause: true });
                }
                setPublicHomeDemoSliderPosition(root, Number(target.value));
                const position = root.querySelector('[data-home-demo-position]');
                if (position) position.textContent = `${sceneIndex + 1} / ${PUBLIC_HOME_DEMO_SCENES.length}`;
                return;
            }
            root.classList.add('is-revealed');
            startPublicHomeDemoLoop(card, sceneIndex);
            return;
        }
        if (target.matches('[data-tour-role="hide-home-toggle"]')) {
            if (event.type !== 'input') return;
            setPublicHomeOnboardingHidden(!!target.checked);
        }
    }

    function handlePublicTourKeydown(event) {
        if (publicTourState.homeTipsOpen && event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closePublicWelcomeTipModal();
            return;
        }
        if (!publicTourState.active && !publicTourState.homeOpen) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (!publicTourState.active) {
                dismissPublicTourHome();
            } else {
                stopPublicTour();
            }
            return;
        }
        if (!publicTourState.active) return;
        if (event.key === 'ArrowRight' || event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            movePublicTourStep(1);
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            movePublicTourStep(-1);
        }
    }

    function handlePublicTourOutsideInteraction(event) {
        const target = event?.target;
        if (!target) return;
        const donate = document.querySelector('.welcome-donate');
        if (donate?.open && !donate.contains(target)) {
            donate.open = false;
        }
        const { widget } = getPublicTourUi();
        if (widget && widget.contains(target)) return;
        if (isWithinActivePublicTourArea(target)) return;
        if (publicTourState.active) {
            if (publicTourState.stepPlaybackDispatchingClick && event.isTrusted === false) return;
            if (event.type === 'focusin') {
                if (target instanceof HTMLElement) target.blur?.();
                document.querySelector('#public-tour-card [data-tour-action="exit-tour"]')?.focus?.();
            } else {
                const hasCoarsePointer = document.documentElement.classList.contains('phone-device')
                    || (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches);
                if (hasCoarsePointer && event.type === 'pointerdown') {
                    const { card } = getPublicTourUi();
                    card?.classList.remove('public-tour-card-attention');
                    void card?.offsetWidth;
                    card?.classList.add('public-tour-card-attention');
                    window.setTimeout(() => card?.classList.remove('public-tour-card-attention'), 520);
                    return;
                }
                event.preventDefault?.();
            }
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            return;
        }
        const interactiveTarget = getPublicTourInteractiveTarget(target);
        if (!publicTourState.homeOpen) return;
        if (!interactiveTarget) return;
        dismissPublicTourHome();
    }

    function bindPublicTourDonateAndOutsideHandlers() {
        const donate = document.querySelector('.welcome-donate');
        const donateToggle = donate?.querySelector('.welcome-donate-toggle');
        const supportMenuButton = document.getElementById('support-us-menu-btn');

        if (donateToggle && supportMenuButton) {
            donateToggle.hidden = true;
        }

        if (donate && !donate.dataset.publicTourCollapseBound) {
            donate.dataset.publicTourCollapseBound = 'true';
            donate.addEventListener('toggle', () => {
                syncPublicTourWelcomeLauncherOffset();
                const openedFromTools = donate.dataset.openedFromTools === 'true';
                delete donate.dataset.openedFromTools;
                if (openedFromTools) return;
                if (publicTourState.active || !publicTourState.homeOpen) return;
                if (donate.open && publicTourState.homeVariant === 'onboarding') {
                    donate.open = false;
                    return;
                }
                if (donate.open && publicTourState.homeVariant !== 'onboarding') dismissPublicTourHome();
            });
        }

        if (donate && supportMenuButton && !supportMenuButton.dataset.supportPopupBound) {
            supportMenuButton.dataset.supportPopupBound = 'true';
            supportMenuButton.addEventListener('click', () => {
                donate.dataset.openedFromTools = 'true';
                donate.open = true;
            });
        }

        if (!document.body.dataset.publicTourOutsideBound) {
            document.body.dataset.publicTourOutsideBound = 'true';
            document.addEventListener('pointerdown', handlePublicTourOutsideInteraction, true);
            document.addEventListener('click', handlePublicTourOutsideInteraction, true);
            document.addEventListener('focusin', handlePublicTourOutsideInteraction, true);
        }

        if (!document.body.dataset.publicTourPlacementBound) {
            document.body.dataset.publicTourPlacementBound = 'true';
            const syncPhonePlacement = () => {
                if (publicTourState.active) window.requestAnimationFrame(syncPublicTourPhoneCardAnchor);
            };
            window.addEventListener('resize', syncPhonePlacement, { passive: true });
            window.addEventListener('orientationchange', syncPhonePlacement, { passive: true });
            window.visualViewport?.addEventListener('resize', syncPhonePlacement, { passive: true });
        }
    }

    function handlePublicTourLauncherPress() {
        if (!isPublicGuideAvailable()) return;
        const { card } = getPublicTourUi();
        if (publicTourState.active) {
            return;
        }

        if (isAskAIModalOpen()) {
            return;
        }

        const activePopupGuideId = getOpenPopupGuideId();
        if (activePopupGuideId) {
            startPopupGuide(activePopupGuideId);
            return;
        }

        const isHomeOpen = !!(card && card.classList.contains('active') && !card.hidden);
        if (isPublicTourWelcomeHomeVisible()) {
            startPublicTour();
            return;
        }

        if (isHomeOpen) {
            dismissPublicTourHome();
            return;
        }

        startPublicTour();
    }

    function ensurePublicTourLauncher(widget) {
        if (!widget) return null;
        if (!isPublicGuideAvailable()) {
            const existingLauncher = document.getElementById('public-tour-launcher');
            const existingRow = existingLauncher?.closest('.public-tour-launcher-row')
                || widget.querySelector('.public-tour-launcher-row');
            if (existingRow) existingRow.remove();
            else existingLauncher?.remove();
            return null;
        }
        let launcher = document.getElementById('public-tour-launcher');
        if (launcher) return launcher;

        const launcherRow = document.createElement('div');
        launcherRow.className = 'public-tour-launcher-row';
        launcherRow.innerHTML = `
            <button id="public-tour-launcher" type="button" aria-haspopup="dialog" aria-expanded="false" aria-controls="public-tour-card" title="Open the guided walkthrough">
                <svg viewBox="0 0 24 24" style="width: 14px; height: 14px; display: inline-block; vertical-align: -2.5px; margin-right: 5px;">
                    <defs>
                        <linearGradient id="guide-grad-1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stop-color="var(--g1, #60a5fa)" />
                            <stop offset="100%" stop-color="var(--g2, #f472b6)" />
                        </linearGradient>
                    </defs>
                    <circle cx="12" cy="12" r="10" stroke="url(#guide-grad-1)" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"></circle>
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="url(#guide-grad-1)" stroke="url(#guide-grad-1)" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></polygon>
                </svg><span class="gradient-text">Guide</span>
            </button>
        `;

        const card = document.getElementById('public-tour-card');
        if (card && card.parentElement === widget) {
            widget.insertBefore(launcherRow, card);
        } else {
            widget.prepend(launcherRow);
        }

        launcher = document.getElementById('public-tour-launcher');
        return launcher || null;
    }

    function installPublicTutorial() {
        let { widget, launcher, card, overlay } = getPublicTourUi();
        const shortcutsBtn = document.getElementById('shortcuts-btn');
        const shortcutsPanel = document.getElementById('shortcuts-panel');

        if (shortcutsBtn) shortcutsBtn.hidden = true;
        if (shortcutsPanel) shortcutsPanel.hidden = false;

        if (!widget) {
            widget = document.createElement('div');
            widget.id = 'public-tour-widget';
            widget.innerHTML = `
                <section id="public-tour-card" class="public-tour-card" hidden aria-live="polite"></section>
            `;
            document.body.appendChild(widget);
            ({ launcher, card } = getPublicTourUi());
        }

        launcher = ensurePublicTourLauncher(widget);
        card = document.getElementById('public-tour-card');

        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'public-tour-overlay';
            overlay.hidden = true;
            document.body.appendChild(overlay);
        }

        ensurePublicDonateControlAnchored();
        ensurePublicTourWelcomeLauncherOffset();
        installPublicFloatingUiVisibilityWatcher();
        bindPublicTourDonateAndOutsideHandlers();
        renderPublicWelcomeTip();
        renderPublicWelcomeTipModal();
        ensureWelcomeTipsMenuButton();

        if (launcher && !launcher.dataset.bound) {
            launcher.dataset.bound = 'true';
            launcher.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                handlePublicTourLauncherPress();
            });
            const selectRandomWelcomeTheme = () => {
                const nextTheme = selectRandomWelcomeNeuralTheme();
                if (typeof triggerWelcomeThemeMorph === 'function') {
                    triggerWelcomeThemeMorph(nextTheme);
                }
            };

            selectRandomWelcomeTheme();

            if (!window.__memoraWelcomeModeObserver) {
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.attributeName === 'class') {
                            const isWelcome = document.body.classList.contains('welcome-mode');
                            if (isWelcome && !observer.wasWelcome) {
                                selectRandomWelcomeTheme();
                            }
                            observer.wasWelcome = isWelcome;
                        }
                    });
                });
                observer.wasWelcome = document.body.classList.contains('welcome-mode');
                observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
                window.__memoraWelcomeModeObserver = observer;
            }
        }

        const askLauncher = document.getElementById('public-ask-launcher');
        if (askLauncher && !askLauncher.dataset.bound) {
            askLauncher.dataset.bound = 'true';
            askLauncher.addEventListener('click', () => {
                if (publicTourState.active) return;
                if (publicTourState.homeOpen) {
                    dismissPublicTourHome();
                }
                if (typeof openAskAIModalFromSearch === 'function') {
                    openAskAIModalFromSearch();
                    return;
                }
                if (typeof openAskAIModal === 'function') {
                    openAskAIModal(document.getElementById('search-input')?.value || '');
                }
            });
        }

        const tipsMenuBtn = ensureWelcomeTipsMenuButton();
        if (tipsMenuBtn && !tipsMenuBtn.dataset.bound) {
            tipsMenuBtn.dataset.bound = 'true';
            tipsMenuBtn.addEventListener('click', () => {
                openPublicWelcomeTipModal();
            });
        }

        if (card && !card.dataset.bound) {
            card.dataset.bound = 'true';
            card.addEventListener('click', handlePublicTourCardClick);
            card.addEventListener('input', handlePublicTourCardChange);
            card.addEventListener('change', handlePublicTourCardChange);
        }

        const tipHost = ensurePublicWelcomeTipSurface();
        if (tipHost && !tipHost.dataset.bound) {
            tipHost.dataset.bound = 'true';
            tipHost.addEventListener('click', handlePublicWelcomeTipInteraction);
        }

        if (overlay && !overlay.dataset.bound) {
            overlay.dataset.bound = 'true';
            overlay.addEventListener('click', () => {
                if (publicTourState.active) return;
                if (publicTourState.homeVariant !== 'onboarding') dismissPublicTourHome();
            });
        }

        const tipModal = ensurePublicWelcomeTipModal();
        if (tipModal && !tipModal.dataset.bound) {
            tipModal.dataset.bound = 'true';
            tipModal.addEventListener('click', (event) => {
                if (event.target === tipModal) {
                    closePublicWelcomeTipModal();
                    return;
                }
                handlePublicWelcomeTipInteraction(event);
            });
        }

        if (!document.body.dataset.publicTourKeybound) {
            document.body.dataset.publicTourKeybound = 'true';
            document.addEventListener('keydown', handlePublicTourKeydown, true);
        }
    }

    function maybeAutoOpenPublicTutorial() {
        if (publicTourState.autoOpened || publicTourState.active || publicTourState.homeOpen) {
            releasePublicOnboardingPrepaint();
            return;
        }
        if (getPublicHomeOnboardingHidden() || hasSeenPublicTutorial()) {
            releasePublicOnboardingPrepaint();
            return;
        }

        const tryOpen = (attempt = 0) => {
            // Dashboard owns app startup, including the frames before the rail finishes mounting.
            if (document.documentElement.classList.contains('memora-live-left-rail-prepaint')
                || document.body?.classList.contains('memora-live-left-rail-preview')) {
                releasePublicOnboardingPrepaint();
                return;
            }
            if (publicTourState.autoOpened || publicTourState.active || publicTourState.homeOpen) {
                releasePublicOnboardingPrepaint();
                return;
            }
            if (getPublicHomeOnboardingHidden() || hasSeenPublicTutorial()) {
                releasePublicOnboardingPrepaint();
                return;
            }
            if (!isPublicTourWelcomeHomeVisible()) {
                if (attempt < 30) {
                    window.setTimeout(() => tryOpen(attempt + 1), 140);
                } else {
                    releasePublicOnboardingPrepaint();
                }
                return;
            }

            publicTourState.autoOpened = true;
            renderPublicTourHome({
                showReturningSessionActions: false
            });
        };

        tryOpen();
    }

    function ensureAuthOverlay() {
        let overlay = document.getElementById('public-auth-overlay');
        if (overlay) return overlay;

        overlay = document.createElement('div');
        overlay.id = 'public-auth-overlay';
        overlay.innerHTML = `
            <div class="public-auth-card">
                <div class="public-auth-orb orb-a" aria-hidden="true"></div>
                <div class="public-auth-orb orb-b" aria-hidden="true"></div>
                <div class="public-auth-scanline" aria-hidden="true"></div>
                <div class="public-auth-brand-wrap" aria-hidden="true">
                    <div class="public-auth-brand-main">Memora</div>
                </div>
                <p class="public-auth-copy">Sign in with your Google account.</p>
                <div id="public-google-login-btn"></div>
                <div id="public-auth-status" class="public-auth-status" aria-live="polite"></div>
                <div class="public-auth-actions">
                    <button id="public-retry-auth" type="button" class="glass-btn theme-border">Retry</button>
                    <button id="public-close-auth" type="button" class="glass-btn">Close</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const retryBtn = document.getElementById('public-retry-auth');
        if (retryBtn) retryBtn.addEventListener('click', renderGoogleButton);
        const closeBtn = document.getElementById('public-close-auth');
        if (closeBtn) closeBtn.addEventListener('click', dismissAuthOverlay);

        return overlay;
    }

    function setAuthStatus(message, tone = 'info') {
        const statusEl = document.getElementById('public-auth-status');
        if (!statusEl) return;
        statusEl.textContent = message || '';
        statusEl.classList.remove('is-info', 'is-error', 'is-warning');
        if (!message) {
            statusEl.classList.remove('active');
            return;
        }
        statusEl.classList.add('active');
        statusEl.classList.add(tone === 'error' ? 'is-error' : tone === 'warning' ? 'is-warning' : 'is-info');
    }

    async function apiRequest(path, options = {}) {
        const apiBase = String(cfg.apiBaseUrl || '').trim();
        if (!apiBase) throw new Error('PUBLIC_APP_CONFIG.apiBaseUrl is not configured.');

        const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
        if (state.token) headers.Authorization = `Bearer ${state.token}`;

        const res = await fetch(`${apiBase}${path}`, {
            method: options.method || 'GET',
            headers,
            body: options.body ? JSON.stringify(options.body) : undefined
        });

        let data = null;
        try { data = await res.json(); } catch (e) { /* ignore */ }

        if (!res.ok) {
            const msg = (data && (data.error || data.message)) || `Request failed (${res.status})`;
            throw new Error(msg);
        }
        return data || {};
    }

    async function hydratePublicRuntimeConfig() {
        const apiBase = String(cfg.apiBaseUrl || '').trim();
        if (!apiBase || String(cfg.googleClientId || '').trim()) return;

        try {
            const res = await fetch(`${apiBase}/public-config`, {
                method: 'GET',
                headers: { Accept: 'application/json' }
            });

            let data = null;
            try { data = await res.json(); } catch (e) { /* ignore */ }
            if (!res.ok || !data) return;

            const serverClientId = String(data.googleClientId || '').trim();
            if (serverClientId) {
                cfg.googleClientId = serverClientId;
            }
        } catch (e) {
            console.warn('Unable to load public runtime config from backend:', e);
        }
    }

    function applySessionPayload(payload) {
        const rule = getRuleForPlan(payload.plan || 'free');
        state.user = payload.user || null;
        state.plan = payload.plan || 'free';
        state.credits = Number.isFinite(Number(payload.credits)) ? Number(payload.credits) : 0;
        state.features = { ...(rule.features || {}), ...(payload.features || {}) };
        updateAccountPill();
        applyToolGates();
    }

    async function handleGoogleCredential(googleResp) {
        if (!googleResp || !googleResp.credential) {
            safePopup('Google login failed. Try again.', true);
            return;
        }

        try {
            const payload = await apiRequest('/auth/google', {
                method: 'POST',
                body: { idToken: googleResp.credential }
            });

            state.token = payload.accessToken || '';
            if (!state.token) throw new Error('Missing access token from auth response.');
            localStorage.setItem(TOKEN_KEY, state.token);

            applySessionPayload(payload);
            setLocked(false);
            setAuthStatus('', 'info');
            safePopup('Signed in', false);
        } catch (e) {
            console.error(e);
            setAuthStatus(e.message || 'Login failed. Check your public auth config.', 'error');
            safePopup(e.message || 'Login failed.', true);
            setLocked(true);
        }
    }

    function renderGoogleButton() {
        const target = document.getElementById('public-google-login-btn');
        if (!target) return;

        const clientId = String(cfg.googleClientId || '').trim();
        const apiBase = String(cfg.apiBaseUrl || '').trim();
        if (!clientId || !apiBase) {
            target.innerHTML = '';
            const missing = [];
            if (!clientId) missing.push('googleClientId (or backend /public-config)');
            if (!apiBase) missing.push('apiBaseUrl');
            setAuthStatus(`Missing config: ${missing.join(', ')} in js/public-config.js`, 'warning');
            return;
        }
        if (!window.google || !google.accounts || !google.accounts.id) {
            target.innerHTML = '';
            setAuthStatus('Loading Google sign-in...', 'info');
            if (target.dataset.googleIdentityLoading !== '1') {
                target.dataset.googleIdentityLoading = '1';
                void window.MemoraRuntime.ensureGoogleIdentityLoaded()
                    .then(() => {
                        delete target.dataset.googleIdentityLoading;
                        renderGoogleButton();
                    })
                    .catch((error) => {
                        delete target.dataset.googleIdentityLoading;
                        setAuthStatus(error?.message || 'Google sign-in could not be loaded. Click Retry.', 'error');
                    });
            }
            return;
        }

        setAuthStatus('', 'info');
        target.innerHTML = '';
        google.accounts.id.initialize({
            client_id: clientId,
            callback: handleGoogleCredential,
            ux_mode: 'popup'
        });
        google.accounts.id.renderButton(target, {
            theme: 'outline',
            size: 'large',
            shape: 'pill',
            width: 300
        });
    }

    async function restoreSession() {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return false;
        state.token = token;

        try {
            const payload = await apiRequest('/me');
            applySessionPayload(payload);
            setLocked(false);
            return true;
        } catch (e) {
            console.warn('Session restore failed:', e);
            localStorage.removeItem(TOKEN_KEY);
            state.token = '';
            return false;
        }
    }

    async function handleLogout() {
        try {
            if (state.token) {
                await apiRequest('/auth/logout', { method: 'POST' }).catch(() => { /* ignore */ });
            }
        } finally {
            state.token = '';
            state.user = null;
            state.plan = 'free';
            state.credits = 0;
            state.features = { ...DEFAULT_RULES.free.features };
            localStorage.removeItem(TOKEN_KEY);
            updateAccountPill();
            applyToolGates();
            setLocked(true);
            renderGoogleButton();
        }
    }

    function featureEnabled(featureKey) {
        if (state.plan === 'developer') return true;
        return !!state.features[featureKey];
    }

    function patchManualSplitLayout() {
        const splitLayout = document.getElementById('manual-split-layout');
        if (!splitLayout) return;

        const instructions = splitLayout.querySelector('.paste-instructions');
        if (instructions) {
            instructions.innerHTML = `
                <p><b>1.</b> Paste the Memora prompt into Gemini or ChatGPT</p>
                <p><b>2.</b> Upload your PDF to the AI chat</p>
                <p><b>3.</b> Copy the JSON-only response</p>
                <p><b>4.</b> Paste it back here to load the quiz</p>
            `;
        }
    }

    function showPublicManualImportHelp() {
        const existingOverlay = document.getElementById('manual-help-overlay');
        if (existingOverlay) existingOverlay.remove();
        const existingPopup = document.getElementById('manual-help-popup');
        if (existingPopup) existingPopup.remove();

        const overlay = document.createElement('div');
        overlay.id = 'manual-help-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:20px;background:transparent;backdrop-filter:blur(12px) saturate(102%);-webkit-backdrop-filter:blur(12px) saturate(102%);z-index:10000;';

        const popup = document.createElement('div');
        popup.id = 'manual-help-popup';
        popup.style.cssText = 'position:relative;background:var(--modal-surface-bg, linear-gradient(165deg, #141b2a 0%, #0a1020 100%));border:1px solid var(--bd, #334155);border-radius:16px;padding:24px 28px;max-width:420px;width:min(420px, calc(100vw - 32px));box-shadow:0 20px 50px rgba(0,0,0,0.5);';
        popup.innerHTML = `
            <button type="button" class="theme-close-btn" id="manual-help-close-btn" style="position:absolute;top:8px;right:12px;">&times;</button>
            <h3 class="gradient-text" style="margin:0 0 16px;font-size:1.1rem;">Manual Import Guide</h3>
            <p style="color:#e2e8f0;font-size:0.9rem;line-height:1.6;margin-bottom:12px;">
                Generate the quiz in Gemini or ChatGPT, then paste the JSON back into Memora.
            </p>
            <p style="color:#94a3b8;font-size:0.85rem;line-height:1.7;margin-bottom:8px;">
                <b style="color:#e2e8f0;">Steps:</b><br>
                <span class="gradient-text">1.</span> Click <b>Gemini</b> or <b>ChatGPT</b> to copy the Memora prompt<br>
                <span class="gradient-text">2.</span> Paste the prompt into the AI chat and upload your PDF<br>
                <span class="gradient-text">3.</span> Paste the response back here and click <b>Load Quiz</b>
            </p>
            <p style="color:#64748b;font-size:0.75rem;margin:0;">
                Keep the response in raw JSON format so Memora can parse it cleanly.
            </p>
        `;

        const closePopup = () => {
            document.removeEventListener('keydown', handleKeydown);
            overlay.remove();
        };
        const handleKeydown = (event) => {
            if (event.key === 'Escape') closePopup();
        };

        popup.querySelector('#manual-help-close-btn')?.addEventListener('click', closePopup);
        document.addEventListener('keydown', handleKeydown);

        overlay.appendChild(popup);
        document.body.appendChild(overlay);
    }

    function applyPublicWebShell() {
        if (!capabilityEnabled('officeImport')) {
            const dropZoneSubtext = document.querySelector('#import-drop-zone .drop-zone-subtext');
            if (dropZoneSubtext) dropZoneSubtext.textContent = 'PDF, JSON, or TXT';

            ['import-drop-file', 'import-api-file'].forEach((id) => {
                const input = document.getElementById(id);
                if (input) input.accept = '.json,.txt,.pdf';
            });
        }

        if (!capabilityEnabled('canvasImport')) {
            const canvasSection = document.getElementById('canvas-import-section');
            if (canvasSection) canvasSection.remove();
            ['canvas-settings-modal', 'canvas-help-modal', 'canvas-browser-modal'].forEach((id) => {
                document.getElementById(id)?.remove();
            });
        }

        document.getElementById('public-import-support-note')?.remove();

        if (!capabilityEnabled('imageQuestionGeneration')) {
            const imageCard = document.getElementById('quiz-gen-image-card');
            const imageItem = imageCard?.closest('.quiz-gen-enhancement-item');
            if (imageItem) imageItem.remove();
            else if (imageCard) imageCard.remove();
            document.getElementById('quiz-gen-image-options')?.remove();
            document.getElementById('public-image-feature-note')?.remove();
        }

        const manualTip = document.querySelector('.manual-tip');
        if (manualTip) {
            manualTip.textContent = 'You can only do one PDF or PowerPoint quiz generation at a time.';
        }
    }

    function installPublicWebOverrides() {
        overrideGlobalFunction('showServerRequiredPopup', (_original, featureName) => {
            safePopup(getPublicWebMessage(featureName), true);
        });

        if (aiBackendEnabled() && !window.fetch.__publicAIBackendProxy) {
            const backendUrl = buildAIBackendUrl();
            if (backendUrl) {
                const proxyFetch = async function (input, init = {}) {
                    const requestUrl = typeof input === 'string'
                        ? input
                        : input instanceof URL
                            ? input.toString()
                            : (input && typeof input.url === 'string' ? input.url : '');

                    if (!isGeminiGenerateRequest(requestUrl)) {
                        return nativeFetch(input, init);
                    }

                    const requestBody = await maybeReadJsonBody(init.body);
                    if (!requestBody || !extractGeminiModelFromUrl(requestUrl)) {
                        return nativeFetch(input, init);
                    }

                    const headers = { 'Content-Type': 'application/json' };
                    if (state.token) {
                        headers.Authorization = `Bearer ${state.token}`;
                    }

                    const forwardedKey = (() => {
                        try {
                            return new URL(requestUrl).searchParams.get('key') || '';
                        } catch (_err) {
                            return '';
                        }
                    })();

                    const payload = {
                        provider: getAIBackendConfig().provider || 'gemini',
                        model: extractGeminiModelFromUrl(requestUrl),
                        contents: Array.isArray(requestBody.contents) ? requestBody.contents : [],
                        generationConfig: requestBody.generationConfig || {}
                    };

                    if (Array.isArray(requestBody.tools)) {
                        payload.tools = requestBody.tools;
                    }

                    if (getAIBackendConfig().attachUserApiKey !== false && forwardedKey && forwardedKey !== BACKEND_AI_SENTINEL_KEY) {
                        payload.apiKey = forwardedKey;
                    }

                    return nativeFetch(backendUrl, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify(payload),
                        signal: init.signal || undefined
                    });
                };

                proxyFetch.__publicAIBackendProxy = true;
                proxyFetch.__nativeFetch = nativeFetch;
                window.fetch = proxyFetch;
            }
        }

        if (!capabilityEnabled('canvasImport')) {
            [
                'openCanvasSettingsModal',
                'saveCanvasCredentials',
                'openCanvasBrowserModal',
                'loadCanvasCourses',
                'loadCanvasFiles',
                'downloadCanvasFile',
                'downloadCanvasFileAndQuiz'
            ].forEach((name) => {
                overrideGlobalFunction(name, () => {
                    safePopup(getPublicWebMessage('Canvas import'), true);
                });
            });
        }

        if (!capabilityEnabled('imageQuestionGeneration')) {
            overrideGlobalFunction('extractImagesFromQuiz', () => {
                safePopup(getPublicWebMessage('Image-based questions'), true);
                return Promise.resolve({ updatedQuiz: null, imagesProcessed: 0, images: {} });
            });
        }

        overrideGlobalFunction('startManualImport', function (original, ...args) {
            const result = original.apply(this, args);
            setTimeout(patchManualSplitLayout, 0);
            return result;
        });

        overrideGlobalFunction('processManualJSON', function (original, ...args) {
            const textarea = document.getElementById('manual-json-paste');
            const errorEl = document.getElementById('manual-json-error');
            if (textarea) {
                const rawText = textarea.value.trim();
                if (rawText) {
                    try {
                        textarea.value = repairManualImportPayload(rawText);
                        if (errorEl) {
                            errorEl.textContent = '';
                            errorEl.classList.remove('visible');
                        }
                    } catch (_err) {
                        // Let the original parser surface the user-facing error.
                    }
                }
            }
            return original.apply(this, args);
        });

        overrideGlobalFunction('showManualImportHelp', () => {
            showPublicManualImportHelp();
        });
    }

    function getAllGatedToolIds() {
        const rules = getPlanRules();
        const ids = new Set();
        Object.values(rules).forEach((rule) => {
            (rule.disabledToolIds || []).forEach((id) => ids.add(id));
        });
        return Array.from(ids);
    }

    function applyToolGates() {
        const rule = getRuleForPlan(state.plan);
        const blockedIds = new Set(rule.disabledToolIds || []);
        const allIds = getAllGatedToolIds();

        allIds.forEach((id) => {
            const btn = document.getElementById(id);
            if (!btn) return;
            const blocked = blockedIds.has(id);
            btn.classList.toggle('public-plan-disabled', blocked);
            btn.disabled = blocked;
            if (blocked) btn.title = 'Upgrade required for this feature';
        });
    }

    async function consumeCredit(actionKey) {
        if (!state.user) {
            safePopup('Please sign in first.', true);
            setLocked(true);
            return false;
        }
        if (state.features.unlimited_ai) return true;

        const costs = cfg.creditCosts || {};
        const cost = Number.isFinite(Number(costs[actionKey])) ? Number(costs[actionKey]) : 1;

        try {
            const payload = await apiRequest('/credits/consume', {
                method: 'POST',
                body: { action: actionKey, cost }
            });
            if (typeof payload.remainingCredits === 'number') {
                state.credits = payload.remainingCredits;
                updateAccountPill();
            }
            return true;
        } catch (e) {
            safePopup(e.message || 'Credit check failed.', true);
            return false;
        }
    }

    function guardFunction(name, options) {
        const original = window[name];
        if (typeof original !== 'function') return;
        if (original.__publicGuardWrapped) return;

        const wrapped = async function (...args) {
            if (state.locked) {
                safePopup('Sign in required.', true);
                setLocked(true);
                return;
            }

            if (options.featureKey && !featureEnabled(options.featureKey)) {
                safePopup(`${options.featureName || 'This feature'} is available on paid plans.`, true);
                return;
            }

            if (options.creditAction) {
                const ok = await consumeCredit(options.creditAction);
                if (!ok) return;
            }

            return original.apply(this, args);
        };

        wrapped.__publicGuardWrapped = true;
        wrapped.__original = original;
        window[name] = wrapped;
    }

    function installGuards() {
        guardFunction('geminiGetHint', { featureKey: 'ai_hint', creditAction: 'ai_hint', featureName: 'Hint' });
        guardFunction('geminiExplainAnswer', { featureKey: 'ai_explain', creditAction: 'ai_explain', featureName: 'Explain' });
        guardFunction('geminiDeepDive', { featureKey: 'ai_deep_dive', creditAction: 'ai_deep_dive', featureName: 'Deep Dive' });
        guardFunction('geminiAskQuestion', { featureKey: 'ai_ask', creditAction: 'ai_ask', featureName: 'Ask AI' });
        guardFunction('startQuizGeneration', { featureKey: 'ai_quiz_generation', creditAction: 'ai_quiz_generation', featureName: 'Quiz Generation' });
        guardFunction('startUnifiedGeneration', { featureKey: 'ai_quiz_generation', creditAction: 'ai_quiz_generation', featureName: 'Batch Quiz Generation' });
        guardFunction('startUnifiedGenerationWithMode', { featureKey: 'ai_quiz_generation', creditAction: 'ai_quiz_generation', featureName: 'Batch Quiz Generation' });
        guardFunction('executeQuizCondensationRequest', { featureKey: 'ai_quiz_generation', creditAction: 'ai_quiz_generation', featureName: 'Quiz Condensation' });
    }

    function normalizeLauncherTarget(rawValue) {
        return String(rawValue || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    function getLauncherTargetFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const direct = params.get('launch') || params.get('guideTarget') || '';
        const normalized = normalizeLauncherTarget(direct);
        if (!normalized) return '';

        const aliases = {
            theme: 'change-theme-color',
            'theme-settings': 'change-theme-color',
            'change-theme': 'change-theme-color',
            import: 'import-content',
            'import-pdf': 'import-content',
            'start-generation': 'start-import-or-generation',
            generation: 'start-import-or-generation',
            'quiz-generation': 'start-import-or-generation',
            'generate-quiz': 'start-import-or-generation',
            history: 'continue-previous-session',
            'session-history': 'continue-previous-session',
            'ask-ai': 'ask-memora',
            ask: 'ask-memora',
            'api-key': 'change-gemini-api-key',
            'gemini-key': 'change-gemini-api-key',
            saved: 'review-saved-questions',
            'saved-questions': 'review-saved-questions',
            stats: 'view-study-statistics',
            statistics: 'view-study-statistics',
            export: 'export-current-quiz',
            database: 'open-database-manager'
        };

        return aliases[normalized] || normalized;
    }

    function clearLauncherTargetFromUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('launch');
        url.searchParams.delete('guideTarget');
        window.history.replaceState(window.history.state, '', url.toString());
    }

    function clickExistingControl(selectors) {
        const list = Array.isArray(selectors) ? selectors : [selectors];
        for (const selector of list) {
            if (!selector) continue;
            const el = document.querySelector(selector);
            if (!el) continue;
            el.click();
            return true;
        }
        return false;
    }

    async function maybeLaunchFromUrlTarget() {
        const target = getLauncherTargetFromUrl();
        if (!target) return false;

        clearLauncherTargetFromUrl();
        await wait(120);

        if (publicTourState.active) stopPublicTour();
        else if (publicTourState.homeOpen) dismissPublicTourHome();

        const actions = {
            'change-theme-color': () => clickExistingControl('#theme-menu-btn'),
            'import-content': () => clickExistingControl('#header-import-btn'),
            'start-import-or-generation': () => clickExistingControl('#header-import-btn'),
            'continue-previous-session': () => clickExistingControl(['#welcome-history-btn', '#history-menu-btn']),
            'ask-memora': () => {
                if (clickExistingControl('#public-ask-launcher')) return true;
                if (typeof openAskAIModal === 'function') {
                    openAskAIModal(document.getElementById('search-input')?.value || '');
                    return true;
                }
                return false;
            },
            'change-gemini-api-key': () => clickExistingControl('#ai-config-btn'),
            'review-saved-questions': () => clickExistingControl('#quiz-saved-main-btn'),
            'view-study-statistics': () => clickExistingControl('#stats-menu-btn'),
            'export-current-quiz': () => clickExistingControl('#export-quiz-btn'),
            'open-database-manager': () => clickExistingControl('#database-btn')
        };

        const action = actions[target];
        if (!action) return false;

        try {
            return !!action();
        } catch (err) {
            console.warn('Memora launcher target failed:', target, err);
            return false;
        }
    }

    async function boot() {
        installDockControlTextInterceptors();
        installPublicTutorial();
        installPublicTourSmokeHooks();
        installMemoraDemoDriver();
        applyPublicTutorialPrefsToApp();
        applyPublicWebShell();
        installPublicWebOverrides();
        installWelcomeNeuralAtlas();
        installQuizPaneNeuralNetwork();
        installQuizEmptyNeuralAtlas();

        const questionEl = document.getElementById('question');
        if (questionEl) {
            const observer = new MutationObserver(() => {
                installQuizEmptyNeuralAtlas();
            });
            observer.observe(questionEl, { childList: true });
        }

        if (!PUBLIC_EDITION_ENABLED) {
            setLocked(false);
            const launched = await maybeLaunchFromUrlTarget();
            if (launched) releasePublicOnboardingPrepaint();
            else maybeAutoOpenPublicTutorial();
            return;
        }

        ensureAuthOverlay();
        updateAccountPill();
        installGuards();
        applyToolGates();

        if (cfg.allowAnonymousPreview) {
            setLocked(false);
            const launched = await maybeLaunchFromUrlTarget();
            if (launched) releasePublicOnboardingPrepaint();
            else maybeAutoOpenPublicTutorial();
            return;
        }

        setLocked(true);
        await hydratePublicRuntimeConfig();
        const restored = await restoreSession();
        if (!restored) {
            setLocked(true);
            renderGoogleButton();
        }

        const menu = document.getElementById('menu-dropdown');
        if (menu) {
            const observer = new MutationObserver(() => applyToolGates());
            observer.observe(menu, { childList: true, subtree: true });
        }

        const launched = await maybeLaunchFromUrlTarget();
        if (launched) releasePublicOnboardingPrepaint();
        else maybeAutoOpenPublicTutorial();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { void boot(); });
    } else {
        void boot();
    }
})();
